import { useState, useMemo, useEffect, useId, Children, isValidElement, cloneElement, createContext, useContext } from "react";
import { usePersistentState, loadPersisted, savePersisted, removePersisted, parseBackup, useStore, useSaveStatus, usePortalLinks, getActiveStore } from "./lib/store.js";
import { callClaude, aiReady, CLAUDE_MODELS, DEFAULT_MODEL, normalizeModel } from "./lib/ai.js";
import { callFunction } from "./lib/supabase.js";
import { buildFees, feeSummary, overtimeFee, reminderEmail, DEFAULT_PAYMENT_INSTRUCTIONS, ymd } from "../supabase/functions/_shared/fees.js";
import { contractTasks, mergeContractTasks, weeklyUpdate, isoWeek } from "../supabase/functions/_shared/duties.js";
import { pricingFrom, buildQuotePrompt, parseQuoteJSON, assembleQuote, packageIdForTier } from "./lib/pricing.js";
import { DEFAULT_MARKET_RATES } from "./lib/market-rates.js";
import { INTAKE_REQUIRED, intakeFilled, buildClientFromIntake } from "../supabase/functions/_shared/intake.js";
import { fillContract, unfilled, TEMPLATE_VERSION } from "../supabase/functions/_shared/contract-template.js";
import { normalizeSettings, migrateClients, packageLabel, taskTemplateFor, slug, ADDON_UNITS, packageChoices, agreementGaps, formatCeremonyTime, addonsFor, formatRate } from "../supabase/functions/_shared/packages.js";
import { Dialog, StatusTag, Note, Meter, Meta, SafeLink, statusClass, useMedia, AgreementDoc } from "./ui.jsx";
import { CloudSettingsSection, EmailSetupSection, TeamSection, CoupleLinksDialog, ShareIntakeDialog, DocFileField, DocFileLink, ChangeRequestNote, SignatureNote } from "./cloud-ui.jsx";

const SETTINGS_KEY = "settings";
// Phase 11: packages, add-ons, structured terms and legal details come from Amanda's contract (packages.js).
// paymentTerms / cancellationPolicy are generated from `terms`, never typed by hand.
const DEFAULT_SETTINGS = normalizeSettings({
  apiKey: "",
  model: DEFAULT_MODEL,
  businessName: "GhostWriter Events",
  plannerName: "",
  plannerEmail: "amanda@ghostwriter-events.com"
});
// Settings for components deep in the tree (package names on cards and tags).
const SettingsContext = createContext(DEFAULT_SETTINGS);
// Phase 11 · Step 6: each couple's payment schedule, for the overdue flag on client cards.
const FeesContext = createContext({});
// Phase 11 · Step 7: weekly updates owed under Partial and Full-Service agreements.
const UpdatesContext = createContext({ contracts: {}, reports: {} });
function UpdateFlag({ client }) {
  const settings = useContext(SettingsContext);
  const { contracts, reports } = useContext(UpdatesContext);
  const w = weeklyUpdate({ client, contract: contracts[client.id], settings, reports: reports[client.id], today: todayYmd() });
  if (!w) return null;
  return w.sent ? <span className="tag tag-done">Update sent {new Date(w.sent).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span> : <span className="tag tag-neutral">Weekly update due</span>;
}
const isoWeekToday = () => isoWeek(todayYmd());
const todayYmd = () => { const d = new Date(); return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0"); };
function PaymentFlag({ client }) {
  const settings = useContext(SettingsContext);
  const fees = useContext(FeesContext)[client.id];
  if (!fees || !fees.length) return null;
  const sum = feeSummary(fees, todayYmd(), settings);
  return sum.overdue.length ? <span className="tag tag-alert">Payment overdue</span> : null;
}
function PackageTag({ client }) {
  const settings = useContext(SettingsContext);
  return <span className="tag tag-neutral">{packageLabel(client, settings)}</span>;
}

/* ─────────────── Quotes (live hotfix, Oct 20, 2026) ───────────────
   Quotes start from Amanda's pricing sheet (src/lib/pricing.js). The AI picks the package
   and add-ons and prices only custom extras, against the Central Coast rate table in Settings.
------------------------------------------------------------------------ */

function summarizeIntakeForQuote(client) {
  const L = [];
  const i = client.intake;
  L.push("Couple: " + client.full);
  L.push("Region: " + (client.location || "—"));
  if (i) {
    L.push("Date: " + (i.basics.dateStatus || "—") + (i.basics.date ? " (" + i.basics.date + ")" : ""));
    L.push("Destination wedding: " + (i.basics.destination || "—") + (i.basics.destinationDetails ? " — " + i.basics.destinationDetails : ""));
    L.push("Guest count: " + (i.basics.guests || client.guests || "—") + " (" + (i.basics.guestCertainty || "certainty n/a") + ")");
    L.push("Wedding party size: " + (i.basics.partySize || "—"));
    L.push("Couple's total budget band: " + (i.budget.band || "—") + (i.budget.exact ? " (~" + fmt(i.budget.exact) + ")" : "") + "; firmness: " + (i.budget.firmness || "—"));
    L.push("Planning-services budget they indicated: " + (i.budget.planningBudget || "—"));
    L.push("Top priorities: " + ((i.budget.priorities || []).join(", ") || "—"));
    L.push("Requested service level: " + (i.vision.serviceTier || client.tier || "—") + "; planning progress: " + (i.vision.howFarAlong || "—"));
    L.push("Design/styling services wanted: " + (i.vision.designServices || "—"));
    L.push("Tasks to take off their plate: " + ((i.vision.tasksToOffload || []).join(", ") || "—"));
    L.push("Venue: " + (i.venueVendors.status || "—") + "; type: " + (i.venueVendors.venueType || "—") + (i.venueVendors.outdoorNeeds ? " — outdoor logistics: " + i.venueVendors.outdoorNeeds : ""));
    L.push("Vendors already booked: " + ((i.venueVendors.vendorsBooked || []).join(", ") || "none"));
    L.push("Vendors needing help: " + ((i.venueVendors.vendorsNeedHelp || []).join(", ") || "none"));
    L.push("Multi-day: " + (i.details.multiDay || "—") + "; additional events: " + (i.details.additionalEvents || "—"));
    L.push("Fusion/cultural: " + (i.details.fusion || "—") + ((i.details.extraEvents && i.details.extraEvents.length) ? " — extra events: " + i.details.extraEvents.join(", ") : "") + (i.details.traditions ? "; traditions: " + i.details.traditions : ""));
    L.push("Involvement: " + (i.working.involvement || "—") + "; meeting cadence: " + (i.working.meetingCadence || "—"));
  } else {
    L.push("Guest count: " + (client.guests || "—"));
    L.push("Approx total budget: " + (client.budget ? fmt(client.budget) : "—"));
    L.push("Requested service tier: " + (client.tier || "—"));
    L.push("Venue: " + (client.venue || "—"));
  }
  return L.join("\n");
}

// The package the couple chose (Phase 11 profile field first, then the questionnaire answer, then the old tier).
function requestedPackageFor(client) {
  if (client.packageId) return client.packageId;
  const i = client.intake;
  return packageIdForTier(i && i.vision && i.vision.serviceTier) || packageIdForTier(client.tier);
}

/* ─────────────── The agreement (Phase 11 · Step 3) ───────────────
   Amanda's own Coordination Services Agreement, filled in by contract-template.js. No AI writes it.
------------------------------------------------------------------------ */

// Both partners get every email: they each sign, so they each need the agreement.
const partnerEmails = client => ((client && client.partners) || []).map(p => p && p.email).filter(Boolean).join(", ");

function buildContractEmail(client, contract, settings, portalUrl) {
  const biz = (settings && settings.businessName) || "GhostWriter Events";
  const planner = (settings && settings.plannerName) || "";
  const to = partnerEmails(client);
  const L = [];
  L.push("Hi " + client.names + ",");
  L.push("");
  if (portalUrl) {
    L.push("Here's the agreement. Read it at your own pace; when it feels right, sign it in your planning portal (or ask for changes there):");
    L.push("");
    L.push(portalUrl);
  } else {
    L.push("Here's the agreement, below. Read it at your own pace and reply with any questions; when it feels right, I'll arrange signatures.");
  }
  L.push("");
  L.push("——————————");
  L.push("");
  L.push(contract.body || "");
  L.push("");
  L.push("——————————");
  L.push("");
  L.push("Warmly,");
  L.push(planner ? (planner + " · " + biz) : biz);
  return { to: to, subject: "Your " + biz + " coordination agreement", body: L.join("\n") };
}

function buildQuoteEmail(client, quote, settings, portalUrl) {
  const biz = (settings && settings.businessName) || "GhostWriter Events";
  const planner = (settings && settings.plannerName) || "";
  const to = partnerEmails(client);
  const investment = quote.finalNumber ? fmt(quote.finalNumber) : (fmt(quote.rangeLow) + " – " + fmt(quote.rangeHigh));
  const L = [];
  L.push("Hi " + client.names + ",");
  L.push("");
  L.push("Thank you for telling me about your wedding. Here's my proposal for planning it with you:");
  L.push("");
  L.push("Package: " + quote.packageTier);
  L.push("Planning investment: " + investment);
  if (quote.showBreakdownToClient && quote.lineItems && quote.lineItems.length) {
    L.push("");
    L.push("What's included:");
    quote.lineItems.forEach(li => L.push("  • " + li.label + " — " + fmt(li.amount) + (li.note ? " (" + li.note + ")" : "")));
  }
  if (settings && settings.paymentTerms) { L.push(""); L.push("Payment terms: " + settings.paymentTerms); }
  L.push("");
  if (portalUrl) {
    L.push("You can read it and approve it, or ask for changes, in your planning portal:");
    L.push(portalUrl);
    L.push("");
    L.push("If you'd like to talk it through first, reply here and I'll find a time.");
  } else {
    L.push("If you'd like to talk it through, reply here and I'll find a time.");
  }
  L.push("");
  L.push("Warmly,");
  L.push(planner ? (planner + " · " + biz) : biz);
  return { to: to, subject: "Your " + biz + " wedding planning proposal", body: L.join("\n") };
}

/* ───────────────────────── sample data ───────────────────────── */

const NOW = Date.parse("Jul 12, 2026");
const fmt = n => "$" + Math.round(n).toLocaleString("en-US");
const prettyDate = iso => iso ? new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";

const initialClients = [
  { id: "c1", names: "Maya & Daniel", full: "Maya Chen & Daniel Okafor",
    partners: [{ name: "Maya Chen", email: "maya.chen@gmail.com", phone: "(917) 555-0142" }, { name: "Daniel Okafor", email: "d.okafor@gmail.com", phone: "(917) 555-0177" }],
    preferred: "email", date: "Oct 10, 2026", venue: "Cedar Hollow Farm", location: "Hudson Valley, NY", guests: 140, budget: 85000, tier: "Full Service",
    engaged: "Dec 24, 2024", story: "Met in a pottery class in Beacon; Daniel proposed at the same wheel two years later. They want the day to feel like a long harvest dinner with their favorite people.",
    colors: ["#c67139", "#7a8a5e", "#f5ead8", "#8c491a"], theme: "Autumn harvest — candlelit barn, brass details",
    boards: ["Pinterest — Autumn Barn", "Shared album — venue visits"],
    must: ["Live band", "Long harvest tables", "Late-night snack bar"], cant: ["Baby's breath", "Seated head table"],
    dietary: "Groom's father is gluten-free; 6 vegetarian guests", accessibility: "Wheelchair access to ceremony lawn; reserved front-row seating",
    family: "Divorced parents — seat separately at dinner", requests: "Dog (Biscuit) as ring bearer, ceremony only" },
  { id: "c2", names: "Sofia & James", full: "Sofia Reyes & James Whitfield",
    partners: [{ name: "Sofia Reyes", email: "sofia.reyes@outlook.com", phone: "(805) 555-0134" }, { name: "James Whitfield", email: "jwhitfield@me.com", phone: "(805) 555-0191" }],
    preferred: "text", date: "Sep 19, 2026", venue: "Casa Miravalle", location: "Santa Barbara, CA", guests: 90, budget: 120000, tier: "Full Service",
    engaged: "Jun 8, 2025", story: "Engaged on the bluff at Casa Miravalle — booking the same spot was non-negotiable. Sofia's family is flying in from Valencia for a week of celebrations.",
    colors: ["#8fa073", "#f9f4ed", "#d67f48", "#645c50"], theme: "Coastal Mediterranean — citrus, linen, terracotta",
    boards: ["Pinterest — Citrus Coast"],
    must: ["Paella station", "String quartet for ceremony", "Welcome dinner"], cant: ["Buffet service", "Fireworks"],
    dietary: "3 shellfish allergies — flag at paella station", accessibility: "Golf-cart shuttle from parking to bluff",
    family: "Abuela speaks Spanish only — bilingual signage and MC", requests: "Citrus from the family orchard in centerpieces" },
  { id: "c3", names: "Priya & Leah", full: "Priya Sharma & Leah Goldstein",
    partners: [{ name: "Priya Sharma", email: "priya.sh@gmail.com", phone: "(503) 555-0118" }, { name: "Leah Goldstein", email: "leah.gold@gmail.com", phone: "(503) 555-0129" }],
    preferred: "email", date: "Nov 14, 2026", venue: "The Glasshouse", location: "Portland, OR", guests: 60, budget: 48000, tier: "Partial Planning",
    engaged: "Feb 14, 2025", story: "Two librarians who met over a mis-shelved atlas. They want a small interfaith ceremony surrounded by living plants, books, and very good cake.",
    colors: ["#ccdbb2", "#7a8a5e", "#a19786", "#2e2b25"], theme: "Modern greenhouse — ferns, candles, minimal florals",
    boards: ["Figma moodboard — Greenhouse"],
    must: ["Chuppah + garland exchange", "Vegetarian menu", "Book-themed favors"], cant: ["Out-of-season cut flowers", "Single-use plastic"],
    dietary: "Fully vegetarian; 4 vegan; kosher-style for Leah's grandparents", accessibility: "Elevator access to mezzanine for elderly guests",
    family: "Two officiants — rabbi and Priya's uncle; coordinate at rehearsal", requests: "Guests plant seeds instead of signing a guest book" },
  { id: "c4", names: "Nate & Owen", full: "Nate Brooks & Owen Callahan",
    partners: [{ name: "Nate Brooks", email: "nate.brooks@yahoo.com", phone: "(508) 555-0153" }, { name: "Owen Callahan", email: "owen.cal@gmail.com", phone: "(508) 555-0166" }],
    preferred: "phone", date: "Aug 22, 2026", venue: "Driftwood Cove", location: "Cape Cod, MA", guests: 180, budget: 95000, tier: "Month-of Coordination",
    engaged: "Sep 1, 2024", story: "Summer neighbors turned partners of nine years. They planned most of it themselves and brought me in to land the plane — big, barefoot, and relaxed.",
    colors: ["#f5ead8", "#a19786", "#d67f48", "#474238"], theme: "Relaxed seaside — driftwood, rope, raw bar",
    boards: ["Shared album — Cove summers"],
    must: ["Raw bar", "Bonfire send-off", "Lawn games at cocktail hour"], cant: ["Assigned cocktail seating", "Formal dress code"],
    dietary: "Extensive shellfish menu — clear allergy labeling required", accessibility: "Beach-mat pathway for the sand ceremony",
    family: "Owen's mother uses a walker — firm pathways throughout", requests: "Both grandmothers as flower girls" }
];

const initialVendors = [
  { id: "v1", client: "c1", inv: "WS", name: "Wildstem Florals", category: "Florist", contact: "Nora Bell", phone: "(845) 555-0164", email: "nora@wildstem.com", website: "wildstem.com", address: "48 Main St, Beacon, NY",
    contractStatus: "Signed", range: "Mar 2 – Oct 10, 2026", keyTerms: "Full design + install; strike included; 30% deposit",
    quote: 9800, deposit: 2940, depositDue: "Mar 15, 2026", depositPaid: true, final: 6860, finalDue: "Sep 26, 2026", finalPaid: false, method: "ACH",
    services: ["Ceremony arch", "Harvest table garlands", "Personal flowers"], addons: ["Late-night bud vases"],
    lastContact: "Jul 8, 2026", recent: true, notes: "Confirmed dahlia availability; swapping ranunculus for garden roses in the personals." },
  { id: "v2", client: "c1", inv: "GH", name: "Golden Hour Studio", category: "Photography", contact: "Marcus Lee", phone: "(914) 555-0189", email: "marcus@goldenhour.studio", website: "goldenhour.studio", address: "12 Garden St, Cold Spring, NY",
    contractStatus: "Signed", range: "Feb 20 – Oct 10, 2026", keyTerms: "9-hr coverage; second shooter; gallery in 8 weeks",
    quote: 7200, deposit: 2400, depositDue: "Mar 1, 2026", depositPaid: true, final: 4800, finalDue: "Sep 12, 2026", finalPaid: false, method: "Card",
    services: ["9-hr coverage", "Second shooter", "Engagement session"], addons: ["Heirloom album"],
    lastContact: "Jul 10, 2026", recent: true, notes: "Shot list approved. Wants 20 minutes for golden-hour portraits starting 6:10 PM." },
  { id: "v3", client: "c1", inv: "HH", name: "Harvest & Hearth Catering", category: "Catering", contact: "Elena Ruiz", phone: "(845) 555-0112", email: "elena@harvesthearth.com", website: "harvesthearth.com", address: "310 Route 9, Rhinebeck, NY",
    contractStatus: "Signed", range: "Apr 1 – Oct 10, 2026", keyTerms: "Family-style for 140; staff + rentals included; final count Sep 12",
    quote: 21500, deposit: 6450, depositDue: "Apr 15, 2026", depositPaid: true, final: 15050, finalDue: "Sep 28, 2026", finalPaid: false, method: "ACH",
    services: ["Family-style dinner", "Staff + rentals", "Bar package"], addons: ["Late-night grilled cheese bar"],
    lastContact: "Jun 30, 2026", recent: false, notes: "Tasting booked Aug 6. Gluten-free menu confirmed for the family table." },
  { id: "v4", client: "c1", inv: "BB", name: "Brass & Birch Band", category: "DJ/Band", contact: "Theo Grant", phone: "(212) 555-0147", email: "theo@brassandbirch.com", website: "brassandbirch.com", address: "88 Water St, Brooklyn, NY",
    contractStatus: "Pending", range: "Hold through Jul 20, 2026", keyTerms: "5-piece band, 4 hours; ceremony trio add-on; 30% on signature",
    quote: 5500, deposit: 1650, depositDue: "Jul 5, 2026", depositPaid: false, final: 3850, finalDue: "Oct 1, 2026", finalPaid: false, method: "Check",
    services: ["5-piece band, 4 hrs", "Ceremony trio"], addons: [],
    lastContact: "Jun 24, 2026", recent: false, notes: "Contract redlines sent back — awaiting counter-signature. Deposit invoice unpaid." },
  { id: "v5", client: "c2", inv: "ME", name: "Miravalle Estate Catering", category: "Catering", contact: "Isabel Fuentes", phone: "(805) 555-0170", email: "events@casamiravalle.com", website: "casamiravalle.com", address: "1400 Miravalle Rd, Santa Barbara, CA",
    contractStatus: "Signed", range: "May 1 – Sep 19, 2026", keyTerms: "Plated dinner for 90; paella station; full bar; final count Aug 15",
    quote: 38000, deposit: 11400, depositDue: "May 15, 2026", depositPaid: true, final: 26600, finalDue: "Sep 5, 2026", finalPaid: false, method: "ACH",
    services: ["Plated dinner", "Paella station", "Full bar"], addons: ["Welcome dinner catering"],
    lastContact: "Jul 9, 2026", recent: true, notes: "Shellfish-allergy protocol documented for the paella station." },
  { id: "v6", client: "c2", inv: "LM", name: "Luz y Mar Photography", category: "Photography", contact: "Carmen Ortiz", phone: "(805) 555-0158", email: "carmen@luzymar.com", website: "luzymar.com", address: "220 State St, Santa Barbara, CA",
    contractStatus: "Signed", range: "Apr 10 – Sep 19, 2026", keyTerms: "10-hr coverage; film add-on; paid in full at booking discount",
    quote: 9500, deposit: 2850, depositDue: "Apr 20, 2026", depositPaid: true, final: 6650, finalDue: "Jun 1, 2026", finalPaid: true, method: "Card",
    services: ["10-hr coverage", "Super-8 film"], addons: [],
    lastContact: "Jul 2, 2026", recent: true, notes: "Paid in full. Scouting the bluff for ceremony light on Jul 30." },
  { id: "v7", client: "c2", inv: "PV", name: "Petal & Vine", category: "Florist", contact: "Gemma Hale", phone: "(805) 555-0183", email: "gemma@petalvine.com", website: "petalvine.com", address: "75 Anacapa St, Santa Barbara, CA",
    contractStatus: "Rejected", range: "—", keyTerms: "Quote declined — 40% over floral budget",
    quote: 14000, deposit: 0, depositDue: "—", depositPaid: false, final: 0, finalDue: "—", finalPaid: false, method: "—",
    services: ["Full floral design"], addons: [],
    lastContact: "Jun 18, 2026", recent: false, notes: "Declined final quote. Sourcing two alternate florists for bids by Jul 24." },
  { id: "v11", client: "c2", inv: "BB", name: "Bloom & Bramble", category: "Florist", contact: "Sasha Rowe", phone: "(805) 555-0196", email: "sasha@bloomandbramble.com", website: "bloomandbramble.com", address: "310 De La Guerra St, Santa Barbara, CA",
    contractStatus: "Considering", range: "Awaiting decision", keyTerms: "Quote received — comparing against other florists",
    quote: 10200, deposit: 0, depositDue: "—", depositPaid: false, final: 0, finalDue: "—", finalPaid: false, method: "—",
    services: ["Ceremony arch", "Reception centerpieces", "Personal flowers"], addons: [],
    lastContact: "Jul 5, 2026", recent: true, notes: "Loose, garden-style arrangements. Can match the estate's citrus grove palette." },
  { id: "v12", client: "c2", inv: "SR", name: "Sable Rose Florals", category: "Florist", contact: "Priya Sable", phone: "(805) 555-0207", email: "priya@sablerose.com", website: "sablerose.com", address: "88 Coast Village Rd, Montecito, CA",
    contractStatus: "Considering", range: "Awaiting decision", keyTerms: "Quote received — comparing against other florists",
    quote: 12750, deposit: 0, depositDue: "—", depositPaid: false, final: 0, finalDue: "—", finalPaid: false, method: "—",
    services: ["Ceremony arch", "Reception centerpieces", "Personal flowers", "Welcome dinner florals"], addons: ["Bud vase upgrade"],
    lastContact: "Jul 7, 2026", recent: true, notes: "Higher-end look with imported garden roses; would need to trim the welcome-dinner add-on to fit budget." },
  { id: "v8", client: "c3", inv: "TG", name: "The Glasshouse", category: "Venue", contact: "Rowan Pierce", phone: "(503) 555-0121", email: "rowan@glasshousepdx.com", website: "glasshousepdx.com", address: "900 SE Alder St, Portland, OR",
    contractStatus: "Signed", range: "Apr 10 – Nov 14, 2026", keyTerms: "12-hr rental; tables + chairs; on-site coordinator",
    quote: 12000, deposit: 4000, depositDue: "Apr 24, 2026", depositPaid: true, final: 8000, finalDue: "Oct 30, 2026", finalPaid: false, method: "ACH",
    services: ["Venue rental, 12 hrs", "Tables + chairs", "On-site coordinator"], addons: [],
    lastContact: "Jul 6, 2026", recent: true, notes: "Mezzanine elevator inspection certificate received and filed." },
  { id: "v9", client: "c3", inv: "FF", name: "Fern & Fig Cakes", category: "Cake Baker", contact: "June Park", phone: "(503) 555-0139", email: "june@fernfig.com", website: "fernfig.com", address: "41 N Mississippi Ave, Portland, OR",
    contractStatus: "Signed", range: "May 5 – Nov 14, 2026", keyTerms: "3-tier + vegan sheet cake; delivery included; paid in full",
    quote: 1850, deposit: 925, depositDue: "May 20, 2026", depositPaid: true, final: 925, finalDue: "Jun 15, 2026", finalPaid: true, method: "Card",
    services: ["3-tier cake", "Vegan sheet cake"], addons: [],
    lastContact: "Jun 27, 2026", recent: false, notes: "Final design approved — pressed ferns, no fresh florals." },
  { id: "v10", client: "c4", inv: "DC", name: "Driftwood Cove Resort", category: "Venue", contact: "Pat Sullivan", phone: "(508) 555-0102", email: "events@driftwoodcove.com", website: "driftwoodcove.com", address: "2 Shore Rd, Chatham, MA",
    contractStatus: "Pending", range: "Hold through Jul 25, 2026", keyTerms: "Beach ceremony + tent reception; room block for 60; insurance rider under review",
    quote: 30000, deposit: 9000, depositDue: "Jul 25, 2026", depositPaid: false, final: 21000, finalDue: "Aug 10, 2026", finalPaid: false, method: "ACH",
    services: ["Beach ceremony site", "Tent + dance floor", "Guest room block"], addons: ["Bonfire permit handling"],
    lastContact: "Jul 11, 2026", recent: true, notes: "Holding the date through Jul 25. Contract in legal review — insurance rider question open." }
];

const CATEGORIES = [
  "Venue", "Officiant", "Catering", "Photography", "Videography", "DJ/Band",
  "Florist", "Cake Baker", "Rentals", "Hair & Makeup", "Stationer",
  "Transportation", "Lighting", "Decor", "Entertainment", "Photo Booth",
  "Bartending", "Accommodations"
];

/* ─────────────── vendor library (global, auto-populated) ───────────────
   Every vendor added to any client is upserted into a single global library,
   deduped by normalized name + category, so proven vendors can be reused.
------------------------------------------------------------------------ */

const normVendorKey = (name, category) =>
  String(name || "").trim().toLowerCase() + "|" + String(category || "").trim().toLowerCase();

// Fold one vendor record into a library array; returns a NEW array.
function upsertVendorToLibrary(library, v, clients) {
  const key = normVendorKey(v.name, v.category);
  const client = (clients || []).find(c => c.id === v.client);
  const clientName = client ? client.full : v.client;
  const next = library.map(e => ({ ...e, usedWith: [...e.usedWith], pricing: [...e.pricing] }));
  let entry = next.find(e => e.id === key);
  if (!entry) {
    entry = {
      id: key, name: v.name, category: v.category,
      contact: v.contact || "", phone: v.phone || "", email: v.email || "", website: v.website || "",
      usedWith: [], pricing: [], notes: "", rating: 0
    };
    next.push(entry);
  }
  // Fill any missing contact fields from this sighting.
  entry.contact = entry.contact || v.contact || "";
  entry.phone = entry.phone || v.phone || "";
  entry.email = entry.email || v.email || "";
  entry.website = entry.website || (v.website && v.website !== "—" ? v.website : "");
  if (v.client && !entry.usedWith.some(u => u.clientId === v.client)) {
    entry.usedWith.push({ clientId: v.client, clientName });
  }
  if (v.quote && !entry.pricing.some(p => p.clientId === v.client && p.quote === v.quote)) {
    entry.pricing.push({ clientId: v.client, clientName, quote: v.quote, status: v.contractStatus || "—" });
  }
  return next;
}

// Backfill the library from an existing set of vendors (used to seed on first load).
function buildVendorLibrary(vendors, clients) {
  return vendors.reduce((lib, v) => upsertVendorToLibrary(lib, v, clients), []);
}

const milestones = {
  c1: [
    { date: "Feb 14", title: "Venue contract signed", desc: "Cedar Hollow Farm — full property, 12 hours", done: true },
    { date: "Apr 3", title: "Design concept approved", desc: "Autumn harvest palette; brass + candlelight", done: true },
    { date: "May 20", title: "Photography & florals booked", desc: "Golden Hour Studio; Wildstem Florals", done: true },
    { date: "Jul 24", title: "Venue walkthrough", desc: "Confirm zones, power for band, rain-plan flip", done: false },
    { date: "Aug 6", title: "Menu tasting & final", desc: "Harvest & Hearth — lock family-style menu", done: false },
    { date: "Sep 12", title: "Final headcount due", desc: "To caterer and rentals", done: false },
    { date: "Sep 28", title: "Final vendor payments due", desc: "Florals + catering balances", done: false },
    { date: "Oct 9", title: "Rehearsal & welcome drinks", desc: "Processional run-through with Biscuit", done: false }
  ],
  c2: [
    { date: "Mar 1", title: "Venue contract signed", desc: "Casa Miravalle — bluff + terrace ballroom", done: true },
    { date: "May 12", title: "Design concept approved", desc: "Citrus, linen, terracotta", done: true },
    { date: "Jun 20", title: "Catering tasting complete", desc: "Paella station approved with allergy protocol", done: true },
    { date: "Jul 24", title: "Florist rebid due", desc: "Two alternates after Petal & Vine declined", done: false },
    { date: "Jul 30", title: "Bluff walkthrough & light scout", desc: "With Luz y Mar", done: false },
    { date: "Aug 15", title: "Final headcount due", desc: "To estate catering", done: false },
    { date: "Sep 5", title: "Final payments due", desc: "Catering balance", done: false },
    { date: "Sep 18", title: "Rehearsal & welcome dinner", desc: "Bilingual run-of-show", done: false }
  ],
  c3: [
    { date: "Apr 10", title: "Venue contract signed", desc: "The Glasshouse — atrium + mezzanine", done: true },
    { date: "Jun 5", title: "Interfaith ceremony plan approved", desc: "Chuppah + garland exchange, two officiants", done: true },
    { date: "Aug 20", title: "Design walkthrough", desc: "Living-plant installs; zero cut flowers out of season", done: false },
    { date: "Oct 1", title: "Final headcount due", desc: "Vegetarian counts + vegan meals", done: false },
    { date: "Oct 30", title: "Final payments due", desc: "Venue balance", done: false },
    { date: "Nov 13", title: "Rehearsal — both officiants", desc: "Coordinate rabbi + uncle; elevator check", done: false }
  ],
  c4: [
    { date: "Jul 25", title: "Venue contract deadline", desc: "Driftwood Cove date hold expires", done: false },
    { date: "Jul 28", title: "Full vendor handoff", desc: "Couple's self-booked vendors to GhostWriter Events", done: false },
    { date: "Aug 1", title: "Final headcount due", desc: "Raw bar quantities + allergy labels", done: false },
    { date: "Aug 10", title: "Final payments due", desc: "Resort balance", done: false },
    { date: "Aug 21", title: "Rehearsal & clambake", desc: "Beach-mat pathway test; fire-marshal permit posted", done: false }
  ]
};

const PHASES = [
  { key: "early", label: "Early Planning", blurb: "Getting started — profile, vision, first vendors" },
  { key: "booking", label: "Vendor Booking", blurb: "Sourcing, comparing, and signing vendors" },
  { key: "final", label: "Final Details", blurb: "Locking numbers, payments, and logistics" },
  { key: "week", label: "Wedding Week", blurb: "Day-of schedule and last-minute coordination" }
];

const PHASE_TAG = { early: "tag-neutral", booking: "tag-neutral", final: "tag-neutral", week: "tag-neutral" };

const PHASE_MODULES = {
  early: ["tasks"],
  booking: ["vendors", "documents"],
  final: ["budget"],
  week: ["timeline"]
};

function derivePhase(client, msList) {
  const daysUntil = Math.round((Date.parse(client.date) - NOW) / 86400000);
  const total = msList.length;
  const doneCount = msList.filter(m => m.done).length;
  const remaining = total - doneCount;
  if (daysUntil <= 14) return "week";
  if (daysUntil <= 45 || (total > 0 && remaining <= 2)) return "final";
  if (doneCount > 0) return "booking";
  return "early";
}

const mkDay = o => [
  { time: "9:00 AM", event: "Planner & setup crew arrive", vendor: "GhostWriter Events", zone: o.main, note: "Load-in via service entrance" },
  { time: "10:30 AM", event: "Florals load-in & install", vendor: o.florist, zone: o.ceremonyZone, note: "Ceremony install first, tables second" },
  { time: "11:30 AM", event: "Catering arrives — kitchen setup", vendor: o.caterer, zone: "Prep kitchen", note: "Vendor parking: " + o.parking },
  { time: "1:00 PM", event: "Photographer arrives — details & getting ready", vendor: o.photog, zone: o.suite, note: "" },
  { time: "2:30 PM", event: "Music soundcheck", vendor: o.music, zone: o.main, note: "" },
  { time: "3:30 PM", event: "First look & portraits", vendor: o.photog, zone: o.portraits, note: "" },
  { time: o.t[0], event: "Ceremony — processional, vows, rings, kiss, recessional", vendor: "All hands", zone: o.ceremonyZone, note: "Guests seated 15 minutes prior" },
  { time: o.t[1], event: "Cocktail hour", vendor: o.caterer, zone: o.cocktail, note: o.cocktailNote || "" },
  { time: o.t[2], event: "Reception opens — dinner service", vendor: o.caterer, zone: o.main, note: "" },
  { time: o.t[3], event: "Toasts & blessings", vendor: "MC", zone: o.main, note: "" },
  { time: o.t[4], event: "Cake cutting", vendor: o.baker || o.caterer, zone: o.main, note: "" },
  { time: o.t[5], event: "First dance — open dance floor", vendor: o.music, zone: o.main, note: "" },
  { time: o.t[6], event: "Last dance & send-off", vendor: o.music, zone: o.main, note: o.sendoff || "" },
  { time: o.t[7], event: "Breakdown & venue close-out", vendor: "All vendors", zone: "All zones", note: "Strike complete; planner final walkthrough" }
];

const days = {
  c1: mkDay({ main: "Barn main hall", ceremonyZone: "Ceremony lawn", suite: "Farmhouse suite", portraits: "Orchard rows", cocktail: "Courtyard", parking: "rear gravel lot",
    florist: "Wildstem Florals", caterer: "Harvest & Hearth", photog: "Golden Hour Studio", music: "Brass & Birch Band",
    t: ["4:30 PM", "5:00 PM", "6:00 PM", "7:00 PM", "8:15 PM", "8:30 PM", "10:45 PM", "11:00 PM"], sendoff: "Sparkler exit — buckets at barn doors" }),
  c2: mkDay({ main: "Terrace ballroom", ceremonyZone: "Bluff overlook", suite: "Bridal casita", portraits: "Citrus grove", cocktail: "Loggia", parking: "lower lot (golf-cart shuttle)",
    florist: "Florist TBD — rebidding", caterer: "Miravalle Estate Catering", photog: "Luz y Mar Photography", music: "String quartet + DJ",
    t: ["5:00 PM", "5:30 PM", "6:30 PM", "7:30 PM", "8:45 PM", "9:00 PM", "11:15 PM", "11:30 PM"], cocktailNote: "Paella station — allergy signage posted" }),
  c3: mkDay({ main: "Glasshouse atrium", ceremonyZone: "Conservatory", suite: "Green room", portraits: "Fern mezzanine", cocktail: "Atrium north", parking: "SE Alder loading dock",
    florist: "In-house greenery", caterer: "Caterer TBD", photog: "Photographer TBD", music: "Playlist + rented PA", baker: "Fern & Fig Cakes",
    t: ["4:00 PM", "4:30 PM", "5:30 PM", "6:30 PM", "7:45 PM", "8:00 PM", "9:45 PM", "10:00 PM"] }),
  c4: mkDay({ main: "Reception tent", ceremonyZone: "Beach ceremony site", suite: "Resort suite 210", portraits: "Dune path", cocktail: "Lawn (games out)", parking: "resort service lot",
    florist: "Couple's florist (handoff)", caterer: "Resort catering", photog: "Couple's photographer (handoff)", music: "DJ (handoff)",
    t: ["4:30 PM", "5:00 PM", "6:15 PM", "7:15 PM", "8:30 PM", "8:45 PM", "10:30 PM", "11:00 PM"], sendoff: "Bonfire send-off — permit posted at tent" })
};

const zones = {
  c1: ["Zone A — barn main hall: reception + dancing", "Ceremony lawn: chairs, arch, aisle runner", "Prep kitchen: catering access only", "Vendor parking: rear gravel lot"],
  c2: ["Terrace ballroom: dinner + dancing", "Bluff overlook: ceremony (shuttle access)", "Loggia: cocktail hour", "Vendor parking: lower lot"],
  c3: ["Atrium: ceremony flips to reception", "Mezzanine: cocktail overflow (elevator)", "Green room: couple + officiants", "Loading dock: SE Alder side"],
  c4: ["Reception tent: dinner + dancing", "Beach site: ceremony (mat pathway)", "Lawn: cocktail hour + games", "Vendor parking: resort service lot"]
};

const contingencies = {
  c1: ["Rain plan: ceremony flips into barn, cocktails to loft — call by 12:00 PM", "Sunset 6:24 PM — portraits must start by 6:10 PM", "Generator on standby for tent heaters if under 50°F"],
  c2: ["Marine layer: hold ceremony 20 min max, then flip to loggia", "Wind over 15 mph: weight linens, move quartet inside", "Shuttle backup: second golf cart on call"],
  c3: ["November rain assumed — all zones indoors; umbrellas at door", "Elevator outage: ground-floor seating plan on file", "Candle policy: enclosed votives only per venue"],
  c4: ["High-tide check 3:40 PM — ceremony line set above wrack line", "Storm plan: full event under tent, walls on standby", "Bonfire canceled if red-flag warning — s'mores bar under tent instead"]
};

const expenses = {
  c1: [{ label: "Marriage license", amt: 40 }, { label: "Welcome bags & favors", amt: 620 }, { label: "Invitations & postage", amt: 480 }, { label: "Vendor tips (cash envelopes)", amt: 1400 }],
  c2: [{ label: "Marriage license", amt: 100 }, { label: "Bilingual signage & printing", amt: 540 }, { label: "Welcome dinner favors", amt: 800 }, { label: "Vendor tips", amt: 1800 }],
  c3: [{ label: "Marriage license", amt: 60 }, { label: "Book-themed favors", amt: 380 }, { label: "Seed-planting station", amt: 220 }, { label: "Vendor tips", amt: 700 }],
  c4: [{ label: "Marriage license", amt: 50 }, { label: "Lawn games rental", amt: 340 }, { label: "Fire-marshal permit", amt: 125 }, { label: "Vendor tips", amt: 1500 }]
};

const DOC_CATEGORIES = ["Signed agreement", "Proposal", "Invoice", "Amendment", "Contract", "Other"];
// Older saves / seed data used plural category names — map them onto the canonical set.
const DOC_CATEGORY_ALIASES = { "Signed agreements": "Signed agreement", "Proposals": "Proposal", "Invoices": "Invoice", "Amendments": "Amendment", "Contracts": "Contract" };

const initialDocs = {
  c1: [
    { id: "c1-doc1", name: "Wildstem — Signed Agreement.pdf", category: "Signed agreement", vendor: "Wildstem Florals", status: "Executed", range: "Mar 2 – Oct 10, 2026", amount: 9800, terms: "30% deposit; balance 14 days before event", cancel: "Full refund 90+ days out; deposit forfeit after", insurance: "COI on file — $1M liability" },
    { id: "c1-doc2", name: "Wildstem — Proposal v3.pdf", category: "Proposal", vendor: "Wildstem Florals", status: "Signed", range: "Feb 2026", amount: 9800, terms: "Design scope: arch, garlands, personals", cancel: "—", insurance: "—" },
    { id: "c1-doc3", name: "Invoice WS-201 — Deposit.pdf", category: "Invoice", vendor: "Wildstem Florals", status: "Sent", range: "Issued Mar 2, 2026", amount: 2940, terms: "Paid Mar 14 via ACH", cancel: "—", insurance: "—" },
    { id: "c1-doc4", name: "Golden Hour — Signed Agreement.pdf", category: "Signed agreement", vendor: "Golden Hour Studio", status: "Executed", range: "Feb 20 – Oct 10, 2026", amount: 7200, terms: "1/3 deposit; balance 4 weeks out", cancel: "Deposit non-refundable within 60 days", insurance: "COI on file" },
    { id: "c1-doc5", name: "Amendment — Heirloom Album.pdf", category: "Amendment", vendor: "Golden Hour Studio", status: "Draft", range: "Jul 2026", amount: 650, terms: "Add-on to base package", cancel: "—", insurance: "—" },
    { id: "c1-doc6", name: "Brass & Birch — Performance Contract.pdf", category: "Contract", vendor: "Brass & Birch Band", status: "Sent", range: "Hold through Jul 20, 2026", amount: 5500, terms: "30% deposit on signature", cancel: "Full refund until signature", insurance: "COI pending", expires: "Date hold expires Jul 20, 2026" }
  ],
  c2: [
    { id: "c2-doc1", name: "Miravalle — Signed Agreement.pdf", category: "Signed agreement", vendor: "Miravalle Estate Catering", status: "Executed", range: "May 1 – Sep 19, 2026", amount: 38000, terms: "30% deposit; balance 2 weeks out", cancel: "50% refund 60+ days out", insurance: "Estate-carried — rider on file" },
    { id: "c2-doc2", name: "Invoice ME-118 — Deposit.pdf", category: "Invoice", vendor: "Miravalle Estate Catering", status: "Sent", range: "Issued May 1, 2026", amount: 11400, terms: "Paid May 12 via ACH", cancel: "—", insurance: "—" },
    { id: "c2-doc3", name: "Luz y Mar — Signed Agreement.pdf", category: "Signed agreement", vendor: "Luz y Mar Photography", status: "Executed", range: "Apr 10 – Sep 19, 2026", amount: 9500, terms: "Paid in full — 5% booking discount", cancel: "Retainer non-refundable", insurance: "COI on file" }
  ],
  c3: [
    { id: "c3-doc1", name: "Glasshouse — Venue Agreement.pdf", category: "Signed agreement", vendor: "The Glasshouse", status: "Executed", range: "Apr 10 – Nov 14, 2026", amount: 12000, terms: "1/3 deposit; balance 2 weeks out", cancel: "Deposit transferable to new date once", insurance: "COI renewal due Aug 1, 2026", expires: "Venue COI renewal due Aug 1, 2026" },
    { id: "c3-doc2", name: "Invoice FF-77 — Paid in Full.pdf", category: "Invoice", vendor: "Fern & Fig Cakes", status: "Sent", range: "Issued May 5, 2026", amount: 1850, terms: "Paid Jun 15 via card", cancel: "—", insurance: "—" }
  ],
  c4: [
    { id: "c4-doc1", name: "Driftwood Cove — Proposal.pdf", category: "Proposal", vendor: "Driftwood Cove Resort", status: "Sent", range: "Hold through Jul 25, 2026", amount: 30000, terms: "30% deposit to confirm date", cancel: "No obligation until signature", insurance: "Rider under legal review", expires: "Date hold expires Jul 25, 2026" }
  ]
};

const portfolio = [
  { couple: "Ava & Theo", date: "Sep 2025", venue: "Stonefield Orchard", style: "Rustic", colors: ["#c67139", "#8c491a", "#f5ead8"], elements: "Ceremony in the apple rows; quilt-square escort wall", credits: ["Wildstem Florals", "Golden Hour Studio"], quote: "Every detail felt like us — down to the cider toast.", h: 260 },
  { couple: "Imani & Rose", date: "Jun 2025", venue: "The Glasshouse", style: "Modern", colors: ["#ccdbb2", "#7a8a5e", "#2e2b25"], elements: "Suspended fern installation; hand-tied single-stem bouquets", credits: ["The Glasshouse", "Fern & Fig Cakes"], quote: null, h: 200 },
  { couple: "Carmen & Luis", date: "May 2025", venue: "Casa Miravalle", style: "Coastal", colors: ["#d67f48", "#f9f4ed", "#8fa073"], elements: "Citrus garlands; blue-tile ceremony backdrop", credits: ["Miravalle Estate Catering", "Luz y Mar Photography"], quote: "Our abuela cried twice. Perfect.", h: 300 },
  { couple: "Beatrix & Sam", date: "Oct 2024", venue: "Cedar Hollow Farm", style: "Rustic", colors: ["#8c491a", "#a19786", "#f5ead8"], elements: "Candlelit barn; 40-foot harvest table", credits: ["Harvest & Hearth", "Golden Hour Studio"], quote: null, h: 220 },
  { couple: "Noor & Dev", date: "Aug 2024", venue: "Driftwood Cove", style: "Coastal", colors: ["#f5ead8", "#a19786", "#474238"], elements: "Sand ceremony at low tide; bonfire send-off", credits: ["Driftwood Cove Resort"], quote: "They made 200 guests feel like a dinner party.", h: 280 },
  { couple: "June & Wren", date: "Apr 2024", venue: "Fern Hollow Gardens", style: "Garden", colors: ["#7a8a5e", "#ccdbb2", "#f9f4ed"], elements: "Living centerpieces guests took home; moss table runners", credits: ["Wildstem Florals"], quote: null, h: 240 }
];

const decisions = {
  c1: ["Approved garden-rose swap in personal flowers", "Locked family-style menu with dedicated GF table", "Chose brass votives over taper candles"],
  c2: ["Declined Petal & Vine quote — rebidding florals", "Approved paella-station allergy protocol", "Confirmed welcome-dinner menu"],
  c3: ["Approved interfaith ceremony order", "Locked vegan cake flavors", "Chose seed-planting station over guest book"],
  c4: ["Escalated Driftwood contract to legal review", "Approved raw-bar layout with allergy labeling", "Booked fire-marshal permit for bonfire"]
};

const TASK_TEMPLATES = {
  "Full Planning": [
    { title: "Initial consultation", description: "Discovery call to understand vision, budget, and priorities." },
    { title: "Vendor research and recommendations", description: "Source and shortlist vendors across every category." },
    { title: "Client meetings and design consultations", description: "Recurring check-ins to align on design direction." },
    { title: "Design development", description: "Finalize color palette, theme, and key design elements." },
    { title: "Timeline creation", description: "Build planning milestones and the wedding-day schedule." },
    { title: "Vendor coordination and contracting", description: "Negotiate, contract, and onboard every booked vendor." },
    { title: "Payment tracking", description: "Track deposits, balances, and due dates across all vendors." },
    { title: "Final walkthrough", description: "Walk the venue with the couple to confirm layout and logistics." },
    { title: "Day-of execution", description: "Run the full wedding day from vendor arrival to send-off." },
    { title: "Post-wedding tasks", description: "Close out final payments, gather photos, and follow up." }
  ],
  "Partial Planning": [
    { title: "Design guidance", description: "Advise on palette, theme, and overall design direction." },
    { title: "Vendor coordination (subset)", description: "Coordinate the vendors the couple hasn't already booked." },
    { title: "Limited client meetings", description: "A defined number of check-ins through the planning process." },
    { title: "Timeline creation", description: "Build planning milestones and the wedding-day schedule." },
    { title: "Final walkthrough", description: "Walk the venue with the couple to confirm layout and logistics." },
    { title: "Day-of execution", description: "Run the full wedding day from vendor arrival to send-off." }
  ],
  "Month-of Coordination": [
    { title: "Final vendor confirmations", description: "Confirm scope, timing, and details with every booked vendor." },
    { title: "Vendor check-ins", description: "Touch base with each vendor in the final weeks." },
    { title: "Timeline finalization", description: "Lock the wedding-day timeline and share with all vendors." },
    { title: "Rehearsal coordination", description: "Run the ceremony rehearsal with wedding party and officiant." },
    { title: "Last-minute adjustments", description: "Handle late changes to guest count, layout, or logistics." },
    { title: "Final payments confirmation", description: "Confirm all final vendor balances are paid before the day." }
  ],
  "Day-of Coordination": [
    { title: "Vendor arrival coordination", description: "Check in every vendor against their scheduled arrival time." },
    { title: "Timeline execution", description: "Keep the wedding-day timeline on schedule in real time." },
    { title: "On-the-day problem-solving", description: "Handle whatever comes up without involving the couple." },
    { title: "Guest management", description: "Direct guests through ceremony, cocktail hour, and reception." },
    { title: "Timeline adjustments", description: "Adjust the run-of-show on the fly as the day unfolds." },
    { title: "Event wrap-up", description: "Oversee breakdown, vendor departure, and venue close-out." }
  ]
};


function seedTask(client, tier, idx, overrides) {
  const t = TASK_TEMPLATES[tier][idx];
  return { id: client + "-t" + idx, title: t.title, description: t.description, status: "incomplete", due: "", assignedTo: "", notes: "", custom: false, ...overrides };
}

const initialTasks = {
  c1: [
    seedTask("c1", "Full Planning", 0, { status: "complete", due: "Jan 15, 2026", assignedTo: "Lead planner" }),
    seedTask("c1", "Full Planning", 1, { status: "complete", due: "Feb 2026", assignedTo: "Lead planner" }),
    seedTask("c1", "Full Planning", 2, { status: "in-progress", assignedTo: "Lead planner", notes: "Monthly check-ins continue through the fall." }),
    seedTask("c1", "Full Planning", 3, { status: "complete", due: "Apr 3, 2026" }),
    seedTask("c1", "Full Planning", 4, { status: "complete", due: "May 20, 2026" }),
    seedTask("c1", "Full Planning", 5, { status: "in-progress", notes: "Brass & Birch Band contract still awaiting counter-signature." }),
    seedTask("c1", "Full Planning", 6, { status: "in-progress", notes: "Florals, catering, and photography balances due in September." }),
    seedTask("c1", "Full Planning", 7, { status: "incomplete", due: "Jul 24, 2026" }),
    seedTask("c1", "Full Planning", 8, { status: "incomplete", due: "Oct 10, 2026" }),
    seedTask("c1", "Full Planning", 9, { status: "incomplete", due: "Oct 2026" }),
    { id: "c1-custom1", title: "Confirm Biscuit's ceremony-only ring-bearer route", description: "Coordinate with the venue and photographer on timing so Biscuit is only present for the ceremony.", status: "incomplete", due: "Sep 2026", assignedTo: "Lead planner", notes: "Added after the couple's request — not part of the standard template.", custom: true }
  ],
  c2: [
    seedTask("c2", "Full Planning", 0, { status: "complete", due: "Feb 2026", assignedTo: "Lead planner" }),
    seedTask("c2", "Full Planning", 1, { status: "complete", due: "Mar 2026" }),
    seedTask("c2", "Full Planning", 2, { status: "complete" }),
    seedTask("c2", "Full Planning", 3, { status: "complete", due: "May 12, 2026" }),
    seedTask("c2", "Full Planning", 4, { status: "complete" }),
    seedTask("c2", "Full Planning", 5, { status: "in-progress", due: "Jul 24, 2026", notes: "Rebidding florals after Petal & Vine's quote came in over budget." }),
    seedTask("c2", "Full Planning", 6, { status: "in-progress" }),
    seedTask("c2", "Full Planning", 7, { status: "incomplete", due: "Jul 30, 2026" }),
    seedTask("c2", "Full Planning", 8, { status: "incomplete", due: "Sep 19, 2026" }),
    seedTask("c2", "Full Planning", 9, { status: "incomplete" }),
    { id: "c2-custom1", title: "Arrange bilingual signage and MC for Abuela", description: "Ensure ceremony and reception signage and announcements are bilingual for Spanish-speaking family.", status: "in-progress", due: "Aug 2026", assignedTo: "Lead planner", notes: "Added after the couple's request — not part of the standard template.", custom: true }
  ],
  c3: [
    seedTask("c3", "Partial Planning", 0, { status: "complete", due: "Jun 5, 2026" }),
    seedTask("c3", "Partial Planning", 1, { status: "in-progress", notes: "Caterer and photographer still to be booked." }),
    seedTask("c3", "Partial Planning", 2, { status: "in-progress" }),
    seedTask("c3", "Partial Planning", 3, { status: "in-progress" }),
    seedTask("c3", "Partial Planning", 4, { status: "incomplete", due: "Aug 20, 2026" }),
    seedTask("c3", "Partial Planning", 5, { status: "incomplete", due: "Nov 14, 2026" }),
    { id: "c3-custom1", title: "Coordinate two officiants for rehearsal", description: "Align the rabbi and Priya's uncle on order of service ahead of rehearsal.", status: "incomplete", due: "Nov 13, 2026", notes: "Added after the couple's request — not part of the standard template.", custom: true }
  ],
  c4: [
    seedTask("c4", "Month-of Coordination", 0, { status: "in-progress", due: "Jul 28, 2026", notes: "Vendor handoff from the couple's self-booked team." }),
    seedTask("c4", "Month-of Coordination", 1, { status: "incomplete", due: "Aug 2026" }),
    seedTask("c4", "Month-of Coordination", 2, { status: "in-progress" }),
    seedTask("c4", "Month-of Coordination", 3, { status: "incomplete", due: "Aug 21, 2026" }),
    seedTask("c4", "Month-of Coordination", 4, { status: "incomplete", due: "Aug 22, 2026" }),
    seedTask("c4", "Month-of Coordination", 5, { status: "incomplete", due: "Aug 10, 2026" }),
    { id: "c4-custom1", title: "Confirm fire-marshal bonfire permit is posted", description: "Verify the permit is displayed at the tent before the bonfire send-off.", status: "incomplete", due: "Aug 21, 2026", notes: "Added after the couple's request — not part of the standard template.", custom: true }
  ]
};

function paymentsFor(vs) {
  const out = [];
  vs.filter(v => v.contractStatus !== "Rejected" && v.contractStatus !== "Considering").forEach(v => {
    const st = (paid, due) => paid ? "Paid" : (Date.parse(due) < NOW ? "Overdue" : "Pending");
    out.push({ vendor: v.name, label: "Deposit — " + v.name, due: v.depositDue, amount: v.deposit, method: v.method, invoice: v.inv + "-201", status: st(v.depositPaid, v.depositDue), paid: v.depositPaid, ts: Date.parse(v.depositDue) });
    out.push({ vendor: v.name, label: "Final — " + v.name, due: v.finalDue, amount: v.final, method: v.method, invoice: v.inv + "-202", status: st(v.finalPaid, v.finalDue), paid: v.finalPaid, ts: Date.parse(v.finalDue) });
  });
  return out;
}

// Status chips (brand deck): Draft/Pending/Considering = vellum · Signed/Paid = ink · Overdue = oxblood (the view's one alert).
// Status chip colours live in ui.jsx (statusClass). Tiers are labels, not statuses: always vellum.
const DOC_STATUSES = ["Draft", "Sent", "Signed", "Executed"];
// Budget chart: ink → graphite → vellum. Oxblood is kept for an over-budget category only.
function inkScale(n) {
  const a = [20, 20, 20], b = [214, 207, 196];
  return Array.from({ length: Math.max(n, 1) }, (_, i) => {
    const t = n <= 1 ? 0 : i / (n - 1);
    return "rgb(" + a.map((x, k) => Math.round(x + (b[k] - x) * t)).join(",") + ")";
  });
}

/* ───────────────────────── small building blocks ───────────────────────── */

function Icon({ path, size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round">
      {path}
    </svg>
  );
}

const ICONS = {
  clients: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M22 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></>,
  vendors: <><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"></path><path d="M3 6h18"></path><path d="M16 10a4 4 0 0 1-8 0"></path></>,
  tasks: <><rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path><path d="m9 14 2 2 4-4"></path></>,
  timeline: <><rect x="3" y="4" width="18" height="17" rx="4"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></>,
  budget: <><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"></path><path d="M3 5v14a2 2 0 0 0 2 2h16v-5"></path><path d="M18 12a2 2 0 0 0 0 4h4v-4Z"></path></>,
  documents: <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z"></path>,
  portfolio: <><rect x="3" y="3" width="18" height="18" rx="4"></rect><circle cx="9" cy="9" r="2"></circle><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path></>,
  report: <><path d="m22 2-7 20-4-9-9-4Z"></path><path d="M22 2 11 13"></path></>,
  library: <><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path><line x1="9" y1="6" x2="16" y2="6"></line></>,
  quotes: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><path d="M14 2v6h6"></path><path d="M12 18v-6"></path><path d="M9 15h6"></path></>,
  gear: <><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"></path></>
};

const TITLES = { clients: "Client Profiles", tasks: "Tasks", vendors: "Vendor Management", timeline: "Timeline & Scheduling", budget: "Budget & Payments", documents: "Documents", quotes: "Quotes & Contracts", portfolio: "Past Weddings", library: "Vendor Library", report: "Weekly Client Report" };
const NAV_LABELS = { clients: "Clients", tasks: "Tasks", vendors: "Vendors", timeline: "Timeline", budget: "Budget", documents: "Documents", quotes: "Quotes & Contracts", portfolio: "Past Weddings", library: "Vendor Library", report: "Weekly Report" };

// Flat, single-level navigation order (phase group headers removed in Phase 8 · Step 2).
const FLAT_NAV = ["clients", "tasks", "vendors", "documents", "budget", "timeline", "quotes", "portfolio", "library", "report"];

function PhotoPlaceholder({ label, height }) {
  return <div className="washed ph-photo" style={{ height }}>{label}</div>;
}

/* ───────────────────────── module views ───────────────────────── */

function IntakeSummary({ intake }) {
  const list = arr => (arr && arr.length) ? arr.join(", ") : "—";
  const val = v => (v == null || v === "") ? "—" : v;
  const rows = [
    ["Home city", val(intake.you.homeCity)],
    ["Date", val(intake.basics.dateStatus) + (intake.basics.date ? " · " + intake.basics.date : "") + (intake.basics.dateFlex ? " (" + intake.basics.dateFlex + ")" : "")],
    ["Destination", val(intake.basics.destination) + (intake.basics.destinationDetails ? " — " + intake.basics.destinationDetails : "")],
    ["Guests", val(intake.basics.guests) + (intake.basics.guestCertainty ? " (" + intake.basics.guestCertainty + ")" : "") + (intake.basics.partySize ? " · party of " + intake.basics.partySize : "")],
    ["Budget band", val(intake.budget.band) + (intake.budget.exact ? " · " + fmt(intake.budget.exact) : "") + " · " + val(intake.budget.firmness)],
    ["Top priorities", list(intake.budget.priorities)],
    ["Service level", val(intake.vision.serviceTier) + " · " + val(intake.vision.howFarAlong)],
    ["Take off their plate", list(intake.vision.tasksToOffload)],
    ["Venue", val(intake.venueVendors.status) + " · " + val(intake.venueVendors.venueType) + (intake.venueVendors.outdoorNeeds ? " — " + intake.venueVendors.outdoorNeeds : "")],
    ["Vendors booked", list(intake.venueVendors.vendorsBooked)],
    ["Needs help with", list(intake.venueVendors.vendorsNeedHelp)],
    ["Vibe / formality", list(intake.vision.vibe) + (intake.vision.formality ? " · " + intake.vision.formality : "")],
    ["Fusion / traditions", val(intake.details.fusion) + (intake.details.extraEvents && intake.details.extraEvents.length ? " — " + intake.details.extraEvents.join(", ") : "") + (intake.details.traditions ? " · " + intake.details.traditions : "")],
    ["Multi-day", val(intake.details.multiDay)],
    ["Meal style", val(intake.details.mealStyle)],
    ["Working style", val(intake.working.involvement) + (intake.working.meetingCadence ? " · " + intake.working.meetingCadence + " check-ins" : "")],
    ["Timeline to book", val(intake.working.timelineToBook)],
    ["How they heard", val(intake.working.howHeard) + (intake.working.referrer ? " (" + intake.working.referrer + ")" : "")]
  ];
  if (intake.vision.wowMoment) rows.push(["The ‘wow’ moment", intake.vision.wowMoment]);
  return (
    <details className="disclosure" style={{ borderTop: "1px solid var(--rule)", paddingTop: 8 }}>
      <summary>Full intake details</summary>
      <div className="kv">
        {rows.map(([k, v]) => [<span key={k + "k"}>{k}</span>, <span key={k + "v"}>{v}</span>])}
      </div>
    </details>
  );
}

/* ───────── Phase 11 · Step 2: what the agreement needs from each couple ───────── */

const isoFromPretty = v => {
  const s = String(v || "").trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const t = Date.parse(s);
  if (!Number.isFinite(t)) return "";
  const d = new Date(t);
  return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
};
const prettyFromIso = iso => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso || "")) return "";
  const [y, m, d] = iso.split("-").map(Number);
  return ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][m - 1] + " " + d + ", " + y;
};

function AgreementDetails({ client }) {
  const settings = useContext(SettingsContext);
  const gaps = agreementGaps(client, settings);
  const addons = (client.addonIds || []).map(id => (settings.addons || []).find(a => a.id === id)).filter(Boolean);
  return (
    <div className="stack" style={{ "--gap": "10px" }}>
      <div className="label">For the agreement</div>
      <div className="kv" style={{ fontSize: 15.5 }}>
        <span>Ceremony</span><span>{formatCeremonyTime(client.ceremonyTime) || "—"}</span>
        <span>Venue address</span><span>{client.venueAddress || "—"}</span>
        <span>Add-ons</span><span>{addons.length ? addons.map(a => a.name).join(", ") : "None"}</span>
        {client.highlineVenue && <><span>Venue</span><span>Run by Highline Adventures (Section 6 included)</span></>}
      </div>
      {gaps.length
        ? <div className="note note-warn" style={{ fontSize: 15 }}>Still needed for the agreement: {gaps.join(", ")}.</div>
        : <div className="field-hint">Everything the agreement needs is here.</div>}
    </div>
  );
}

function AgreementDetailsDialog({ client, onSave, onClose }) {
  const settings = useContext(SettingsContext);
  const p0 = (client.partners && client.partners[0]) || {}, p1 = (client.partners && client.partners[1]) || {};
  const [f, setF] = useState({
    packageId: client.packageId || "", addonIds: client.addonIds || [],
    date: isoFromPretty(client.date), ceremonyTime: /^\d{1,2}:\d{2}$/.test(client.ceremonyTime || "") ? client.ceremonyTime.padStart(5, "0") : "",
    ceremonyNote: /^\d{1,2}:\d{2}$/.test(client.ceremonyTime || "") ? "" : (client.ceremonyTime || ""),
    venue: client.venue || "", venueAddress: client.venueAddress || "", guests: client.guests || "",
    p0name: p0.name || "", p0email: p0.email || "", p0phone: p0.phone || "",
    p1name: p1.name || "", p1email: p1.email || "", p1phone: p1.phone || "",
    highlineVenue: !!client.highlineVenue
  });
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));
  const offered = addonsFor(settings, f.packageId);
  const toggleAddon = id => setF(prev => ({ ...prev, addonIds: prev.addonIds.includes(id) ? prev.addonIds.filter(x => x !== id) : [...prev.addonIds, id] }));
  const save = () => {
    const partners = (client.partners || [{}, {}]).slice();
    partners[0] = { ...(partners[0] || {}), name: f.p0name.trim(), email: f.p0email.trim(), phone: f.p0phone.trim() };
    partners[1] = { ...(partners[1] || {}), name: f.p1name.trim(), email: f.p1email.trim(), phone: f.p1phone.trim() };
    const first = n => n.split(" ")[0] || n;
    onSave({
      packageId: f.packageId || null,
      addonIds: f.addonIds.filter(id => offered.some(a => a.id === id)),
      date: prettyFromIso(f.date) || client.date,
      ceremonyTime: f.ceremonyTime || f.ceremonyNote.trim(),
      venue: f.venue.trim() || client.venue, venueAddress: f.venueAddress.trim(),
      guests: Number(f.guests) || 0,
      partners,
      names: (partners[0].name && partners[1].name) ? first(partners[0].name) + " & " + first(partners[1].name) : client.names,
      full: (partners[0].name && partners[1].name) ? partners[0].name + " & " + partners[1].name : client.full,
      highlineVenue: f.highlineVenue
    });
    onClose();
  };
  return (
    <Dialog title={"Agreement details · " + client.names} width={680} onClose={onClose}
      footer={<><button className="btn btn-secondary" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save}>Save details</button></>}>
      <section className="stack" style={{ "--gap": "14px" }}>
        <div className="label">Package</div>
        <Field label="Package booked">
          <select className="input" value={f.packageId} onChange={e => set("packageId", e.target.value)}>
            <option value="">Not chosen yet</option>
            {(settings.packages || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
        <Field label="Add-ons included">
          <div className="stack" style={{ "--gap": "4px" }}>
            {offered.map(a => (
              <label key={a.id} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 16, cursor: "pointer", minHeight: 44 }}>
                <input type="checkbox" checked={f.addonIds.includes(a.id)} onChange={() => toggleAddon(a.id)} />
                <span>{a.name} <span className="mono text-muted" style={{ fontSize: 13 }}>· {formatRate(a)}</span></span>
              </label>
            ))}
          </div>
        </Field>
      </section>
      <hr className="rule" />
      <section className="stack" style={{ "--gap": "14px" }}>
        <div className="label">Event details (Section 1)</div>
        <div className="grid-2">
          <Field label="Wedding date"><input className="input" type="date" value={f.date} onChange={e => set("date", e.target.value)} /></Field>
          <Field label="Ceremony start time"><input className="input" type="time" value={f.ceremonyTime} onChange={e => set("ceremonyTime", e.target.value)} /></Field>
          <Field label="Venue name"><input className="input" value={f.venue} onChange={e => set("venue", e.target.value)} /></Field>
          <Field label="Estimated guest count"><input className="input" inputMode="numeric" value={f.guests} onChange={e => set("guests", e.target.value)} /></Field>
        </div>
        <Field label="Venue address"><input className="input" value={f.venueAddress} onChange={e => set("venueAddress", e.target.value)} /></Field>
        {f.ceremonyNote && <div className="field-hint">The couple wrote &ldquo;{f.ceremonyNote}&rdquo; for the ceremony time. Pick an exact time above to replace it.</div>}
        <label style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 16, cursor: "pointer", minHeight: 44 }}>
          <input type="checkbox" checked={f.highlineVenue} onChange={e => set("highlineVenue", e.target.checked)} style={{ marginTop: 3 }} />
          The venue is run by Highline Adventures (HLADV LLC). The agreement then includes the venue relationship section.
        </label>
      </section>
      <hr className="rule" />
      <section className="stack" style={{ "--gap": "14px" }}>
        <div className="label">Who signs</div>
        <div className="grid-3">
          <Field label="Partner 1 name"><input className="input" value={f.p0name} onChange={e => set("p0name", e.target.value)} /></Field>
          <Field label="Partner 1 email"><input className="input" type="email" value={f.p0email} onChange={e => set("p0email", e.target.value)} /></Field>
          <Field label="Partner 1 phone"><input className="input" type="tel" value={f.p0phone} onChange={e => set("p0phone", e.target.value)} /></Field>
          <Field label="Partner 2 name"><input className="input" value={f.p1name} onChange={e => set("p1name", e.target.value)} /></Field>
          <Field label="Partner 2 email"><input className="input" type="email" value={f.p1email} onChange={e => set("p1email", e.target.value)} /></Field>
          <Field label="Partner 2 phone"><input className="input" type="tel" value={f.p1phone} onChange={e => set("p1phone", e.target.value)} /></Field>
        </div>
      </section>
    </Dialog>
  );
}

function ClientProfiles({ clients, clientId, onSetActive, onUpdateClient, onRemoveClient }) {
  const [editing, setEditing] = useState(null);
  const editClient = editing ? clients.find(c => c.id === editing) : null;
  return (
    <div className="stack" style={{ "--gap": "22px" }}>
      <p className="module-intro">Profiles fill in from each couple&rsquo;s intake questionnaire. No manual entry needed.</p>
      <div className="auto-grid" style={{ "--min": "420px" }}>
        {clients.map(cl => {
          const active = cl.id === clientId;
          return (
            <article key={cl.id} className={"panel" + (active ? " is-active" : "")} style={{ gap: 18 }}>
              <div className="panel-head" style={{ alignItems: "flex-start" }}>
                <div style={{ flex: "1 1 200px", minWidth: 0 }}>
                  <div className="label">{cl.date} · {cl.location.split(",")[0]}</div>
                  <h3 className="panel-title" style={{ fontSize: 32, marginTop: 6 }}>{cl.names}</h3>
                </div>
                <div className="row" style={{ "--gap": "6px" }}>
                  {active && <span className="tag tag-done">Active</span>}
                  <PaymentFlag client={cl} />
                  <UpdateFlag client={cl} />
                  <PackageTag client={cl} />
                </div>
              </div>
              <div className="grid-2" style={{ "--gap": "14px 22px" }}>
                <Meta label="Venue">{cl.venue} · {cl.location}</Meta>
                <Meta label="Guests · Budget">{cl.guests} guests · <span className="figure">{fmt(cl.budget)}</span></Meta>
              </div>
              <div className="grid-2" style={{ "--gap": "12px 22px" }}>
                {cl.partners.map(p => (
                  <div key={p.name} style={{ borderLeft: "2px solid var(--vellum)", paddingLeft: 12, fontSize: 15, minWidth: 0 }}>
                    <div style={{ fontSize: 17, fontWeight: 500 }}>{p.name}</div>
                    {p.email && <div className="text-muted" style={{ overflowWrap: "anywhere" }}>{p.email}</div>}
                    {p.phone && <div className="text-muted">{p.phone}</div>}
                  </div>
                ))}
              </div>
              <div className="label">Prefers {String(cl.preferred).toLowerCase()} · Engaged {cl.engaged}</div>
              {cl.story && <p className="display-italic" style={{ fontSize: 20, lineHeight: 1.35, margin: 0 }}>{cl.story}</p>}
              <div className="stack" style={{ "--gap": "10px" }}>
                <div className="label">Design preferences</div>
                <div className="row">
                  {cl.colors.map((sw, i) => <span key={i} className="swatch" style={{ background: sw }} title={sw}></span>)}
                  {cl.theme && <span style={{ fontSize: 16 }}>{cl.theme}</span>}
                </div>
                {cl.boards.length > 0 && (
                  <div className="row" style={{ "--gap": "6px 16px", fontSize: 15 }}>
                    {cl.boards.map((b, i) => <SafeLink key={i} href={b} />)}
                  </div>
                )}
                {(cl.must.length > 0 || cl.cant.length > 0) && (
                  <div className="row" style={{ "--gap": "6px" }}>
                    {cl.must.map((m, i) => <span key={i} className="tag tag-neutral">{m}</span>)}
                    {cl.cant.map((x, i) => <span key={i} className="tag tag-outline" style={{ textDecoration: "line-through" }} title="Dealbreaker">{x}</span>)}
                  </div>
                )}
              </div>
              <div className="note">
                <div className="kv">
                  <span>Dietary</span><span>{cl.dietary || "—"}</span>
                  <span>Accessibility</span><span>{cl.accessibility || "—"}</span>
                  <span>Family</span><span>{cl.family || "—"}</span>
                  <span>Special</span><span>{cl.requests || "—"}</span>
                </div>
              </div>
              <AgreementDetails client={cl} />
              {cl.intake && <IntakeSummary intake={cl.intake} />}
              <div className="row-end" style={{ "--gap": "10px" }}>
                {onUpdateClient && <button className="btn btn-secondary" onClick={() => setEditing(cl.id)}>Edit agreement details</button>}
                {onRemoveClient && <button className="btn btn-ghost" onClick={() => onRemoveClient(cl)} aria-label={"Remove couple: " + cl.names}>Remove couple</button>}
                <button className="btn btn-secondary" disabled={active} onClick={() => onSetActive(cl.id)}>{active ? "Active client" : "Set as active"}</button>
              </div>
            </article>
          );
        })}
      </div>
      {editClient && <AgreementDetailsDialog client={editClient} onSave={patch => onUpdateClient(editClient.id, patch)} onClose={() => setEditing(null)} />}
    </div>
  );
}

function Tasks({ client, tasks, onCycle, onAdd, onUpdate, onRemove }) {
  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", due: "", assignedTo: "", notes: "" });

  const templateKey = taskTemplateFor(client);
  const doneCount = tasks.filter(t => t.status === "complete").length;
  const pct = tasks.length ? Math.round(doneCount / tasks.length * 100) : 0;
  const remaining = TASK_TEMPLATES[templateKey].filter(t => !tasks.some(x => x.title === t.title));
  const statusLabel = { incomplete: "Incomplete", "in-progress": "In progress", complete: "Complete" };
  const nextStatus = { incomplete: "in-progress", "in-progress": "complete", complete: "incomplete" };

  const openAdd = () => { setEditId(null); setForm({ title: "", description: "", due: "", assignedTo: "", notes: "" }); setFormOpen(true); };
  const openEdit = t => { setEditId(t.id); setForm({ title: t.title, description: t.description, due: t.due, assignedTo: t.assignedTo, notes: t.notes }); setFormOpen(true); };
  const submitForm = () => {
    if (!form.title.trim()) return;
    if (editId) onUpdate(editId, form); else onAdd({ ...form, status: "incomplete", custom: true });
    setFormOpen(false);
  };
  const addFromTemplate = t => onAdd({ title: t.title, description: t.description, status: "incomplete", due: "", assignedTo: "", notes: "", custom: false });

  return (
    <div className="stack measure" style={{ "--gap": "22px", "--measure": "860px" }}>
      <div className="row" style={{ "--gap": "12px 18px" }}>
        <PackageTag client={client} />
        <span className="text-muted" style={{ fontSize: 15 }}>{doneCount} of {tasks.length} complete</span>
        <Meter value={pct} label="Tasks complete" className="task-meter" />
        <button className="btn btn-primary push" onClick={openAdd}>+ Add task</button>
      </div>

      <div className="panel flush">
        {tasks.map(t => (
          <div key={t.id} className="task-row">
            <button
              className={"task-check" + (t.status === "complete" ? " done" : t.status === "in-progress" ? " partial" : "")}
              onClick={() => onCycle(t.id)}
              aria-label={t.title + ": " + statusLabel[t.status] + ". Mark as " + statusLabel[nextStatus[t.status]]}
              title={"Mark as " + statusLabel[nextStatus[t.status]]}
            >
              {t.status === "complete" && <Icon path={<polyline points="20 6 9 17 4 12"></polyline>} size={18} />}
            </button>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="row" style={{ "--gap": "6px 10px" }}>
                <span style={{ fontSize: 18, fontWeight: 500, lineHeight: 1.3 }}>{t.title}</span>
                {t.custom && <span className="tag tag-outline">Custom</span>}
                <StatusTag status={statusLabel[t.status]} className="push" />
              </div>
              {t.description && <div className="text-muted" style={{ fontSize: 15, marginTop: 4 }}>{t.description}</div>}
              {(t.due || t.assignedTo) && (
                <div className="row label" style={{ "--gap": "4px 18px", marginTop: 8 }}>
                  {t.due && <span>Due {t.due}</span>}
                  {t.assignedTo && <span>Assigned to {t.assignedTo}</span>}
                </div>
              )}
              {t.notes && <div style={{ fontSize: 15, marginTop: 8, fontStyle: "italic", borderLeft: "2px solid var(--vellum)", paddingLeft: 12 }}>{t.notes}</div>}
            </div>
            <div className="icon-btn-row">
              <button className="btn btn-icon btn-ghost" onClick={() => openEdit(t)} aria-label={"Edit task: " + t.title}>
                <Icon path={<><path d="M12 20h9"></path><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"></path></>} size={16} />
              </button>
              <button className="btn btn-icon btn-ghost" onClick={() => onRemove(t.id)} aria-label={"Remove task: " + t.title}>
                <Icon path={<><path d="M3 6h18"></path><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"></path><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></>} size={16} />
              </button>
            </div>
          </div>
        ))}
        {tasks.length === 0 && <div className="empty" style={{ padding: "18px 22px" }}>No tasks yet. Add one, or pull from the {templateKey} template below.</div>}
      </div>

      {remaining.length > 0 && (
        <div className="stack" style={{ "--gap": "10px" }}>
          <div className="label">Remaining {templateKey} template tasks</div>
          <div className="row" style={{ "--gap": "8px" }}>
            {remaining.map((t, i) => <button key={i} className="chip chip-sm" onClick={() => addFromTemplate(t)}>+ {t.title}</button>)}
          </div>
        </div>
      )}

      {formOpen && (
        <Dialog title={editId ? "Edit task" : "Add task"} kicker={client.names} onClose={() => setFormOpen(false)}
          footer={<>
            <button className="btn btn-secondary" onClick={() => setFormOpen(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={submitForm} disabled={!form.title.trim()}>{editId ? "Save" : "Add task"}</button>
          </>}>
          <Field label="Title" required><input className="input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></Field>
          <Field label="Description"><textarea className="input" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}></textarea></Field>
          <div className="grid-2">
            <Field label="Due date"><input className="input" placeholder="e.g. Aug 15" value={form.due} onChange={e => setForm({ ...form, due: e.target.value })} /></Field>
            <Field label="Assigned to"><input className="input" value={form.assignedTo} onChange={e => setForm({ ...form, assignedTo: e.target.value })} /></Field>
          </div>
          <Field label="Notes"><textarea className="input" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}></textarea></Field>
        </Dialog>
      )}
    </div>
  );
}

function VendorManagement({
  vendors, clients, clientId, vendorCat, setVendorCat, vendorStatus, setVendorStatus, sortKey, sortDir, setSort, vendorId, setVendorId,
  vendorView, setVendorView, compareCategory, setCompareCategory, customCategories, onAddCustomCategory,
  comparisonVendorIds, onSwapIn, onRemoveFromComparison, onAddVendorToCategory
}) {
  const vs = useMemo(() => vendors.filter(v => v.client === clientId), [vendors, clientId]);
  const categories = useMemo(() => ["All", ...new Set(vs.map(v => v.category))], [vs]);
  const statuses = ["All", "Signed", "Pending", "Considering", "Rejected"];

  const filtered = vs.filter(v => (vendorCat === "All" || v.category === vendorCat) && (vendorStatus === "All" || v.contractStatus === vendorStatus));
  const sorted = [...filtered].sort((a, b) => {
    const va = sortKey === "quote" ? a.quote : String(a[sortKey]).toLowerCase();
    const vb = sortKey === "quote" ? b.quote : String(b[sortKey]).toLowerCase();
    return (va < vb ? -1 : va > vb ? 1 : 0) * sortDir;
  });
  const payStatusOf = v => {
    if (v.contractStatus === "Rejected" || v.contractStatus === "Considering") return "—";
    if (v.depositPaid && v.finalPaid) return "Paid";
    const overdue = (!v.depositPaid && Date.parse(v.depositDue) < NOW) || (!v.finalPaid && Date.parse(v.finalDue) < NOW);
    return overdue ? "Overdue" : "Pending";
  };
  const arrow = k => sortKey === k ? (sortDir === 1 ? "↑" : "↓") : "";
  const sv = vendors.find(v => v.id === vendorId);

  const [newCategoryInput, setNewCategoryInput] = useState("");
  const [addVendorOpen, setAddVendorOpen] = useState(false);
  const [addVendorForm, setAddVendorForm] = useState({ name: "", contact: "", phone: "", email: "", quote: "", services: "", notes: "" });

  const allCategories = [...CATEGORIES, ...customCategories];
  const categoryCandidates = vs.filter(v => v.category === compareCategory);
  const comparisonVendors = comparisonVendorIds.map(id => vs.find(v => v.id === id)).filter(Boolean);
  const swapCandidates = categoryCandidates.filter(v => !comparisonVendorIds.includes(v.id));

  const submitNewCategory = () => {
    const name = newCategoryInput.trim();
    if (name) onAddCustomCategory(name);
    setNewCategoryInput("");
  };
  const submitAddVendor = () => {
    if (!addVendorForm.name.trim()) return;
    onAddVendorToCategory(addVendorForm);
    setAddVendorForm({ name: "", contact: "", phone: "", email: "", quote: "", services: "", notes: "" });
    setAddVendorOpen(false);
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 16, borderBottom: "1px solid var(--rule)", marginBottom: 24 }}>
        <div className="tabs" role="tablist" aria-label="Vendor view" style={{ borderBottom: "none" }}>
          <button role="tab" className="tab" aria-selected={vendorView === "list"} onClick={() => setVendorView("list")}>List</button>
          <button role="tab" className="tab" aria-selected={vendorView === "compare"} onClick={() => setVendorView("compare")}>Compare by category</button>
        </div>
        {vendorView === "list" && <button className="btn-link" style={{ marginLeft: "auto" }} onClick={() => setVendorView("compare")} title="Add a vendor from the Compare view">+ Vendor</button>}
      </div>

      {vendorView === "list" && (
        <div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "14px 32px", marginBottom: 22, alignItems: "flex-end" }}>
            <div role="group" aria-labelledby="vf-cat" style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
              <span id="vf-cat" className="label" style={{ width: "100%", marginBottom: 2 }}>Category</span>
              {categories.map(cat => (
                <button key={cat} className={"chip chip-sm" + (vendorCat === cat ? " active" : "")} aria-pressed={vendorCat === cat} onClick={() => setVendorCat(cat)}>{cat}</button>
              ))}
            </div>
            <div role="group" aria-labelledby="vf-con" style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
              <span id="vf-con" className="label" style={{ width: "100%", marginBottom: 2 }}>Contract</span>
              {statuses.map(st => (
                <button key={st} className={"chip chip-sm" + (vendorStatus === st ? " active" : "")} aria-pressed={vendorStatus === st} onClick={() => setVendorStatus(st)}>{st}</button>
              ))}
            </div>
          </div>
          <div className="panel flush" style={{ marginBottom: 24 }}>
            <table className="table table-cards">
              <thead><tr>
                <th><button className="sortbtn" onClick={() => setSort("name")}>Vendor {arrow("name")}</button></th>
                <th><button className="sortbtn" onClick={() => setSort("category")}>Category {arrow("category")}</button></th>
                <th>Contract</th>
                <th style={{ textAlign: "right" }}><button className="sortbtn" onClick={() => setSort("quote")}>Quote {arrow("quote")}</button></th>
                <th>Payment</th>
                <th>Last contact</th>
              </tr></thead>
              <tbody>
                {sorted.map(v => {
                  const payStatus = payStatusOf(v);
                  return (
                    <tr key={v.id} onClick={() => setVendorId(v.id)} style={{ cursor: "pointer" }}>
                      <td>
                        <button className="sortbtn" style={{ fontFamily: "var(--font-body)", textTransform: "none", letterSpacing: 0, fontSize: 18, fontWeight: 500 }} onClick={e => { e.stopPropagation(); setVendorId(v.id); }}>{v.name}</button>
                        <div className="text-muted" style={{ fontSize: 15 }}>{v.contact}</div>
                      </td>
                      <td data-label="Category">{v.category}</td>
                      <td data-label="Contract"><StatusTag status={v.contractStatus} /></td>
                      <td data-label="Quote" className="figure" style={{ textAlign: "right" }}>{fmt(v.quote)}</td>
                      <td data-label="Payment"><StatusTag status={payStatus} /></td>
                      <td data-label="Last contact" className="figure text-muted" style={{ fontSize: 13.5 }}>{v.lastContact}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="dropzone">
            <Icon path={<><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></>} size={22} />
            <div style={{ flex: "1 1 240px" }}>
              <div style={{ fontSize: 17, fontWeight: 500 }}>Drop a contract, quote, or screenshot here</div>
              <div className="text-muted" style={{ fontSize: 15 }}>AI will pull out the vendor&rsquo;s details, pricing, and payment schedule for review.</div>
            </div>
            <span className="label">Coming soon</span>
          </div>
        </div>
      )}

      {vendorView === "compare" && (
        <div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 18, alignItems: "center" }}>
            {allCategories.map(cat => (
              <button key={cat} className={"chip chip-sm" + (compareCategory === cat ? " active" : "")} aria-pressed={compareCategory === cat} onClick={() => setCompareCategory(cat)}>{cat}</button>
            ))}
            <input
              className="input" aria-label="Add a category" style={{ width: 190, fontSize: 15 }} placeholder="+ Add category"
              value={newCategoryInput} onChange={e => setNewCategoryInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") submitNewCategory(); }}
            />
          </div>

          {comparisonVendors.length > 0 && (
            <div className="auto-grid" style={{ "--min": "250px", "--gap": "16px", marginBottom: 20 }}>
              {comparisonVendors.map(v => (
                <div key={v.id} className="panel" style={{ gap: 10 }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="label">{v.category}</div>
                      <button className="sortbtn display" style={{ fontSize: 26, textTransform: "none", letterSpacing: 0, marginTop: 4 }} onClick={() => setVendorId(v.id)}>{v.name}</button>
                    </div>
                    <button className="btn btn-icon btn-ghost" onClick={() => onRemoveFromComparison(v.id)} aria-label="Remove from comparison" title="Remove from comparison">
                      <Icon path={<><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></>} size={14} />
                    </button>
                  </div>
                  <div className="figure-strong" style={{ fontSize: 19 }}>{fmt(v.quote)}</div>
                  <div className="row" style={{ "--gap": "6px" }}>
                    {v.services.map((sx, i) => <span key={i} className="tag tag-neutral">{sx}</span>)}
                  </div>
                  <div style={{ fontSize: 15.5, minWidth: 0 }}>
                    <div>{v.contact}</div>
                    <div className="text-muted">{v.phone}</div>
                    <div className="text-muted" style={{ overflowWrap: "anywhere" }}>{v.email}</div>
                  </div>
                  {v.notes && <div style={{ fontSize: 15, borderLeft: "2px solid var(--vellum)", padding: "2px 12px", fontStyle: "italic" }}>{v.notes}</div>}
                </div>
              ))}
            </div>
          )}

          {categoryCandidates.length === 0 && (
            <div className="dropzone" style={{ marginBottom: 18 }}>
              <Icon path={<><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.35-4.35"></path></>} size={22} />
              <div style={{ flex: "1 1 240px" }}>
                <div style={{ fontSize: 17, fontWeight: 500 }}>No candidates yet for {compareCategory}</div>
                <div className="text-muted" style={{ fontSize: 15 }}>Search and suggest local {compareCategory.toLowerCase()} vendors near {clients.find(c => c.id === clientId)?.venue || "the venue"}.</div>
              </div>
              <span className="label">Coming soon</span>
            </div>
          )}

          <div className="row" style={{ "--gap": "8px" }}>
            {swapCandidates.length > 0 && (
              <>
                <span className="label" style={{ marginRight: 4 }}>Swap in</span>
                {swapCandidates.map(v => (
                  <button key={v.id} className="chip chip-sm" onClick={() => onSwapIn(v.id)}>+ {v.name} ({fmt(v.quote)})</button>
                ))}
              </>
            )}
            <button className="btn btn-primary" style={{ marginLeft: swapCandidates.length ? 0 : "auto" }} onClick={() => setAddVendorOpen(true)}>+ Add vendor to {compareCategory}</button>
          </div>

          {addVendorOpen && (
            <Dialog title={"Add vendor · " + compareCategory} onClose={() => setAddVendorOpen(false)}
              footer={<>
                <button className="btn btn-secondary" onClick={() => setAddVendorOpen(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={submitAddVendor} disabled={!addVendorForm.name.trim()}>Add vendor</button>
              </>}>
              <Field label="Vendor / business name" required><input className="input" value={addVendorForm.name} onChange={e => setAddVendorForm({ ...addVendorForm, name: e.target.value })} /></Field>
              <div className="grid-2">
                <Field label="Contact person"><input className="input" value={addVendorForm.contact} onChange={e => setAddVendorForm({ ...addVendorForm, contact: e.target.value })} /></Field>
                <Field label="Quote amount"><input className="input" inputMode="decimal" value={addVendorForm.quote} onChange={e => setAddVendorForm({ ...addVendorForm, quote: e.target.value })} /></Field>
                <Field label="Phone"><input className="input" type="tel" value={addVendorForm.phone} onChange={e => setAddVendorForm({ ...addVendorForm, phone: e.target.value })} /></Field>
                <Field label="Email"><input className="input" type="email" value={addVendorForm.email} onChange={e => setAddVendorForm({ ...addVendorForm, email: e.target.value })} /></Field>
              </div>
              <Field label="Services" hint="Comma separated"><input className="input" value={addVendorForm.services} onChange={e => setAddVendorForm({ ...addVendorForm, services: e.target.value })} /></Field>
              <Field label="Notes"><textarea className="input" value={addVendorForm.notes} onChange={e => setAddVendorForm({ ...addVendorForm, notes: e.target.value })}></textarea></Field>
            </Dialog>
          )}
        </div>
      )}

      {sv && (
        <Dialog title={sv.name} kicker={sv.category} width={680} onClose={() => setVendorId(null)}
          aside={<StatusTag status={sv.contractStatus} style={{ marginTop: 4 }} />}>
          <div className="grid-2" style={{ "--gap": "14px 22px" }}>
            <Meta label="Contact">{sv.contact}</Meta>
            <Meta label="Phone">{sv.phone}</Meta>
            <Meta label="Email">{sv.email}</Meta>
            <Meta label="Website"><SafeLink href={sv.website} /></Meta>
            <Meta label="Address" style={{ gridColumn: "1/-1" }}>{sv.address}</Meta>
          </div>
          <div className="note">
            <div className="kv"><span>Contract</span><span>{sv.range}</span><span>Key terms</span><span>{sv.keyTerms}</span></div>
          </div>
          <table className="table table-cards">
            <thead><tr><th>Payment</th><th>Amount</th><th>Due</th><th>Status</th></tr></thead>
            <tbody>
              <tr><td>Deposit</td><td data-label="Amount" className="figure">{fmt(sv.deposit)}</td><td data-label="Due" className="figure">{sv.depositDue}</td><td data-label="Status"><StatusTag status={sv.depositPaid ? "Paid" : "Pending"} /></td></tr>
              <tr><td>Final payment</td><td data-label="Amount" className="figure">{fmt(sv.final)}</td><td data-label="Due" className="figure">{sv.finalDue}</td><td data-label="Status"><StatusTag status={sv.finalPaid ? "Paid" : "Pending"} /></td></tr>
            </tbody>
          </table>
          <div>
            <div className="label" style={{ marginBottom: 8 }}>Services &amp; add-ons</div>
            <div className="row" style={{ "--gap": "6px" }}>
              {sv.services.map((sx, i) => <span key={i} className="tag tag-neutral">{sx}</span>)}
              {sv.addons.map((ax, i) => <span key={i} className="tag tag-outline">{ax}</span>)}
            </div>
          </div>
          <div style={{ fontSize: 16 }}><div className="label">Last contact · {sv.lastContact}</div>{sv.notes}</div>
        </Dialog>
      )}
    </div>
  );
}

function Timeline({ client, timelineView, setTimelineView }) {
  const msRows = milestones[client.id] || [];
  const dayRows = days[client.id] || [];
  return (
    <div className="stack" style={{ "--gap": "24px" }}>
      <div className="stack" style={{ "--gap": "10px" }}>
        <div className="tabs" role="tablist" aria-label="Timeline view">
          <button role="tab" className="tab" aria-selected={timelineView === "milestones"} onClick={() => setTimelineView("milestones")}>Planning milestones</button>
          <button role="tab" className="tab" aria-selected={timelineView === "day"} onClick={() => setTimelineView("day")}>Wedding day</button>
        </div>
        <div className="label">{client.full} · {client.date} · {client.venue}</div>
      </div>

      {timelineView === "milestones" && (
        <ol className="measure" style={{ "--measure": "720px", listStyle: "none", margin: 0, padding: 0 }}>
          {msRows.map((m, i) => (
            <li key={i} className="timeline-item">
              <div className="timeline-mark" aria-hidden="true">
                <span className={"dot" + (m.done ? " done" : "")}></span>
                {i < msRows.length - 1 && <span className="line"></span>}
              </div>
              <div style={{ paddingBottom: 26, minWidth: 0 }}>
                <div className="row" style={{ "--gap": "4px 14px" }}>
                  <span className="figure text-muted" style={{ fontSize: 13.5 }}>{m.date}</span>
                  <span style={{ fontSize: 18, fontWeight: 500 }}>{m.title}</span>
                  <StatusTag status={m.done ? "Done" : "Upcoming"} className="push" />
                </div>
                <div className="text-muted" style={{ fontSize: 15, marginTop: 4 }}>{m.desc}</div>
              </div>
            </li>
          ))}
        </ol>
      )}

      {timelineView === "day" && (
        <div className="split" style={{ "--a": "1.7fr", "--b": "1fr" }}>
          <div className="panel flush">
            <div className="panel-head"><div className="label">Run of show</div></div>
            <table className="table table-cards">
              <thead><tr><th style={{ width: 96 }}>Time</th><th>Event</th><th>Vendor</th><th>Zone</th></tr></thead>
              <tbody>
                {dayRows.map((d, i) => (
                  <tr key={i}>
                    <td className="figure-strong" style={{ fontSize: 14.5 }}>{d.time}</td>
                    <td data-label="Event" className="stacked"><div>{d.event}</div>{d.note && <div className="text-muted" style={{ fontSize: 14.5 }}>{d.note}</div>}</td>
                    <td data-label="Vendor" style={{ fontSize: 15 }}>{d.vendor}</td>
                    <td data-label="Zone"><span className="tag tag-neutral">{d.zone}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="stack" style={{ "--gap": "18px" }}>
            <div className="panel" style={{ gap: 6 }}>
              <div className="label">Zones &amp; vendor parking</div>
              {(zones[client.id] || []).map((z, i) => <div key={i} className="list-row" style={{ fontSize: 15.5 }}>{z}</div>)}
            </div>
            <div className="panel" style={{ gap: 6, background: "var(--vellum)", borderColor: "var(--vellum)" }}>
              <div className="label">Weather backup &amp; contingencies</div>
              {(contingencies[client.id] || []).map((cg, i) => (
                <div key={i} style={{ fontSize: 15.5, padding: "5px 0", display: "flex", gap: 10 }}><span aria-hidden="true">&mdash;</span><span>{cg}</span></div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Budget({ client, vendors }) {
  const vs = vendors.filter(v => v.client === client.id);
  const active = vs.filter(v => v.contractStatus !== "Rejected" && v.contractStatus !== "Considering");
  const exp = expenses[client.id] || [];
  const expSum = exp.reduce((a, e) => a + e.amt, 0);
  const committed = active.reduce((a, v) => a + v.quote, 0) + expSum;
  const pays = paymentsFor(vs);
  const paid = pays.filter(p => p.paid).reduce((a, p) => a + p.amount, 0) + expSum;
  const pct = Math.min(100, Math.round(committed / client.budget * 100));
  const over = committed > client.budget;
  const byCat = {};
  active.forEach(v => { byCat[v.category] = (byCat[v.category] || 0) + v.quote; });
  if (expSum) byCat["Non-vendor"] = expSum;
  const entries = Object.entries(byCat).sort((x, y) => y[1] - x[1]);
  const shades = inkScale(entries.length);
  const C = 2 * Math.PI * 40;
  const gap = entries.length > 1 ? 0.8 : 0;
  let acc = 0;
  const donutSegs = [], donutLegend = [];
  entries.forEach(([label, amt], i) => {
    const frac = committed ? amt / committed : 0;
    // Oxblood marks only the category that pushes the total over budget (the largest one, when over).
    const color = over && i === 0 ? "var(--oxblood)" : shades[i];
    donutSegs.push({ color, dash: Math.max(0, frac * C - gap).toFixed(2) + " " + C.toFixed(2), offset: (-acc * C).toFixed(2) });
    donutLegend.push({ label, color, amt: fmt(amt), pct: Math.round(frac * 100) + "%" });
    acc += frac;
  });
  const payRows = [...pays].sort((a, b) => a.ts - b.ts);
  const lineItems = active.map(v => {
    const vPaid = (v.depositPaid ? v.deposit : 0) + (v.finalPaid ? v.final : 0);
    return { name: v.name, totalFmt: fmt(v.quote), paidFmt: fmt(vPaid), balFmt: fmt(v.quote - vPaid) };
  });
  const alerts = payRows.filter(p => p.status !== "Paid").slice(0, 3);
  const Stat = ({ label, value, note }) => (
    <div className="panel stat" style={{ gap: 0, padding: "20px 22px" }}>
      <div className="label">{label}</div>
      <div className="figure-lg">{value}</div>
      <div className="text-muted">{note}</div>
    </div>
  );

  return (
    <div className="stack" style={{ "--gap": "24px" }}>
      <div className="grid-4" style={{ "--gap": "16px" }}>
        <Stat label="Total budget" value={fmt(client.budget)} note={"Contingency buffer " + fmt(client.budget * 0.05)} />
        <Stat label="Committed" value={fmt(committed)} note={pct + "% of budget"} />
        <Stat label="Paid to date" value={fmt(paid)} note="Deposits + settled invoices" />
        <Stat label="Remaining" value={fmt(client.budget - committed)} note="Unallocated funds" />
      </div>
      <Meter value={pct} label="Share of budget committed" />

      <div className="split" style={{ "--a": "1fr", "--b": "0.9fr" }}>
        <div className="panel">
          <div className="label">Committed by category</div>
          <div className="row" style={{ "--gap": "20px 28px", alignItems: "center" }}>
            <svg width="168" height="168" viewBox="0 0 100 100" style={{ transform: "rotate(-90deg)", flex: "none" }} role="img" aria-label="Committed spend by category">
              <circle cx="50" cy="50" r="40" fill="none" stroke="var(--vellum)" strokeWidth="14"></circle>
              {donutSegs.map((g, i) => <circle key={i} cx="50" cy="50" r="40" fill="none" stroke={g.color} strokeWidth="14" strokeDasharray={g.dash} strokeDashoffset={g.offset}></circle>)}
            </svg>
            <div className="stack" style={{ "--gap": "8px", flex: "1 1 200px", minWidth: 0 }}>
              {donutLegend.map((l, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 15.5 }}>
                  <span className="swatch sm" style={{ background: l.color, borderColor: "transparent" }}></span>
                  <span style={{ flex: 1, minWidth: 0 }}>{l.label}</span>
                  <span className="figure">{l.amt}</span>
                  <span className="figure text-muted" style={{ width: 42, textAlign: "right", fontSize: 13.5 }}>{l.pct}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="panel" style={{ gap: 0 }}>
          <div className="label" style={{ marginBottom: 6 }}>Coming due</div>
          {alerts.length === 0 && <div className="empty">Nothing outstanding.</div>}
          {alerts.map((al, i) => (
            <div key={i} className="list-row" style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 16.5, fontWeight: 500 }}>{al.label}</div>
                <div className="label" style={{ marginTop: 3 }}>Due {al.due} · {fmt(al.amount)} · Invoice {al.invoice}</div>
              </div>
              <StatusTag status={al.status} />
            </div>
          ))}
        </div>
      </div>

      <div className="split" style={{ "--a": "1.4fr", "--b": "1fr" }}>
        <div className="panel flush">
          <div className="panel-head"><div className="label">Payment tracker</div></div>
          <table className="table table-cards">
            <thead><tr><th>Payment</th><th>Due</th><th>Method</th><th>Invoice</th><th style={{ textAlign: "right" }}>Amount</th><th>Status</th></tr></thead>
            <tbody>
              {payRows.map((p, i) => (
                <tr key={i}>
                  <td style={{ fontWeight: 500 }}>{p.label}</td>
                  <td data-label="Due" className="figure" style={{ fontSize: 13.5 }}>{p.due}</td>
                  <td data-label="Method">{p.method}</td>
                  <td data-label="Invoice" className="figure text-muted" style={{ fontSize: 13.5 }}>{p.invoice}</td>
                  <td data-label="Amount" className="figure" style={{ textAlign: "right" }}>{fmt(p.amount)}</td>
                  {/* The overdue alert above is this screen's one oxblood mark; the tracker repeats it quietly. */}
                  <td data-label="Status"><span className={"tag " + (p.status === "Overdue" ? "tag-outline-ink" : statusClass(p.status))}>{p.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="stack" style={{ "--gap": "22px" }}>
          <div className="panel flush">
            <div className="panel-head"><div className="label">Per-vendor line items</div></div>
            <table className="table table-cards">
              <thead><tr><th>Vendor</th><th style={{ textAlign: "right" }}>Total</th><th style={{ textAlign: "right" }}>Paid</th><th style={{ textAlign: "right" }}>Balance</th></tr></thead>
              <tbody>
                {lineItems.map((li, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 500 }}>{li.name}</td>
                    <td data-label="Total" className="figure" style={{ textAlign: "right" }}>{li.totalFmt}</td>
                    <td data-label="Paid" className="figure" style={{ textAlign: "right" }}>{li.paidFmt}</td>
                    <td data-label="Balance" className="figure-strong" style={{ textAlign: "right" }}>{li.balFmt}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="panel" style={{ gap: 0 }}>
            <div className="label" style={{ marginBottom: 6 }}>Non-vendor expenses</div>
            {exp.map((ex, i) => (
              <div key={i} className="list-row list-split" style={{ fontSize: 15.5 }}><span>{ex.label}</span><span className="figure">{fmt(ex.amt)}</span></div>
            ))}
            <div className="list-split" style={{ paddingTop: 12, borderTop: "1px solid var(--ink)" }}><span className="label" style={{ color: "var(--ink)" }}>Total</span><span className="figure-strong">{fmt(expSum)}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Documents({ client, docs, vendors, docQuery, setDocQuery, onAdd, onUpdate, onRemove }) {
  const q = docQuery.trim().toLowerCase();
  const low = v => String(v || "").toLowerCase();
  const match = d => !q || low(d.name).includes(q) || low(d.category).includes(q) || low(d.terms).includes(q) || low(d.range).includes(q) || low(d.vendor).includes(q) || low(d.file && d.file.name).includes(q);
  const filtered = docs.filter(match);
  const expAlerts = docs.filter(d => d.expires).map(d => ({ label: d.vendor || d.name, text: d.expires }));

  const blankForm = { name: "", category: DOC_CATEGORIES[0], vendor: "", status: "Draft", range: "", amount: "", terms: "", cancel: "", insurance: "", file: null };
  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(blankForm);

  const openAdd = () => { setEditId(null); setForm(blankForm); setFormOpen(true); };
  const openEdit = d => { setEditId(d.id); setForm({ ...blankForm, ...d, vendor: d.vendor || "", amount: String(d.amount == null ? "" : d.amount) }); setFormOpen(true); };
  const removeDoc = d => {
    if (d.file && !window.confirm("Remove “" + d.name + "” and its attached file?")) return;
    if (d.file) { const st = getActiveStore(); if (st.deleteDocFile) st.deleteDocFile(d.file.path); }
    onRemove(d.id);
  };
  const submitForm = () => {
    if (!form.name.trim()) return;
    const payload = { ...form, vendor: form.vendor || null, amount: Number(form.amount) || 0 };
    if (editId) onUpdate(editId, payload); else onAdd(payload);
    setFormOpen(false);
  };

  return (
    <div className="stack" style={{ "--gap": "20px" }}>
      <div className="row" style={{ "--gap": "12px" }}>
        <div className="search">
          <Icon path={<><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.3-4.3"></path></>} size={16} />
          <input className="input" type="search" aria-label="Search documents by name, category, vendor, or keyword" placeholder="Search name, category, vendor, keyword…" value={docQuery} onChange={e => setDocQuery(e.target.value)} />
        </div>
        <button className="btn btn-primary push" onClick={openAdd}>+ Add document</button>
      </div>

      {expAlerts.length > 0 && (
        <div className="stack" style={{ "--gap": "8px" }}>
          {expAlerts.map((ea, i) => (
            <Note key={i} tone="warn"><span className="label" style={{ color: "var(--ink)", marginRight: 8 }}>Expiring</span><strong style={{ fontWeight: 500 }}>{ea.label}</strong> — {ea.text}</Note>
          ))}
        </div>
      )}

      <div className="panel flush">
        {filtered.map(d => (
          <div key={d.id} className="doc-row">
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="row" style={{ "--gap": "6px 10px" }}>
                <span style={{ fontSize: 18, fontWeight: 500, marginRight: 4 }}>{d.name}</span>
                <span className="tag tag-neutral">{d.category}</span>
                {d.vendor && <span className="tag tag-outline">{d.vendor}</span>}
                <StatusTag status={d.status} />
                <DocFileLink file={d.file} />
                {d.range && <span className="figure text-muted push" style={{ fontSize: 13.5 }}>{d.range}</span>}
              </div>
              <div className="text-muted" style={{ fontSize: 15, marginTop: 8, display: "flex", gap: "4px 18px", flexWrap: "wrap" }}>
                <span className="figure-strong" style={{ color: "var(--ink)" }}>{fmt(d.amount)}</span>
                {d.terms && <span>Terms: {d.terms}</span>}
                {d.cancel && <span>Cancellation: {d.cancel}</span>}
                {d.insurance && <span>Insurance: {d.insurance}</span>}
              </div>
            </div>
            <div className="icon-btn-row">
              <button className="btn btn-icon btn-ghost" onClick={() => openEdit(d)} aria-label={"Edit document: " + d.name}>
                <Icon path={<><path d="M12 20h9"></path><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"></path></>} size={16} />
              </button>
              <button className="btn btn-icon btn-ghost" onClick={() => removeDoc(d)} aria-label={"Remove document: " + d.name}>
                <Icon path={<><path d="M3 6h18"></path><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"></path><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></>} size={16} />
              </button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="empty" style={{ padding: "18px 22px" }}>No documents match. Try a different search, or add one.</div>}
      </div>

      {formOpen && (
        <Dialog title={editId ? "Edit document" : "Add document"} kicker={client.names} onClose={() => setFormOpen(false)}
          footer={<>
            <button className="btn btn-secondary" onClick={() => setFormOpen(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={submitForm} disabled={!form.name.trim()}>{editId ? "Save" : "Add document"}</button>
          </>}>
          <Field label="Document name" required><input className="input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></Field>
          <div className="grid-2">
            <Field label="Category">
              <select className="input" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                {DOC_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Vendor">
              <select className="input" value={form.vendor} onChange={e => setForm({ ...form, vendor: e.target.value })}>
                <option value="">No vendor</option>
                {vendors.map(v => <option key={v.id} value={v.name}>{v.name}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select className="input" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                {DOC_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Amount"><input className="input" inputMode="decimal" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} /></Field>
          </div>
          <Field label="Date range" hint="e.g. Mar 2 – Oct 10, 2026"><input className="input" value={form.range} onChange={e => setForm({ ...form, range: e.target.value })} /></Field>
          <Field label="Terms"><input className="input" value={form.terms} onChange={e => setForm({ ...form, terms: e.target.value })} /></Field>
          <Field label="Cancellation policy"><input className="input" value={form.cancel} onChange={e => setForm({ ...form, cancel: e.target.value })} /></Field>
          <Field label="Insurance note"><input className="input" value={form.insurance} onChange={e => setForm({ ...form, insurance: e.target.value })} /></Field>
          <DocFileField clientId={client.id} value={form.file} onChange={file => setForm(f => ({ ...f, file, name: f.name || (file ? file.name.replace(/\.[^.]+$/, "") : "") }))} />
        </Dialog>
      )}
    </div>
  );
}

function Portfolio({ portFilter, setPortFilter }) {
  const styles = ["All", ...new Set(portfolio.map(w => w.style))];
  const items = portfolio.filter(w => portFilter === "All" || w.style === portFilter);
  return (
    <div className="stack" style={{ "--gap": "22px" }}>
      <div className="row" role="group" aria-label="Filter by style" style={{ "--gap": "6px" }}>
        {styles.map(st => <button key={st} className={"chip chip-sm" + (portFilter === st ? " active" : "")} aria-pressed={portFilter === st} onClick={() => setPortFilter(st)}>{st}</button>)}
      </div>
      <div className="masonry">
        {items.map((w, i) => (
          <article key={i} className="panel" style={{ breakInside: "avoid", marginBottom: 20, padding: 0, gap: 0 }}>
            <div style={{ height: w.h, overflow: "hidden" }}><PhotoPlaceholder label={w.couple} height={w.h} /></div>
            <div className="stack" style={{ "--gap": "10px", padding: "18px 20px 16px" }}>
              <div className="row" style={{ "--gap": "6px 10px", alignItems: "baseline" }}>
                <h3 className="panel-title" style={{ fontSize: 26 }}>{w.couple}</h3>
                <span className="tag tag-neutral push">{w.style}</span>
              </div>
              <div className="label">{w.date} · {w.venue}</div>
              <div className="row" style={{ "--gap": "6px" }}>
                {w.colors.map((sw, j) => <span key={j} className="swatch sm" style={{ background: sw }}></span>)}
              </div>
              <div style={{ fontSize: 15.5 }}>{w.elements}</div>
              <div className="grid-2" style={{ "--gap": "10px" }}>
                <figure style={{ margin: 0 }}>
                  <div style={{ height: 84, overflow: "hidden" }}><PhotoPlaceholder label="Inspiration" height={84} /></div>
                  <figcaption className="label" style={{ marginTop: 5, textAlign: "center" }}>Inspiration</figcaption>
                </figure>
                <figure style={{ margin: 0 }}>
                  <div style={{ height: 84, overflow: "hidden" }}><PhotoPlaceholder label="Execution" height={84} /></div>
                  <figcaption className="label" style={{ marginTop: 5, textAlign: "center" }}>Execution</figcaption>
                </figure>
              </div>
              {w.quote && <blockquote className="display-italic" style={{ margin: 0, borderLeft: "2px solid var(--ink)", paddingLeft: 14, fontSize: 19, lineHeight: 1.35 }}>&ldquo;{w.quote}&rdquo;</blockquote>}
              <div className="row" style={{ "--gap": "4px 14px", fontSize: 14.5 }}>
                {w.credits.map((cr, j) => <a key={j} href="#" onClick={e => e.preventDefault()}>{cr}</a>)}
                <span className="icon-btn-row push">
                  <button className="btn btn-icon btn-ghost" aria-label={"Share " + w.couple + " to Instagram"}><Icon path={<><rect x="2" y="2" width="20" height="20" rx="6"></rect><circle cx="12" cy="12" r="4"></circle><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></>} size={15} /></button>
                  <button className="btn btn-icon btn-ghost" aria-label={"Share " + w.couple}><Icon path={<><circle cx="18" cy="5" r="3"></circle><circle cx="6" cy="12" r="3"></circle><circle cx="18" cy="19" r="3"></circle><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line></>} size={15} /></button>
                  <button className="btn btn-icon btn-ghost" aria-label={"Copy link to " + w.couple}><Icon path={<><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path></>} size={15} /></button>
                </span>
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

// The week the report covers: Monday to Sunday of the current week, e.g. "Oct 19 – 25, 2026".
function reportWeek(today = new Date()) {
  const d = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const mon = new Date(d); mon.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
  const m = x => x.toLocaleString("en-US", { month: "short" });
  return m(mon) + " " + mon.getDate() + " – " + (m(sun) !== m(mon) ? m(sun) + " " : "") + sun.getDate() + ", " + sun.getFullYear();
}

// The weekly update as a plain-text email Amanda reviews, copies and sends herself (the app never sends it).
function buildWeeklyEmail(client, settings, note, r, week) {
  const biz = (settings && settings.businessName) || "GhostWriter Events";
  const planner = (settings && settings.plannerName) || "";
  const to = (client.partners || []).map(p => p.email).filter(Boolean).join(", ");
  const L = ["Hi " + client.names + ",", ""];
  if (note && note.trim()) { L.push(note.trim()); L.push(""); }
  const section = (title, lines) => { if (!lines.length) return; L.push(title.toUpperCase()); lines.forEach(x => L.push("  • " + x)); L.push(""); };
  section("Vendors I was in touch with", r.contacts.map(c => c.name + " (" + c.when.replace(/^Last contact /, "") + "): " + c.note));
  if (r.payments.length) section("Payments made", [...r.payments.map(p => p.label + ": " + fmt(p.amount)), "Total: " + r.payTotal]);
  section("Decisions made", r.decisions);
  section("Done this week", r.tasks);
  section("Coming up", r.deadlines.map(d => d.date + " · " + d.label));
  L.push("Reply here with any questions.");
  L.push("");
  L.push("Warmly,");
  L.push(planner ? planner + " · " + biz : biz);
  return { to, subject: "Your weekly planning update · " + week, body: L.join("\n") };
}

function WeeklyReport({ client, vendors, settings, reportNote, setReportNote, reportSent, setReportSent, weekly }) {
  const vs = vendors.filter(v => v.client === client.id);
  const pays = paymentsFor(vs);
  const repContacts = vs.filter(v => v.recent).map(v => ({ name: v.name, when: "Last contact " + v.lastContact, note: (v.notes || "").split(".")[0] + "." }));
  const repPayments = pays.filter(p => p.paid);
  const repPayTotal = fmt(repPayments.reduce((a, p) => a + p.amount, 0));
  const msRows = milestones[client.id] || [];
  const repTasks = msRows.filter(m => m.done).map(m => m.title + " (" + m.date + ")");
  const repDecisions = decisions[client.id] || [];
  const repDeadlines = [
    ...msRows.filter(m => !m.done).slice(0, 3).map(m => ({ date: m.date, label: m.title })),
    ...pays.filter(p => p.status !== "Paid").sort((a, b) => a.ts - b.ts).slice(0, 3).map(p => ({ date: p.due.replace(", 2026", ""), label: p.label + " — " + fmt(p.amount) }))
  ];
  const week = reportWeek();
  const email = buildWeeklyEmail(client, settings, reportNote, { contacts: repContacts, payments: repPayments, payTotal: repPayTotal, decisions: repDecisions, tasks: repTasks, deadlines: repDeadlines }, week);
  const mailto = "mailto:" + email.to.split(",").map(x => encodeURIComponent(x.trim()).replace(/%40/g, "@")).join(",") + "?subject=" + encodeURIComponent(email.subject) + "&body=" + encodeURIComponent(email.body);
  const [copyNote, setCopyNote] = useState("");
  const copy = async () => {
    try { if (navigator.clipboard && navigator.clipboard.writeText) { await navigator.clipboard.writeText(email.body); setCopyNote("Copied. Paste it into a new email to " + (email.to || "the couple") + "."); return; } } catch (e) { /* fall through */ }
    setCopyNote("Select the text below and copy it (⌘/Ctrl-C).");
  };
  const Check = ({ children }) => <div className="list-row" style={{ fontSize: 15.5, display: "flex", gap: 10 }}><span aria-hidden="true">&#10003;</span><span>{children}</span></div>;
  const Empty = () => <div className="text-muted" style={{ fontSize: 15, paddingTop: 6 }}>Nothing this week. Left out of the email.</div>;

  return (
    <div className="stack measure" style={{ "--gap": "22px", "--measure": "1000px" }}>
      <section className="panel" style={{ gap: 12 }}>
        <div className="label">Week of {week}</div>
        <h3 className="panel-title" style={{ fontSize: 30 }}>Weekly update for {client.names}</h3>
        {weekly && <Note tone={weekly.sent ? "ok" : undefined}>{weekly.sent
          ? "This week's update was sent " + new Date(weekly.sent).toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" }) + (weekly.by ? " by " + weekly.by : "") + "."
          : "Their agreement promises a written update every week. This week's update hasn't gone out yet."}</Note>}
        <p className="text-muted" style={{ fontSize: 15.5 }}>Prepared from vendors, payments, timeline and budget. Read it over, edit the note, then copy it into your own email and send it. The app doesn&rsquo;t send it for you.</p>
        <Field label="Your note to the couple">
          <textarea className="input" style={{ minHeight: 110 }} value={reportNote} onChange={e => setReportNote(e.target.value)}></textarea>
        </Field>
      </section>

      <div className="auto-grid" style={{ "--min": "290px", "--gap": "18px" }}>
        <div className="panel" style={{ gap: 0 }}>
          <div className="label" style={{ marginBottom: 4 }}>Vendors contacted</div>
          {repContacts.length ? repContacts.map((rc, i) => (
            <div key={i} className="list-row">
              <div style={{ fontSize: 16.5, fontWeight: 500 }}>{rc.name}</div>
              <div className="text-muted" style={{ fontSize: 15 }}>{rc.when} · {rc.note}</div>
            </div>
          )) : <Empty />}
        </div>
        <div className="panel" style={{ gap: 0 }}>
          <div className="label" style={{ marginBottom: 4 }}>Payments processed</div>
          {repPayments.length ? <>
            {repPayments.map((rp, i) => (
              <div key={i} className="list-row list-split" style={{ fontSize: 15.5 }}><span>{rp.label}</span><span className="figure">{fmt(rp.amount)}</span></div>
            ))}
            <div className="list-split" style={{ paddingTop: 12, borderTop: "1px solid var(--ink)" }}><span className="label" style={{ color: "var(--ink)" }}>Total processed</span><span className="figure-strong">{repPayTotal}</span></div>
          </> : <Empty />}
        </div>
        <div className="panel" style={{ gap: 0 }}>
          <div className="label" style={{ marginBottom: 4 }}>Decisions made</div>
          {repDecisions.length ? repDecisions.map((rd, i) => <Check key={i}>{rd}</Check>) : <Empty />}
        </div>
        <div className="panel" style={{ gap: 0 }}>
          <div className="label" style={{ marginBottom: 4 }}>Tasks completed</div>
          {repTasks.length ? repTasks.map((rt, i) => <Check key={i}>{rt}</Check>) : <Empty />}
        </div>
        <div className="panel" style={{ gridColumn: "1/-1", background: "var(--vellum)", borderColor: "var(--vellum)" }}>
          <div className="label">Upcoming deadlines</div>
          {repDeadlines.length ? <div className="auto-grid" style={{ "--min": "250px", "--gap": "4px 28px" }}>
            {repDeadlines.map((dl, i) => (
              <div key={i} style={{ display: "flex", gap: 14, fontSize: 15.5, padding: "5px 0" }}><span className="figure-strong" style={{ width: 64, flex: "none", fontSize: 13.5, paddingTop: 2 }}>{dl.date}</span><span>{dl.label}</span></div>
            ))}
          </div> : <Empty />}
        </div>
      </div>

      <section className="panel" style={{ gap: 12 }} aria-labelledby="weekly-email-h">
        <div className="label" id="weekly-email-h">Ready to send</div>
        <div className="kv">
          <div>To</div><div style={{ overflowWrap: "anywhere" }}>{email.to || <span className="text-muted">No email on file for this couple. Add one on the Clients screen.</span>}</div>
          <div>Subject</div><div>{email.subject}</div>
        </div>
        <textarea className="input" readOnly aria-label="Weekly update email" style={{ minHeight: 260, fontSize: 15.5 }} value={email.body}></textarea>
        <div className="row" style={{ "--gap": "10px 14px" }}>
          <button className="btn btn-accent" onClick={copy}>Copy email</button>
          <a className="btn btn-secondary" href={mailto}>Open in my email app</a>
          {!reportSent
            ? <button className="btn-link" onClick={() => setReportSent(new Date().toISOString())}>I&rsquo;ve sent it</button>
            : <span className="tag tag-done" role="status">Marked sent {new Date(reportSent === true ? Date.now() : reportSent).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>}
        </div>
        {copyNote && <div className="text-muted" role="status" style={{ fontSize: 15 }}>{copyNote}</div>}
        <div className="field-hint">To change the wording, edit your note above. You can also edit the email in your own inbox before you send it. &ldquo;Open in my email app&rdquo; starts a new email with this text filled in; if your email app cuts it short, paste the copied version instead.</div>
      </section>
    </div>
  );
}

/* ───────────────────────── couple-facing portal ───────────────────────── */

function CoupleProfile({ client }) {
  const cl = client;
  return (
    <article className="panel measure" style={{ "--measure": "780px", gap: 20, padding: "30px 32px" }}>
      <div className="panel-head" style={{ alignItems: "flex-start" }}>
        <div style={{ flex: "1 1 220px", minWidth: 0 }}>
          <div className="label">{cl.date} · {cl.location.split(",")[0]}</div>
          <h2 className="panel-title" style={{ fontSize: 34, marginTop: 6 }}>{cl.names}</h2>
        </div>
        <PackageTag client={cl} />
      </div>
      <div className="grid-2" style={{ "--gap": "14px 22px" }}>
        <Meta label="Venue">{cl.venue} · {cl.location}{cl.venueAddress ? <><br />{cl.venueAddress}</> : null}</Meta>
        <Meta label="Guests">{cl.guests} guests</Meta>
        {cl.ceremonyTime && <Meta label="Ceremony">{formatCeremonyTime(cl.ceremonyTime)}</Meta>}
      </div>
      <div className="grid-2" style={{ "--gap": "12px 22px" }}>
        {cl.partners.map(p => (
          <div key={p.name} style={{ borderLeft: "2px solid var(--vellum)", paddingLeft: 12, fontSize: 15.5, minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 500 }}>{p.name}</div>
            {p.email && <div className="text-muted" style={{ overflowWrap: "anywhere" }}>{p.email}</div>}
            {p.phone && <div className="text-muted">{p.phone}</div>}
          </div>
        ))}
      </div>
      {cl.engaged && cl.engaged !== "—" && <div className="label">Engaged {cl.engaged}</div>}
      {cl.story && <p className="display-italic" style={{ fontSize: 21, lineHeight: 1.35, margin: 0 }}>{cl.story}</p>}
      <div className="stack" style={{ "--gap": "10px" }}>
        <div className="label">Design preferences</div>
        <div className="row">
          {cl.colors.map((sw, i) => <span key={i} className="swatch" style={{ background: sw }} title={sw}></span>)}
          {cl.theme && <span style={{ fontSize: 16 }}>{cl.theme}</span>}
        </div>
        {cl.boards.length > 0 && (
          <div className="row" style={{ "--gap": "6px 16px", fontSize: 15.5 }}>
            {cl.boards.map((b, i) => <SafeLink key={i} href={b} />)}
          </div>
        )}
        {(cl.must.length > 0 || cl.cant.length > 0) && (
          <div className="row" style={{ "--gap": "6px" }}>
            {cl.must.map((m, i) => <span key={i} className="tag tag-neutral">{m}</span>)}
            {cl.cant.map((x, i) => <span key={i} className="tag tag-outline" style={{ textDecoration: "line-through" }} title="Not for us">{x}</span>)}
          </div>
        )}
      </div>
    </article>
  );
}

function CoupleBudgetSummary({ client, vendors }) {
  const vs = vendors.filter(v => v.client === client.id);
  const active = vs.filter(v => v.contractStatus !== "Rejected" && v.contractStatus !== "Considering");
  const exp = expenses[client.id] || [];
  const expSum = exp.reduce((a, e) => a + e.amt, 0);
  const committed = active.reduce((a, v) => a + v.quote, 0) + expSum;
  const pays = paymentsFor(vs);
  const paid = pays.filter(p => p.paid).reduce((a, p) => a + p.amount, 0) + expSum;
  const pct = client.budget ? Math.min(100, Math.round(committed / client.budget * 100)) : 0;
  const byCat = {};
  active.forEach(v => { byCat[v.category] = (byCat[v.category] || 0) + v.quote; });
  if (expSum) byCat["Non-vendor"] = expSum;
  const C = 2 * Math.PI * 40;
  let acc = 0;
  const donutSegs = [], donutLegend = [];
  const cats = Object.entries(byCat).sort((x, y) => y[1] - x[1]);
  const shades = inkScale(cats.length);
  cats.forEach(([label, amt], i) => {
    const frac = committed ? amt / committed : 0;
    donutSegs.push({ color: shades[i], dash: Math.max(0, frac * C - (cats.length > 1 ? 0.8 : 0)).toFixed(2) + " " + C.toFixed(2), offset: (-acc * C).toFixed(2) });
    donutLegend.push({ label, color: shades[i], amt: fmt(amt), pct: Math.round(frac * 100) + "%" });
    acc += frac;
  });
  const Stat = ({ label, value, note }) => (
    <div className="panel stat" style={{ gap: 0, padding: "20px 22px" }}>
      <div className="label">{label}</div>
      <div className="figure-lg">{value}</div>
      {note && <div className="text-muted">{note}</div>}
    </div>
  );

  return (
    <div className="stack" style={{ "--gap": "22px" }}>
      <div className="grid-4" style={{ "--gap": "16px" }}>
        <Stat label="Total budget" value={fmt(client.budget)} />
        <Stat label="Committed" value={fmt(committed)} note={pct + "% of budget"} />
        <Stat label="Paid to date" value={fmt(paid)} />
        <Stat label="Remaining" value={fmt(client.budget - committed)} />
      </div>
      <Meter value={pct} label="Share of budget committed" />
      {cats.length > 0 && (
        <div className="panel measure" style={{ "--measure": "620px" }}>
          <div className="label">Committed by category</div>
          <div className="row" style={{ "--gap": "20px 28px" }}>
            <svg width="168" height="168" viewBox="0 0 100 100" style={{ transform: "rotate(-90deg)", flex: "none" }} role="img" aria-label="Committed spend by category">
              <circle cx="50" cy="50" r="40" fill="none" stroke="var(--vellum)" strokeWidth="14"></circle>
              {donutSegs.map((g, i) => <circle key={i} cx="50" cy="50" r="40" fill="none" stroke={g.color} strokeWidth="14" strokeDasharray={g.dash} strokeDashoffset={g.offset}></circle>)}
            </svg>
            <div className="stack" style={{ "--gap": "8px", flex: "1 1 200px", minWidth: 0 }}>
              {donutLegend.map((l, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 15.5 }}>
                  <span className="swatch sm" style={{ background: l.color, borderColor: "transparent" }}></span>
                  <span style={{ flex: 1, minWidth: 0 }}>{l.label}</span>
                  <span className="figure">{l.amt}</span>
                  <span className="figure text-muted" style={{ width: 42, textAlign: "right", fontSize: 13.5 }}>{l.pct}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CoupleVendors({ client, vendors }) {
  const booked = vendors.filter(v => v.client === client.id && v.contractStatus !== "Rejected" && v.contractStatus !== "Considering");
  if (booked.length === 0) return <div className="empty">No vendors booked yet.</div>;
  return (
    <div className="auto-grid" style={{ "--min": "260px", "--gap": "16px" }}>
      {booked.map(v => (
        <div key={v.id} className="panel" style={{ gap: 10 }}>
          <div className="label">{v.category}</div>
          <h3 className="panel-title" style={{ fontSize: 24 }}>{v.name}</h3>
          <div className="row" style={{ "--gap": "6px" }}>
            {v.services.map((sx, i) => <span key={i} className="tag tag-neutral">{sx}</span>)}
          </div>
          <div style={{ fontSize: 15.5, minWidth: 0 }}>
            <div>{v.contact}</div>
            {v.phone && <div className="text-muted">{v.phone}</div>}
            {v.email && <div className="text-muted" style={{ overflowWrap: "anywhere" }}>{v.email}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

const COLOR_PRESETS = [
  { name: "Terracotta", hex: "#c67139" }, { name: "Sage", hex: "#7a8a5e" }, { name: "Ivory", hex: "#f5ead8" },
  { name: "Espresso", hex: "#8c491a" }, { name: "Soft sage", hex: "#ccdbb2" }, { name: "Taupe", hex: "#a19786" },
  { name: "Clay", hex: "#d67f48" }, { name: "Charcoal", hex: "#474238" }
];

/* ─────────── Intake 2.0 — 7-step wizard field set & options ─────────── */

const INTAKE_STEPS = [
  { key: "you", label: "You two", headline: "Who I’m writing for.", dek: "Start with the two of you: names, and how to reach you." },
  { key: "basics", label: "The basics", headline: "When and where.", dek: "Rough answers are fine. We’ll sharpen them together." },
  { key: "budget", label: "Budget", headline: "The budget, stated plainly.", dek: "Where the money goes first, and where it can give." },
  { key: "venue", label: "Venue & vendors", headline: "The setting and the cast.", dek: "What’s already booked, and who you still need." },
  { key: "vision", label: "Your vision", headline: "The day you’re picturing.", dek: "Tell me what it should feel like." },
  { key: "details", label: "The details", headline: "The fine print.", dek: "Traditions, family, food: everything that makes it yours." },
  { key: "working", label: "Working together", headline: "How we’ll work together.", dek: "A few last notes, then it’s in my hands." }
];

const BUDGET_BANDS = ["Under $25k", "$25k–50k", "$50k–100k", "$100k–200k", "$200k+"];
const PLANNING_BUDGET_BANDS = ["Under $3k", "$3k–6k", "$6k–10k", "$10k–20k", "$20k+", "Not sure yet"];
const PRIORITY_OPTIONS = ["Food & drink", "Photography", "Music & entertainment", "Florals & decor", "Venue", "Attire & beauty", "Guest experience", "Videography", "Overall design"];
const TASK_OFFLOAD = ["Vendor sourcing", "Budget management", "Timeline & logistics", "Design & styling", "Guest management / RSVPs", "Day-of coordination", "Contract review", "Travel & lodging"];
const VIBE_CHIPS = ["Romantic", "Modern", "Rustic", "Elegant", "Boho", "Minimalist", "Vintage", "Garden", "Glam", "Coastal", "Industrial", "Whimsical"];
const EXTRA_EVENTS = ["Mehndi", "Sangeet", "Haldi", "Tea ceremony", "Baraat", "Nikah", "Welcome dinner", "Rehearsal dinner", "Day-after brunch"];
// Phase 11: the service-level chips are Amanda's packages from Settings, plus "Help me choose".
const HELP_ME_CHOOSE = "Help me choose";
const VENUE_TYPES = ["Indoor / traditional venue", "Outdoor (tent/rentals may be needed)", "Private home or estate", "Barn / farm", "Beach / waterfront", "Restaurant / winery", "Not sure yet"];
const HEARD_OPTIONS = ["Referral from a friend / family", "Past client", "Instagram", "Google search", "A vendor recommended you", "The Knot / WeddingWire", "Other"];

const INTAKE_DEFAULTS = {
  // Step 1 — You two
  partner1Name: "", partner1Pronouns: "", partner1Email: "", partner1Phone: "",
  partner2Name: "", partner2Pronouns: "", partner2Email: "", partner2Phone: "",
  homeCity: "", preferred: "Email",
  // Step 2 — The basics
  dateStatus: "", date: "", dateFlex: "", dowPref: "", location: "",
  destination: "", destinationDetails: "", guests: "", guestCertainty: "", partySize: "",
  // Step 3 — Budget
  budgetBand: "", budgetFirm: "", planningBudget: "", priorities: [], willingToTrim: "", contributors: "", budgetExact: "",
  // Step 4 — Venue & vendors
  venueStatus: "", venueName: "", venueAddress: "", ceremonyTime: "", sameSite: "", venueType: "", venueOutdoorNeeds: "",
  vendorsBooked: [], vendorsNeedHelp: [], officiantType: "",
  // Step 5 — Your vision
  tier: "", packageId: "", howFarAlong: "", designServices: "", tasksToOffload: [], vibe: [], formality: "",
  story: "", boards: "", colors: [], theme: "", must: "", cant: "", wowMoment: "",
  // Step 6 — The details
  traditions: "", fusion: "", extraEvents: [], religiousReq: "", family: "", languages: "",
  dietary: "", accessibility: "", children: "", mealStyle: "", multiDay: "",
  additionalEvents: "", transportLodging: "", weatherRisk: "", siteNotes: "",
  // Step 7 — Working together
  meetingCadence: "", involvement: "", otherDeciders: "", workedWithPlanner: "",
  timelineToBook: "", howHeard: "", referrer: "", requests: "", consent: false
};


// Labels its control for screen readers: a native input/select/textarea gets a real <label for>,
// anything else (chip groups, swatches) becomes a named group.
const NATIVE_CONTROLS = ["input", "select", "textarea"];
function Field({ label, children, required, hint }) {
  const id = useId();
  const hintId = hint ? id + "-hint" : undefined;
  const kids = Children.toArray(children);
  const native = kids.length === 1 && isValidElement(kids[0]) && NATIVE_CONTROLS.includes(kids[0].type);
  const control = native ? cloneElement(kids[0], { id: kids[0].props.id || id + "-control", "aria-label": undefined, "aria-describedby": hintId, "aria-required": required || undefined }) : null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
      {native
        ? <label htmlFor={control.props.id} className="field-label">{label}{required && <span aria-hidden="true" className="req"> *</span>}{required && <span className="sr-only"> (required)</span>}</label>
        : <div id={id + "-label"} className="field-label">{label}{required && <span aria-hidden="true" className="req"> *</span>}{required && <span className="sr-only"> (required)</span>}</div>}
      {hint && <div id={hintId} className="field-hint" style={{ marginTop: -4 }}>{hint}</div>}
      {native ? control : <div role="group" aria-labelledby={id + "-label"} aria-describedby={hintId}>{children}</div>}
    </div>
  );
}

// Single-choice chip group.
function ChipChoice({ options, value, onSelect }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
      {options.map(o => (
        <button key={o} type="button" aria-pressed={value === o} className={"chip" + (value === o ? " active" : "")} onClick={() => onSelect(o)}>{o}</button>
      ))}
    </div>
  );
}

// Multi-choice chip group with optional max cap.
function ChipMulti({ options, value, onToggle, max }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
      {options.map(o => {
        const on = value.includes(o);
        const capped = !on && max && value.length >= max;
        return (
          <button key={o} type="button" aria-pressed={on} aria-disabled={capped || undefined} className={"chip" + (on ? " active" : "")}
            style={{ opacity: capped ? 0.4 : 1, cursor: capped ? "not-allowed" : "pointer" }}
            onClick={() => { if (!capped) onToggle(o); }}>
            {on ? "✓ " : ""}{o}
          </button>
        );
      })}
    </div>
  );
}

const INTAKE_DRAFT_KEY = "intakeDraft";

// variant "planner" (default): opened from the dashboard, saves to this browser.
// variant "public": a couple's own link — draft is saved to the server through onSaveDraft.
function IntakeQuestionnaire({ onSubmit, onDone, variant = "planner", initialDraft, onSaveDraft, onStepDone, studioName, notice, packages }) {
  const pkgChoices = packageChoices(packages ? { packages } : null);
  const isPublic = variant === "public";
  const draft = isPublic ? (initialDraft || null) : loadPersisted(INTAKE_DRAFT_KEY, null);
  const [form, setForm] = useState(() => (draft && draft.form) ? { ...INTAKE_DEFAULTS, ...draft.form } : INTAKE_DEFAULTS);
  const [step, setStep] = useState(() => (draft && typeof draft.step === "number") ? Math.min(draft.step, INTAKE_STEPS.length - 1) : 0);
  const [resumed, setResumed] = useState(!!(draft && draft.form && Object.keys(draft.form).length > 1));
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(null);
  const [busy, setBusy] = useState(false);
  const brand = studioName || "GhostWriter Events";

  // Auto-save each change so a couple can close the tab and come back.
  useEffect(() => {
    if (submitted) return;
    if (isPublic) { if (onSaveDraft) { const t = setTimeout(() => onSaveDraft(form, step), 800); return () => clearTimeout(t); } return; }
    savePersisted(INTAKE_DRAFT_KEY, { form, step });
  }, [form, step, submitted]);

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));
  const toggle = (key, val, max) => setForm(prev => {
    const arr = prev[key] || [];
    if (arr.includes(val)) return { ...prev, [key]: arr.filter(x => x !== val) };
    if (max && arr.length >= max) return prev;
    return { ...prev, [key]: [...arr, val] };
  });
  const toggleColor = hex => toggle("colors", hex, 4);

  const stepKey = INTAKE_STEPS[step].key;
  const missing = INTAKE_REQUIRED[stepKey].filter(k => !intakeFilled(form[k]));

  const next = async () => {
    if (missing.length) { setError("A few required answers are still missing (marked *)."); return; }
    setError("");
    if (onStepDone) {
      setBusy(true);
      try { await onStepDone(step, form); } catch (e) { setError(e.message || "I couldn't save your progress. Try again in a moment."); setBusy(false); return; }
      setBusy(false);
    }
    setStep(s => Math.min(s + 1, INTAKE_STEPS.length - 1));
    window.scrollTo(0, 0);
  };
  const back = () => { setError(""); setStep(s => Math.max(s - 1, 0)); window.scrollTo(0, 0); };
  const goStep = i => { if (i <= step) { setError(""); setStep(i); window.scrollTo(0, 0); } };

  const submit = async () => {
    if (missing.length) { setError("A few required answers are still missing (marked *)."); return; }
    setError("");
    setBusy(true);
    try {
      const newClient = await onSubmit(form);
      if (!isPublic) removePersisted(INTAKE_DRAFT_KEY);
      setSubmitted(newClient || { names: [form.partner1Name, form.partner2Name].map(n => String(n || "").split(" ")[0]).join(" & ") });
    } catch (e) {
      setError(e.message || "That didn't go through. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  const startOver = () => {
    if (!isPublic) removePersisted(INTAKE_DRAFT_KEY);
    setForm(INTAKE_DEFAULTS); setStep(0); setResumed(false); setError("");
  };

  if (submitted) {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "var(--paper)", color: "var(--ink)", padding: 28 }}>
        <div className="card" style={{ padding: 40, maxWidth: 480, textAlign: "center", gap: 16, alignItems: "center" }}>
          <img src="/brand/wax-seal.svg" alt="" width="72" height="72" style={{ width: 72, height: 72 }} />
          <div className="display" style={{ fontSize: 32 }}>Thank you, {submitted.names}. Chapter one is done.</div>
          <p style={{ margin: 0, fontSize: 17, lineHeight: 1.6 }}>
            {isPublic ? "I have your answers. I'll read through everything and be in touch soon about next steps." : "Saved to their planning profile. You'll find them on the Clients screen."}
          </p>
          {!isPublic && <button className="btn btn-primary" onClick={onDone}>Back to planner dashboard</button>}
        </div>
      </div>
    );
  }

  const pct = Math.round(((step + 1) / INTAKE_STEPS.length) * 100);
  const WORDS = ["one", "two", "three", "four", "five", "six", "seven", "eight", "nine"];
  const chapter = INTAKE_STEPS[step];

  return (
    <div style={{ minHeight: "100vh", background: "var(--paper)", color: "var(--ink)" }}>
      <header className="intake-top">
        {!isPublic && <button className="btn btn-secondary" onClick={onDone}>&larr; Back to planner dashboard</button>}
        <img className="logo" src="/brand/manuscript-light.svg" alt={brand} width="134" height="49" />
        <h1 className="label" style={{ margin: "0 0 0 auto", textAlign: "right" }}>{isPublic ? "Let's plan your wedding" : "Client Intake"}</h1>
      </header>

      <div className="intake-body" style={{ maxWidth: 780, margin: "0 auto", padding: "28px 28px 72px", display: "flex", flexDirection: "column", gap: 22 }}>
        {isPublic && (
          <input aria-hidden="true" tabIndex={-1} autoComplete="off" name="website" value={form.website || ""} onChange={e => set("website", e.target.value)}
            style={{ position: "absolute", left: -10000, width: 1, height: 1, opacity: 0 }} />
        )}
        {notice}
        {/* Chapter index + thin ink progress rule */}
        <nav aria-label="Intake chapters">
          <div className="chapter-index">
            {INTAKE_STEPS.map((s, i) => (
              <button key={s.key} type="button" onClick={() => goStep(i)} disabled={i > step}
                className={i < step ? "done" : undefined}
                aria-current={i === step ? "step" : undefined}
                title={i <= step ? "Go to " + s.label : "Complete earlier steps first"}
                aria-label={String(i + 1).padStart(2, "0") + " " + s.label + ", chapter " + (i + 1) + " of " + INTAKE_STEPS.length}>
                {String(i + 1).padStart(2, "0")}<span className="cname"> {s.label}</span>
              </button>
            ))}
          </div>
          <div className="progress-rule" aria-hidden="true"><span style={{ width: pct + "%" }} /></div>
        </nav>

        {resumed && step === 0 && (
          <div style={{ border: "1px solid var(--rule)", background: "var(--vellum)", padding: "12px 16px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <span style={{ fontSize: 16 }}>Welcome back. I kept your place from last time.</span>
            <button className="btn-link" onClick={startOver} style={{ marginLeft: "auto" }}>Start over</button>
          </div>
        )}

        <section className="intake-sheet" aria-labelledby="chapter-headline">
          <div>
            <div className="label">Chapter {step + 1} of {INTAKE_STEPS.length} · {chapter.label}</div>
            <h2 id="chapter-headline" className="intake-headline">{chapter.headline}</h2>
            <p className="intake-dek">{chapter.dek}</p>
          </div>
          <hr className="rule" />
          {stepKey === "you" && (<>
            <div className="grid-2" style={{ "--gap": "24px 18px" }}>
              <Field label="Partner 1 — full name" required><input className="input" value={form.partner1Name} onChange={e => set("partner1Name", e.target.value)} /></Field>
              <Field label="Partner 2 — full name" required><input className="input" value={form.partner2Name} onChange={e => set("partner2Name", e.target.value)} /></Field>
              <Field label="Partner 1 — pronouns"><input className="input" aria-label="she/her, he/him, they/them" placeholder="she/her, he/him, they/them…" value={form.partner1Pronouns} onChange={e => set("partner1Pronouns", e.target.value)} /></Field>
              <Field label="Partner 2 — pronouns"><input className="input" aria-label="she/her, he/him, they/them" placeholder="she/her, he/him, they/them…" value={form.partner2Pronouns} onChange={e => set("partner2Pronouns", e.target.value)} /></Field>
              <Field label="Partner 1 — email" required><input className="input" value={form.partner1Email} onChange={e => set("partner1Email", e.target.value)} /></Field>
              <Field label="Partner 2 — email"><input className="input" value={form.partner2Email} onChange={e => set("partner2Email", e.target.value)} /></Field>
              <Field label="Partner 1 — phone" required><input className="input" value={form.partner1Phone} onChange={e => set("partner1Phone", e.target.value)} /></Field>
              <Field label="Partner 2 — phone"><input className="input" value={form.partner2Phone} onChange={e => set("partner2Phone", e.target.value)} /></Field>
            </div>
            <Field label="Home city" required><input className="input" aria-label="City, State" placeholder="City, State" value={form.homeCity} onChange={e => set("homeCity", e.target.value)} /></Field>
            <Field label="Best way to reach you">
              <ChipChoice options={["Email", "Text", "Phone call"]} value={form.preferred} onSelect={v => set("preferred", v)} />
            </Field>
          </>)}

          {stepKey === "basics" && (<>
            <Field label="Do you have a date?" required>
              <ChipChoice options={["We have a set date", "We have a range in mind", "Flexible / no date yet"]} value={form.dateStatus} onSelect={v => set("dateStatus", v)} />
            </Field>
            {(form.dateStatus === "We have a set date" || form.dateStatus === "We have a range in mind") && (
              <div className="grid-2" style={{ "--gap": "24px 18px" }}>
                <Field label={form.dateStatus === "We have a range in mind" ? "Target / earliest date" : "Wedding date"}><input type="date" className="input" value={form.date} onChange={e => set("date", e.target.value)} /></Field>
                <Field label="How flexible is it?"><input className="input" aria-label="e.g. within Sept–Oct, weekends only" placeholder="e.g. within Sept–Oct, weekends only" value={form.dateFlex} onChange={e => set("dateFlex", e.target.value)} /></Field>
              </div>
            )}
            <Field label="Day-of-week preference"><ChipChoice options={["Saturday", "Friday", "Sunday", "Weekday", "No preference"]} value={form.dowPref} onSelect={v => set("dowPref", v)} /></Field>
            <Field label="Location / region" required hint="City, state, or general area you're considering"><input className="input" value={form.location} onChange={e => set("location", e.target.value)} /></Field>
            <Field label="Is this a destination wedding?" required><ChipChoice options={["Yes", "No", "Maybe"]} value={form.destination} onSelect={v => set("destination", v)} /></Field>
            {(form.destination === "Yes" || form.destination === "Maybe") && (
              <Field label="Destination details" hint="Where, and any travel or logistics I should know about">
                <textarea className="input" style={{ minHeight: 60 }} value={form.destinationDetails} onChange={e => set("destinationDetails", e.target.value)}></textarea>
              </Field>
            )}
            <div className="grid-3" style={{ "--gap": "24px 18px" }}>
              <Field label="Estimated guest count" required><input type="number" className="input" value={form.guests} onChange={e => set("guests", e.target.value)} /></Field>
              <Field label="How certain is that?"><ChipChoice options={["Firm", "Rough estimate"]} value={form.guestCertainty} onSelect={v => set("guestCertainty", v)} /></Field>
              <Field label="Wedding party size"><input type="number" className="input" value={form.partySize} onChange={e => set("partySize", e.target.value)} /></Field>
            </div>
          </>)}

          {stepKey === "budget" && (<>
            <Field label="Total budget band" required><ChipChoice options={BUDGET_BANDS} value={form.budgetBand} onSelect={v => set("budgetBand", v)} /></Field>
            <div className="grid-2" style={{ "--gap": "24px 18px" }}>
              <Field label="Is that budget firm or flexible?" required><ChipChoice options={["Firm", "Somewhat flexible", "Flexible"]} value={form.budgetFirm} onSelect={v => set("budgetFirm", v)} /></Field>
              <Field label="Exact total (optional)" hint="If you have a specific number"><input type="number" className="input" value={form.budgetExact} onChange={e => set("budgetExact", e.target.value)} /></Field>
            </div>
            <Field label="Planning-services budget"><ChipChoice options={PLANNING_BUDGET_BANDS} value={form.planningBudget} onSelect={v => set("planningBudget", v)} /></Field>
            <Field label="Top 3 priorities" required hint="Where the budget should go first — pick up to 3">
              <ChipMulti options={PRIORITY_OPTIONS} value={form.priorities} onToggle={v => toggle("priorities", v, 3)} max={3} />
            </Field>
            <Field label="Where are you willing to trim, if needed?"><input className="input" value={form.willingToTrim} onChange={e => set("willingToTrim", e.target.value)} /></Field>
            <Field label="Who's contributing to the budget?"><input className="input" aria-label="e.g. the two of us, both families" placeholder="e.g. the two of us, both families…" value={form.contributors} onChange={e => set("contributors", e.target.value)} /></Field>
          </>)}

          {stepKey === "venue" && (<>
            <Field label="Where are you with a venue?" required><ChipChoice options={["Booked", "Shortlisting", "Need help finding one"]} value={form.venueStatus} onSelect={v => set("venueStatus", v)} /></Field>
            {form.venueStatus === "Booked" && (
              <div className="grid-2" style={{ "--gap": "24px 18px" }}>
                <Field label="Venue name"><input className="input" value={form.venueName} onChange={e => set("venueName", e.target.value)} /></Field>
                <Field label="Venue address"><input className="input" autoComplete="street-address" value={form.venueAddress} onChange={e => set("venueAddress", e.target.value)} /></Field>
                <Field label="Ceremony start time" hint="If you know it"><input className="input" type="time" value={form.ceremonyTime} onChange={e => set("ceremonyTime", e.target.value)} /></Field>
                <Field label="Same site for ceremony & reception?"><ChipChoice options={["Yes", "No", "Not sure"]} value={form.sameSite} onSelect={v => set("sameSite", v)} /></Field>
              </div>
            )}
            <Field label="Venue type" required><ChipChoice options={VENUE_TYPES} value={form.venueType} onSelect={v => set("venueType", v)} /></Field>
            {form.venueType && form.venueType.indexOf("Outdoor") === 0 && (
              <Field label="Outdoor logistics" hint="Tent, rentals, power, restrooms, rain plan — anything you know">
                <textarea className="input" style={{ minHeight: 56 }} value={form.venueOutdoorNeeds} onChange={e => set("venueOutdoorNeeds", e.target.value)}></textarea>
              </Field>
            )}
            <Field label="Vendors already booked"><ChipMulti options={CATEGORIES} value={form.vendorsBooked} onToggle={v => toggle("vendorsBooked", v)} /></Field>
            <Field label="Vendors you need help with"><ChipMulti options={CATEGORIES} value={form.vendorsNeedHelp} onToggle={v => toggle("vendorsNeedHelp", v)} /></Field>
            <Field label="Officiant / ceremony type"><input className="input" aria-label="e.g. religious, civil, friend officiating" placeholder="e.g. religious, civil, friend officiating…" value={form.officiantType} onChange={e => set("officiantType", e.target.value)} /></Field>
          </>)}

          {stepKey === "vision" && (<>
            <Field label="Which package fits?" required hint="Not sure yet? Choose Help me choose and I'll recommend one.">
              <ChipChoice options={pkgChoices.map(p => p.name).concat(HELP_ME_CHOOSE)} value={form.tier}
                onSelect={v => { const p = pkgChoices.find(x => x.name === v); setForm(prev => ({ ...prev, tier: v, packageId: p ? p.id : "" })); }} />
            </Field>
            <div className="grid-2" style={{ "--gap": "24px 18px" }}>
              <Field label="How far along is your planning?" required><ChipChoice options={["Just starting", "Some pieces booked", "Well underway", "Almost there"]} value={form.howFarAlong} onSelect={v => set("howFarAlong", v)} /></Field>
              <Field label="Want design/styling services?"><ChipChoice options={["Yes", "No", "Maybe"]} value={form.designServices} onSelect={v => set("designServices", v)} /></Field>
            </div>
            <Field label="What would you love to take off your plate?"><ChipMulti options={TASK_OFFLOAD} value={form.tasksToOffload} onToggle={v => toggle("tasksToOffload", v)} /></Field>
            <Field label="Vibe" hint="Pick whatever resonates"><ChipMulti options={VIBE_CHIPS} value={form.vibe} onToggle={v => toggle("vibe", v)} /></Field>
            <Field label="Formality"><ChipChoice options={["Casual", "Semi-formal", "Formal", "Black tie"]} value={form.formality} onSelect={v => set("formality", v)} /></Field>
            <Field label="Tell me about the day you're picturing"><textarea className="input" style={{ minHeight: 80 }} value={form.story} onChange={e => set("story", e.target.value)}></textarea></Field>
            <Field label="Color palette — pick up to 4">
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {COLOR_PRESETS.map(c => (
                  <button key={c.hex} type="button" className={"chip" + (form.colors.includes(c.hex) ? " active" : "")} onClick={() => toggleColor(c.hex)} aria-pressed={form.colors.includes(c.hex)}>
                    <span className="swatch sm" style={{ background: c.hex, borderColor: "var(--rule-strong)" }}></span>
                    {c.name}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Aesthetic or theme"><input className="input" value={form.theme} onChange={e => set("theme", e.target.value)} /></Field>
            <Field label="Inspiration links (one per line)"><textarea className="input" style={{ minHeight: 54 }} value={form.boards} onChange={e => set("boards", e.target.value)}></textarea></Field>
            <div className="grid-2" style={{ "--gap": "24px 18px" }}>
              <Field label="Must-haves" required hint="Comma separated"><input className="input" value={form.must} onChange={e => set("must", e.target.value)} /></Field>
              <Field label="Dealbreakers" hint="Comma separated"><input className="input" value={form.cant} onChange={e => set("cant", e.target.value)} /></Field>
            </div>
            <Field label="One moment you'd like people to remember"><input className="input" value={form.wowMoment} onChange={e => set("wowMoment", e.target.value)} /></Field>
          </>)}

          {stepKey === "details" && (<>
            <Field label="Cultural or religious traditions to honor"><textarea className="input" style={{ minHeight: 50 }} value={form.traditions} onChange={e => set("traditions", e.target.value)}></textarea></Field>
            <Field label="Is this a fusion / multicultural wedding?"><ChipChoice options={["Yes", "No"]} value={form.fusion} onSelect={v => set("fusion", v)} /></Field>
            {form.fusion === "Yes" && (
              <Field label="Additional ceremonies / events"><ChipMulti options={EXTRA_EVENTS} value={form.extraEvents} onToggle={v => toggle("extraEvents", v)} /></Field>
            )}
            <Field label="Religious requirements"><input className="input" value={form.religiousReq} onChange={e => set("religiousReq", e.target.value)} /></Field>
            <Field label="Family dynamics or sensitive situations"><textarea className="input" style={{ minHeight: 50 }} value={form.family} onChange={e => set("family", e.target.value)}></textarea></Field>
            <div className="grid-2" style={{ "--gap": "24px 18px" }}>
              <Field label="Languages spoken"><input className="input" value={form.languages} onChange={e => set("languages", e.target.value)} /></Field>
              <Field label="Children at the wedding?"><ChipChoice options={["Yes", "No", "Immediate family only"]} value={form.children} onSelect={v => set("children", v)} /></Field>
            </div>
            <Field label="Dietary needs"><textarea className="input" style={{ minHeight: 46 }} value={form.dietary} onChange={e => set("dietary", e.target.value)}></textarea></Field>
            <Field label="Accessibility needs"><textarea className="input" style={{ minHeight: 46 }} value={form.accessibility} onChange={e => set("accessibility", e.target.value)}></textarea></Field>
            <div className="grid-2" style={{ "--gap": "24px 18px" }}>
              <Field label="Meal style"><ChipChoice options={["Plated", "Family-style", "Buffet", "Stations", "Not sure"]} value={form.mealStyle} onSelect={v => set("mealStyle", v)} /></Field>
              <Field label="Single or multi-day celebration?" required><ChipChoice options={["Single day", "Multi-day"]} value={form.multiDay} onSelect={v => set("multiDay", v)} /></Field>
            </div>
            <Field label="Additional events (welcome party, brunch…)"><input className="input" value={form.additionalEvents} onChange={e => set("additionalEvents", e.target.value)} /></Field>
            <Field label="Transportation / lodging needs"><input className="input" value={form.transportLodging} onChange={e => set("transportLodging", e.target.value)} /></Field>
            <div className="grid-2" style={{ "--gap": "24px 18px" }}>
              <Field label="Outdoor weather risk / rain plan"><input className="input" value={form.weatherRisk} onChange={e => set("weatherRisk", e.target.value)} /></Field>
              <Field label="Unusual site notes"><input className="input" value={form.siteNotes} onChange={e => set("siteNotes", e.target.value)} /></Field>
            </div>
          </>)}

          {stepKey === "working" && (<>
            <div className="grid-2" style={{ "--gap": "24px 18px" }}>
              <Field label="Preferred meeting cadence"><ChipChoice options={["Weekly", "Biweekly", "Monthly", "As needed"]} value={form.meetingCadence} onSelect={v => set("meetingCadence", v)} /></Field>
              <Field label="How involved do you want to be?"><ChipChoice options={["Hands-on", "Balanced", "Hands-off"]} value={form.involvement} onSelect={v => set("involvement", v)} /></Field>
            </div>
            <Field label="Anyone else who'll be part of the decisions?"><input className="input" value={form.otherDeciders} onChange={e => set("otherDeciders", e.target.value)} /></Field>
            <div className="grid-2" style={{ "--gap": "24px 18px" }}>
              <Field label="Worked with a planner before?"><ChipChoice options={["Yes", "No"]} value={form.workedWithPlanner} onSelect={v => set("workedWithPlanner", v)} /></Field>
              <Field label="Timeline to book a planner"><ChipChoice options={["ASAP", "1–3 months", "3–6 months", "Just exploring"]} value={form.timelineToBook} onSelect={v => set("timelineToBook", v)} /></Field>
            </div>
            <Field label="How did you hear about me?" required><ChipChoice options={HEARD_OPTIONS} value={form.howHeard} onSelect={v => set("howHeard", v)} /></Field>
            {(form.howHeard === "Referral from a friend / family" || form.howHeard === "A vendor recommended you" || form.howHeard === "Other") && (
              <Field label="Who sent you my way? I'd like to thank them"><input className="input" value={form.referrer} onChange={e => set("referrer", e.target.value)} /></Field>
            )}
            <Field label="Anything else I should know?"><textarea className="input" style={{ minHeight: 60 }} value={form.requests} onChange={e => set("requests", e.target.value)}></textarea></Field>
            <label style={{ display: "flex", alignItems: "flex-start", gap: 12, fontSize: 16, lineHeight: 1.5, cursor: "pointer" }}>
              <input type="checkbox" checked={form.consent} onChange={e => set("consent", e.target.checked)} style={{ marginTop: 3 }} />
              <span>I agree to {brand} storing these answers to prepare my planning profile and a proposal (see the <a href="/privacy" target="_blank" rel="noopener">privacy policy</a>). <span className="req" aria-hidden="true">*</span></span>
            </label>
          </>)}

          {error && <div role="alert" className="error-text">{error}</div>}

          <hr className="rule" />
          <div className="intake-actions">
            <button className="btn btn-secondary" style={{ visibility: step === 0 ? "hidden" : "visible" }} onClick={back}>&larr; Back</button>
            <span style={{ flex: 1 }} />
            <span className="label desk-only">Saved as you go</span>
            {step < INTAKE_STEPS.length - 1
              ? <button className="btn btn-accent" onClick={next} disabled={busy}>{busy ? "Saving…" : <>Continue &rarr;</>}</button>
              : <button className="btn btn-accent" onClick={submit} disabled={busy}>{busy ? "Sending…" : "Send it in"}</button>}
          </div>
        </section>
      </div>
    </div>
  );
}

function CouplePortal({ client, vendors, portalSection, setPortalSection, onExit, extraSections = [], studioName }) {
  const [timelineView, setTimelineView] = useState("milestones");
  const [portFilter, setPortFilter] = useState("All");
  const sections = [
    ...extraSections.filter(s => s.first),
    { key: "profile", label: "Wedding details" },
    { key: "timeline", label: "Timeline" },
    { key: "budget", label: "Budget" },
    { key: "vendors", label: "Our vendors" },
    { key: "gallery", label: "Gallery" },
    ...extraSections.filter(s => !s.first)
  ];
  const extra = extraSections.find(s => s.key === portalSection);
  const brand = studioName || "GhostWriter Events";

  return (
    <div className="portal">
      <header className="portal-top">
        {onExit && <button className="btn btn-secondary" onClick={onExit}>&larr; Back to planner dashboard</button>}
        <img className="logo" src="/brand/manuscript-light.svg" alt={brand} width="128" height="47" />
        <span className="label push">Your planning portal</span>
      </header>

      <div className="portal-body">
        <section className="portal-hero" aria-labelledby="portal-title">
          <img src="/brand/wax-seal.svg" alt="" width="72" height="72" className="portal-seal" />
          <div style={{ minWidth: 0 }}>
            <div className="label">{[client.date, client.venue].filter(Boolean).join(" · ")}</div>
            <h1 id="portal-title" className="portal-title">{client.full}&rsquo;s Wedding</h1>
            <p className="display-italic portal-dek">Planned with {brand}. Everything I&rsquo;m writing for your day, in one place.</p>
          </div>
        </section>

        <nav className="portal-nav" aria-label="Portal sections">
          <div className="tabs" role="tablist">
            {sections.map(s => (
              <button key={s.key} role="tab" className="tab" aria-selected={portalSection === s.key} onClick={() => setPortalSection(s.key)}>
                {s.label}{s.badge ? <span className="tab-dot" aria-label=" (needs your attention)">•</span> : null}
              </button>
            ))}
          </div>
        </nav>

        <div className="portal-section">
          {extra && extra.render()}
          {portalSection === "profile" && <CoupleProfile client={client} />}
          {portalSection === "timeline" && <Timeline client={client} timelineView={timelineView} setTimelineView={setTimelineView} />}
          {portalSection === "budget" && <CoupleBudgetSummary client={client} vendors={vendors} />}
          {portalSection === "vendors" && <CoupleVendors client={client} vendors={vendors} />}
          {portalSection === "gallery" && <Portfolio portFilter={portFilter} setPortFilter={setPortFilter} />}
        </div>
      </div>

      <footer className="portal-foot">
        <span className="label">ghostwriter-events.com</span>
        <a href="/privacy">Privacy</a>
      </footer>
    </div>
  );
}

/* ───────────────────────── app shell ───────────────────────── */

function App() {
  const store = useStore();
  const cloud = store.mode === "cloud" ? store : null;
  const canManage = !cloud || cloud.isOwner;
  const portalLinks = usePortalLinks();
  const saveStatus = useSaveStatus();
  const [module, setModule] = useState("clients");
  const [selectedId, setClientId] = useState("c1");
  const [linksOpen, setLinksOpen] = useState(false);
  const [intakeShareOpen, setIntakeShareOpen] = useState(false);
  const [vendorCat, setVendorCat] = useState("All");
  const [vendorStatus, setVendorStatus] = useState("All");
  const [vendorId, setVendorId] = useState(null);
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState(1);
  const [timelineView, setTimelineView] = useState("milestones");
  const [docQuery, setDocQuery] = useState("");
  const [portFilter, setPortFilter] = useState("All");
  const [reportNote, setReportNote] = useState("This week I locked in vendor contracts and confirmed your day-of flow. Everything below is on schedule; two payment dates are coming up, flagged under deadlines.");
  const [tasksByClient, setTasksByClient] = usePersistentState("tasksByClient", initialTasks);
  const [docsByClient, setDocsByClient] = usePersistentState("docsByClient", initialDocs);
  // One-time cleanup: normalize any old plural doc categories saved on this browser.
  useEffect(() => {
    setDocsByClient(prev => {
      let changed = false;
      const next = {};
      Object.entries(prev).forEach(([cid, list]) => {
        next[cid] = (list || []).map(d => {
          if (DOC_CATEGORY_ALIASES[d.category]) { changed = true; return { ...d, category: DOC_CATEGORY_ALIASES[d.category] }; }
          return d;
        });
      });
      return changed ? next : prev;
    });
  }, []);
  const [vendorsState, setVendorsState] = usePersistentState("vendors", initialVendors);
  const [vendorLibrary, setVendorLibrary] = usePersistentState("vendorLibrary", buildVendorLibrary(initialVendors, initialClients));
  const [vendorView, setVendorView] = useState("list");
  const [libQuery, setLibQuery] = useState("");
  const [libCat, setLibCat] = useState("All");
  const [libSort, setLibSort] = useState("name");
  const [libSelectedId, setLibSelectedId] = useState(null);
  const [rawSettings, setRawSettings] = usePersistentState(SETTINGS_KEY, DEFAULT_SETTINGS);
  // Older saved settings get Phase 11's packages, add-ons and terms filled in on read; the summaries are always regenerated.
  const settings = useMemo(() => normalizeSettings(rawSettings), [rawSettings]);
  const canEditSettings = !cloud || cloud.isOwner;
  const setSettings = next => setRawSettings(prev => normalizeSettings(typeof next === "function" ? next(normalizeSettings(prev)) : next));
  // One-time cleanup: saved settings from before the rebrand still carry the old sample studio name.
  // Only the owner (or local mode) writes it back; planners just read the normalized copy.
  useEffect(() => {
    if (!canEditSettings) return;
    setRawSettings(prev => {
      const patch = {};
      if (["Willow & Vow", "Events By AD", "Ghostwriter-Events", "GhostWriter-Events"].includes(prev.businessName)) patch.businessName = "GhostWriter Events";
      if (prev.plannerName === "Sylvie Marchand") patch.plannerName = "";
      if (!prev.plannerEmail) patch.plannerEmail = "amanda@ghostwriter-events.com";
      if (prev.model !== normalizeModel(prev.model)) patch.model = normalizeModel(prev.model);
      // Phase 11 fields are filled in on read (and by the server), so they're only written on a real save.
      return Object.keys(patch).length ? normalizeSettings({ ...prev, ...patch }) : prev;
    });
  }, [canEditSettings]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);   // phone: full-screen module sheet
  const [moreOpen, setMoreOpen] = useState(false);   // phone: header actions
  const [quotesByClient, setQuotesByClient] = usePersistentState("quotesByClient", {});
  const [contractsByClient, setContractsByClient] = usePersistentState("contractsByClient", {});
  const [feesByClient, setFeesByClient] = usePersistentState("feesByClient", {});
  const [reportsByClient, setReportsByClient] = usePersistentState("reportsByClient", {});
  const [compareCategory, setCompareCategory] = useState("Florist");
  const [customCategories, setCustomCategories] = usePersistentState("customCategories", []);
  const [comparisonSlots, setComparisonSlots] = usePersistentState("comparisonSlots", {});
  const [portalMode, setPortalMode] = useState(false);
  const [portalSection, setPortalSection] = useState("profile");
  const [clientsState, setClientsState] = usePersistentState("clients", initialClients);
  // Phase 11: couples saved with the old free-text tier get a packageId (Month-of → Wedding Day Coordination).
  // Runs whenever a couple without one appears (a new intake, an import, a teammate's change); idempotent.
  useEffect(() => {
    if (clientsState.some(c => c && !("packageId" in c))) setClientsState(prev => migrateClients(prev));
  }, [clientsState]);
  const [intakeMode, setIntakeMode] = useState(false);
  const [backupNote, setBackupNote] = useState("");
  const onImportFile = e => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async ev => {
      try {
        const state = parseBackup(ev.target.result);
        if (cloud && !window.confirm("Import this backup into the shared studio data? Matching records are replaced for everyone on the team.")) return;
        setBackupNote("Importing…");
        await store.importBackup(state);
        setBackupNote("Backup imported.");
      } catch (err) {
        setBackupNote(err.message || "Could not read this file.");
      }
    };
    reader.onerror = () => setBackupNote("Could not read this file.");
    reader.readAsText(file);
  };
  const onReset = () => {
    if (window.confirm("Reset to the built-in sample data? This erases saved clients, tasks, documents and vendors on this browser. Export a backup first if you want to keep them.")) {
      store.resetToSample();
    }
  };
  const actor = cloud ? cloud.actor() : undefined;
  const stamp = label => actor ? { at: new Date().toISOString(), label, by: actor } : { at: new Date().toISOString(), label };

  const client = clientsState.find(c => c.id === selectedId) || clientsState[0] || null;
  const clientId = client ? client.id : null;
  const currentPhase = client ? derivePhase(client, milestones[clientId] || []) : "early";

  const goModule = m => () => { setModule(m); setMenuOpen(false); setMoreOpen(false); };
  const onClientChange = id => { setClientId(id); setVendorId(null); setVendorCat("All"); setVendorStatus("All"); };
  const onSetActive = id => { setClientId(id); setVendorId(null); };
  const setSort = k => setSortKey(prev => { setSortDir(d => prev === k ? -d : 1); return k; });

  // Remove a couple (a test, a spam intake, a couple who went elsewhere): their profile and everything filed under them,
  // their uploaded files, and their portal link. Rows become tombstones the nightly cleanup clears after 30 days.
  const dropKey = id => prev => { if (!prev || !(id in prev)) return prev; const next = { ...prev }; delete next[id]; return next; };
  const removeClient = async cl => {
    if (!cl) return;
    const signed = contractsByClient[cl.id] && ["Executed", "Signed", "Signed by couple"].includes(contractsByClient[cl.id].status);
    const msg = "Remove " + cl.names + "?\n\nTheir profile, tasks, documents, quote, agreement and payments are removed for everyone on the team, and their portal link stops working." +
      (signed ? " Their agreement is signed: download the signed copy from Documents first if you want to keep it." : "") +
      "\n\nThis can't be undone from the app.";
    if (!window.confirm(msg)) return;
    const st = getActiveStore();
    (docsByClient[cl.id] || []).forEach(d => { if (d.file && d.file.path && st.deleteDocFile) st.deleteDocFile(d.file.path); });
    if (st.revokePortalLink && portalLinks && portalLinks[cl.id]) { try { await st.revokePortalLink(cl.id); } catch (e) { console.warn("Portal link not turned off", e); } }
    [setTasksByClient, setDocsByClient, setQuotesByClient, setContractsByClient, setFeesByClient, setReportsByClient, setComparisonSlots].forEach(set => set(dropKey(cl.id)));
    setVendorsState(prev => prev.some(v => v.client === cl.id) ? prev.filter(v => v.client !== cl.id) : prev);
    setClientsState(prev => prev.filter(c => c.id !== cl.id));
    if (cl.id === clientId) { const nextC = clientsState.find(c => c.id !== cl.id); setClientId(nextC ? nextC.id : null); }
  };

  const defaultSlots = category => vendorsState.filter(v => v.client === clientId && v.category === category).slice(0, 3).map(v => v.id);
  const currentSlots = () => (comparisonSlots[clientId] && comparisonSlots[clientId][compareCategory]) || defaultSlots(compareCategory);
  const setSlots = next => setComparisonSlots(prev => ({ ...prev, [clientId]: { ...(prev[clientId] || {}), [compareCategory]: next } }));

  const addCustomCategory = name => setCustomCategories(prev => prev.includes(name) ? prev : [...prev, name]);
  // Single choke point every vendor-add path calls, so the global library stays in sync.
  const registerVendor = vendor => setVendorLibrary(prev => upsertVendorToLibrary(prev, vendor, clientsState));
  const updateLibraryEntry = (id, patch) => setVendorLibrary(prev => prev.map(e => e.id === id ? { ...e, ...patch } : e));
  const addVendorFromLibrary = entry => {
    const existing = vendorsState.some(v => v.client === clientId && normVendorKey(v.name, v.category) === entry.id);
    if (existing) return false;
    const id = clientId + "-vendor-" + Date.now();
    const vendor = {
      id, client: clientId, inv: entry.name.slice(0, 2).toUpperCase(), name: entry.name, category: entry.category,
      contact: entry.contact || "—", phone: entry.phone || "—", email: entry.email || "—", website: entry.website || "—", address: "—",
      contractStatus: "Considering", range: "Awaiting decision", keyTerms: "Added from Vendor Library",
      quote: 0, deposit: 0, depositDue: "—", depositPaid: false, final: 0, finalDue: "—", finalPaid: false, method: "—",
      services: [], addons: [], lastContact: "—", recent: false, notes: entry.notes || ""
    };
    setVendorsState(prev => [...prev, vendor]);
    registerVendor(vendor);
    return true;
  };
  const swapIntoComparison = vendorId => {
    const current = currentSlots();
    if (current.includes(vendorId)) return;
    setSlots(current.length < 3 ? [...current, vendorId] : [...current.slice(0, 2), vendorId]);
  };
  const removeFromComparison = vendorId => setSlots(currentSlots().filter(id => id !== vendorId));
  const addVendorToCategory = form => {
    const id = clientId + "-vendor-" + Date.now();
    const vendor = {
      id, client: clientId, inv: form.name.slice(0, 2).toUpperCase(), name: form.name, category: compareCategory,
      contact: form.contact, phone: form.phone, email: form.email, website: "—", address: "—",
      contractStatus: "Considering", range: "Awaiting decision", keyTerms: "Manually added candidate",
      quote: Number(form.quote) || 0, deposit: 0, depositDue: "—", depositPaid: false, final: 0, finalDue: "—", finalPaid: false, method: "—",
      services: form.services ? form.services.split(",").map(s => s.trim()).filter(Boolean) : [], addons: [],
      lastContact: "—", recent: false, notes: form.notes || ""
    };
    setVendorsState(prev => [...prev, vendor]);
    registerVendor(vendor);
    const current = currentSlots();
    setSlots(current.length < 3 ? [...current, id] : current);
  };

  const cycleTaskStatus = taskId => setTasksByClient(prev => ({
    ...prev,
    [clientId]: prev[clientId].map(t => t.id === taskId ? { ...t, status: t.status === "incomplete" ? "in-progress" : t.status === "in-progress" ? "complete" : "incomplete" } : t)
  }));
  const addTask = task => setTasksByClient(prev => ({
    ...prev,
    [clientId]: [...(prev[clientId] || []), { ...task, id: clientId + "-custom-" + Date.now() }]
  }));
  const updateTask = (taskId, patch) => setTasksByClient(prev => ({
    ...prev,
    [clientId]: prev[clientId].map(t => t.id === taskId ? { ...t, ...patch } : t)
  }));
  const removeTask = taskId => setTasksByClient(prev => ({
    ...prev,
    [clientId]: prev[clientId].filter(t => t.id !== taskId)
  }));

  const addDocument = doc => setDocsByClient(prev => ({
    ...prev,
    [clientId]: [...(prev[clientId] || []), { ...doc, id: clientId + "-doc-" + Date.now() }]
  }));
  const updateDocument = (docId, patch) => setDocsByClient(prev => ({
    ...prev,
    [clientId]: prev[clientId].map(d => d.id === docId ? { ...d, ...patch } : d)
  }));
  const removeDocument = docId => setDocsByClient(prev => ({
    ...prev,
    [clientId]: prev[clientId].filter(d => d.id !== docId)
  }));

  // ── Quotes (Phase 8 · Step 5) ──
  const quote = quotesByClient[clientId] || null;
  // Merge a patch into the active client's quote and append a timeline event.
  const commitQuote = (patch, eventLabel) => setQuotesByClient(prev => {
    const cur = prev[clientId] || {};
    const events = eventLabel ? [...(cur.events || []), stamp(eventLabel)] : (cur.events || []);
    return { ...prev, [clientId]: { ...cur, ...patch, events } };
  });
  const generateQuote = async () => {
    const pricing = pricingFrom(settings);
    const requested = requestedPackageFor(client);
    const chosen = { ids: client.addonIds || [], qty: client.addonQty || {} };
    const { system, prompt } = buildQuotePrompt(summarizeIntakeForQuote(client), requested, pricing, chosen);
    const { text } = await callClaude(settings, { system, prompt, maxTokens: 1800 });
    const built = assembleQuote(parseQuoteJSON(text), pricing, requested, chosen);
    const existing = quotesByClient[clientId];
    const q = {
      id: (existing && existing.id) || clientId + "-quote-" + Date.now(),
      createdAt: (existing && existing.createdAt) || new Date().toISOString(),
      status: "Draft",
      packageId: built.packageId,
      packageTier: built.packageTier,
      rangeLow: built.rangeLow,
      rangeHigh: built.rangeHigh,
      lineItems: built.lineItems,
      rationale: built.rationale,
      pricedFrom: "pricing-sheet-2026-10-15",
      finalNumber: null,
      showBreakdownToClient: false,
      changeRequest: (existing && existing.changeRequest) || null,
      email: null,
      sentAt: null,
      events: [...((existing && existing.events) || []), stamp(existing ? "Regenerated draft" : "Draft generated")]
    };
    setQuotesByClient(prev => ({ ...prev, [clientId]: q }));
  };
  const prepareQuoteEmail = () => {
    const q = quotesByClient[clientId];
    if (!q) return;
    commitQuote({ email: buildQuoteEmail(client, q, settings, portalLinks && portalLinks[clientId] && portalLinks[clientId].url) }, "Email prepared");
  };

  // ── Contracts (Phase 8 · Step 6) ──
  const contract = contractsByClient[clientId] || null;
  const commitContract = (patch, eventLabel) => setContractsByClient(prev => {
    const cur = prev[clientId] || {};
    const events = eventLabel ? [...(cur.events || []), stamp(eventLabel)] : (cur.events || []);
    return { ...prev, [clientId]: { ...cur, ...patch, events } };
  });
  // Phase 11: "Prepare agreement" fills in her template. Nothing is drafted by AI, and nothing half-filled is created.
  const prepareContract = () => {
    const q = quotesByClient[clientId];
    if (!q || q.status !== "Client approved") throw new Error("Mark this couple’s quote as “Client approved” first. That unlocks the agreement.");
    const r = fillContract({ client, settings, quote: q });
    if (!r.ok) { const e = new Error("The agreement still needs: " + r.missing.join(", ") + "."); e.missing = r.missing; throw e; }
    const existing = contractsByClient[clientId];
    const c = {
      id: (existing && existing.id) || clientId + "-contract-" + Date.now(),
      createdAt: (existing && existing.createdAt) || new Date().toISOString(),
      status: "Draft",
      body: r.body,
      preparedBody: r.body,
      templateVersion: r.version,
      money: { total: r.money.total, retainer: r.money.retainer, balance: r.money.balance, balanceDue: r.money.balanceDue ? r.money.balanceDue.toISOString().slice(0, 10) : null },
      editLogged: false,
      email: null,
      sentAt: null,
      signedAt: null,
      changeRequest: (existing && existing.changeRequest) || null,
      filedDocId: (existing && existing.filedDocId) || null,
      events: [...((existing && existing.events) || []), stamp(existing ? "Prepared again from the template" : "Prepared from the template")]
    };
    setContractsByClient(prev => ({ ...prev, [clientId]: c }));
  };
  const prepareContractEmail = () => {
    const c = contractsByClient[clientId];
    if (!c) return;
    commitContract({ email: buildContractEmail(client, c, settings, portalLinks && portalLinks[clientId] && portalLinks[clientId].url) }, "Email prepared");
  };
  const fileContractToDocuments = docStatus => {
    const q = quotesByClient[clientId];
    const amount = q ? (q.finalNumber || q.rangeHigh || 0) : 0;
    const id = clientId + "-contract-doc-" + Date.now();
    const doc = {
      id: id,
      name: ((settings && settings.businessName) || "GhostWriter Events") + " — Coordination Services Agreement",
      category: "Signed agreement",
      vendor: null,
      status: docStatus,
      range: client.date || "",
      amount: amount,
      terms: (settings && settings.paymentTerms) || "",
      cancel: (settings && settings.cancellationPolicy) || "",
      insurance: "—"
    };
    setDocsByClient(prev => ({ ...prev, [clientId]: [...(prev[clientId] || []), doc] }));
    return id;
  };
  const markContractSent = () => {
    const c = contractsByClient[clientId];
    if (!c) return;
    const docId = c.filedDocId || fileContractToDocuments("Sent");
    commitContract({ status: "Sent", sentAt: new Date().toISOString(), filedDocId: docId }, "Marked as sent — filed to Documents");
  };
  const markContractSigned = () => {
    const c = contractsByClient[clientId];
    if (!c) return;
    let docId = c.filedDocId;
    if (docId) {
      setDocsByClient(prev => ({ ...prev, [clientId]: (prev[clientId] || []).map(d => d.id === docId ? { ...d, status: "Executed" } : d) }));
    } else {
      docId = fileContractToDocuments("Executed");
    }
    const effectiveDate = todayYmd();
    commitContract({ status: "Signed", signedAt: new Date().toISOString(), filedDocId: docId, effectiveDate }, "Marked as signed on paper");
    // Phase 11 · Step 6: the payment schedule starts from today's date, as the Effective Date.
    const fees = buildFees({ contract: c, client, settings, effectiveDate });
    if (fees.length) setFeesByClient(prev => (prev[clientId] && prev[clientId].length) ? prev : { ...prev, [clientId]: fees });
    // Phase 11 · Step 7: the agreement's deadlines become tasks.
    setTasksByClient(prev => { const cur = prev[clientId] || []; const next = mergeContractTasks(cur, contractTasks({ client, settings })); return next === cur ? prev : { ...prev, [clientId]: next }; });
  };

  const submitIntake = form => {
    const newClient = buildClientFromIntake(form, { source: "dashboard" });
    setClientsState(prev => [...prev, newClient]);
    setClientId(newClient.id);
    setModule("clients");
    return newClient;
  };

  if (intakeMode) {
    return <IntakeQuestionnaire onSubmit={submitIntake} onDone={() => setIntakeMode(false)} studioName={settings.businessName} packages={settings.packages} />;
  }

  if (portalMode && client) {
    return <CouplePortal client={client} vendors={vendorsState} portalSection={portalSection} setPortalSection={setPortalSection} onExit={() => setPortalMode(false)} studioName={settings.businessName} />;
  }

  const statusDot = !cloud ? "var(--ink)" : saveStatus.state === "error" ? "var(--oxblood)" : saveStatus.state === "saving" ? "var(--graphite)" : "var(--ink)";
  const statusLabel = !cloud ? "Data saved on this browser" : saveStatus.state === "error" ? "Not saved yet — retrying" : saveStatus.state === "saving" ? "Saving…" : "All changes saved";

  return (
    <SettingsContext.Provider value={settings}>
    <FeesContext.Provider value={feesByClient}>
    <UpdatesContext.Provider value={{ contracts: contractsByClient, reports: reportsByClient }}>
    <div className="app-shell">
      {/* Phone: slim top bar with the Manuscript logo and a Menu button */}
      <div className="app-topbar">
        <img className="logo" src="/brand/manuscript-light.svg" alt={settings.businessName || "GhostWriter Events"} width="117" height="43" />
        <button className="btn btn-secondary" aria-expanded={menuOpen} aria-controls="app-modules" onClick={() => setMenuOpen(true)}>Menu</button>
      </div>

      <aside id="app-modules" className={"app-sidebar" + (menuOpen ? " open" : "")} aria-label="Modules">
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "0 6px 22px" }}>
          <img src="/brand/manuscript-light.svg" alt="" width="170" height="62" style={{ width: 170, height: "auto", margin: "-8px 0 0 -6px" }} />
          <span className="sr-only">{settings.businessName || "GhostWriter Events"}</span>
          <button className="btn btn-secondary phone-only" onClick={() => setMenuOpen(false)}>Close</button>
        </div>

        {FLAT_NAV.map(mk => (
          <button key={mk} className={"navlink" + (module === mk ? " active" : "")} aria-current={module === mk ? "page" : undefined} onClick={goModule(mk)}>
            <Icon path={ICONS[mk]} size={17} />
            {NAV_LABELS[mk]}
          </button>
        ))}

        <div style={{ marginTop: "auto", paddingTop: 22, display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ borderTop: "1px solid var(--rule)", paddingTop: 14, display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 15, fontWeight: 500 }} title={saveStatus.message || ""}>
              <span aria-hidden="true" style={{ width: 7, height: 7, background: statusDot, flex: "none" }} />
              {statusLabel}
            </div>
            <div className="text-muted" style={{ fontSize: 14, lineHeight: 1.4 }}>
              {cloud ? "Saved to your studio account — sign in from any device. Export keeps an extra copy." : "Changes persist automatically. Keep a backup file for safety."}
            </div>
            <div style={{ display: "flex", gap: 18, marginTop: 2 }}>
              <button className="btn-link" onClick={() => { store.exportBackup(); setBackupNote("Backup downloaded."); }}>Export</button>
              {canManage && (
                <label className="btn-link" style={{ margin: 0 }}>
                  Import
                  <input type="file" accept="application/json,.json" onChange={onImportFile} style={{ display: "none" }} />
                </label>
              )}
              {!cloud && <button className="btn-link" onClick={onReset}>Reset</button>}
            </div>
            {backupNote ? <div className="text-muted" style={{ fontSize: 14 }}>{backupNote}</div> : null}
          </div>
          <div style={{ borderTop: "1px solid var(--rule)", paddingTop: 12 }}>
            <div style={{ fontSize: 16, fontWeight: 500 }}>{cloud ? cloud.actor() : (settings.plannerName || settings.businessName || "GhostWriter Events")}</div>
            <div className="text-muted" style={{ fontSize: 14 }}>{cloud ? (cloud.isOwner ? "Owner" : "Planner") : "Lead planner"} · {clientsState.length} active clients</div>
            {cloud && <button className="btn-link" style={{ marginTop: 6 }} onClick={() => cloud.signOut()}>Sign out</button>}
          </div>
        </div>
      </aside>

      <main className="app-main">
        <header className="app-header">
          <div style={{ flex: 1, minWidth: 220 }}>
            {client && <div className="label" title={PHASES.find(p => p.key === currentPhase).blurb}>Phase · {PHASES.find(p => p.key === currentPhase).label}</div>}
            <h2 className="module-title" style={{ marginTop: client ? 4 : 0 }}>{TITLES[module]}</h2>
          </div>
          <div className="header-controls">
          {client && (
            <select className="input" aria-label="Active client" value={clientId} onChange={e => onClientChange(e.target.value)} style={{ width: 280 }}>
              {clientsState.map(cl => <option key={cl.id} value={cl.id}>{cl.full} — {cl.date}</option>)}
            </select>
          )}
          <div className="desk-only row" style={{ "--gap": "10px" }}>
            <button className="btn btn-secondary" onClick={() => cloud ? setIntakeShareOpen(true) : setIntakeMode(true)}>Share intake form</button>
            {client && <button className="btn btn-secondary" onClick={() => cloud ? setLinksOpen(true) : setPortalMode(true)}>{cloud ? "Couple portal" : "View as couple"}</button>}
            {client && <button className="btn btn-primary" onClick={goModule("report")}>Weekly report</button>}
            <button className="btn btn-secondary btn-icon" title="Settings" aria-label="Settings" onClick={() => setSettingsOpen(true)}>
              <Icon path={ICONS.gear} size={18} />
            </button>
          </div>
          <div className="phone-only" style={{ position: "relative" }}>
            <button className="btn btn-secondary" aria-haspopup="menu" aria-expanded={moreOpen} onClick={() => setMoreOpen(o => !o)}>More</button>
            {moreOpen && (
              <div className="more-menu" role="menu">
                <button role="menuitem" onClick={() => { setMoreOpen(false); cloud ? setIntakeShareOpen(true) : setIntakeMode(true); }}>Share intake form</button>
                {client && <button role="menuitem" onClick={() => { setMoreOpen(false); cloud ? setLinksOpen(true) : setPortalMode(true); }}>{cloud ? "Couple portal" : "View as couple"}</button>}
                {client && <button role="menuitem" onClick={goModule("report")}>Weekly report</button>}
                <button role="menuitem" onClick={() => { setMoreOpen(false); setSettingsOpen(true); }}>Settings</button>
              </div>
            )}
          </div>
          </div>
        </header>

        <div className="app-main-scroll" tabIndex={0} role="region" aria-label={TITLES[module]}>
          {!client && (
            <div className="panel measure" style={{ "--measure": "640px", padding: "34px 34px 30px", gap: 16 }}>
              <div className="label">Chapter one</div>
              <h3 className="panel-title" style={{ fontSize: 34 }}>Welcome to {settings.businessName || "GhostWriter Events"}</h3>
              <p style={{ margin: 0, fontSize: 17, lineHeight: 1.6 }}>
                There are no clients yet. Share the intake form with a couple and they&rsquo;ll appear here as soon as they submit it
                {canManage ? <> &mdash; or bring over everything from the old dashboard with <strong>Import</strong> (bottom left) using its exported backup file.</> : "."}
              </p>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button className="btn btn-accent" onClick={() => cloud ? setIntakeShareOpen(true) : setIntakeMode(true)}>Share intake form</button>
                <button className="btn btn-secondary" onClick={() => setIntakeMode(true)}>Fill in an intake myself</button>
              </div>
            </div>
          )}
          {client && (<>
          {module === "clients" && <ClientProfiles clients={clientsState} clientId={clientId} onSetActive={onSetActive} onUpdateClient={(id, patch) => setClientsState(prev => prev.map(c => c.id === id ? { ...c, ...patch } : c))} onRemoveClient={canManage ? removeClient : null} />}
          {module === "tasks" && (
            <Tasks key={client.id} client={client} tasks={tasksByClient[clientId] || []} onCycle={cycleTaskStatus} onAdd={addTask} onUpdate={updateTask} onRemove={removeTask} />
          )}
          {module === "vendors" && (
            <VendorManagement
              vendors={vendorsState} clients={clientsState}
              clientId={clientId} vendorCat={vendorCat} setVendorCat={setVendorCat}
              vendorStatus={vendorStatus} setVendorStatus={setVendorStatus}
              sortKey={sortKey} sortDir={sortDir} setSort={setSort}
              vendorId={vendorId} setVendorId={setVendorId}
              vendorView={vendorView} setVendorView={setVendorView}
              compareCategory={compareCategory} setCompareCategory={setCompareCategory}
              customCategories={customCategories} onAddCustomCategory={addCustomCategory}
              comparisonVendorIds={currentSlots()} onSwapIn={swapIntoComparison}
              onRemoveFromComparison={removeFromComparison} onAddVendorToCategory={addVendorToCategory}
            />
          )}
          {module === "timeline" && <Timeline client={client} timelineView={timelineView} setTimelineView={setTimelineView} />}
          {module === "budget" && <Budget client={client} vendors={vendorsState} />}
          {module === "documents" && (
            <Documents
              client={client} docs={docsByClient[clientId] || []} vendors={vendorsState.filter(v => v.client === clientId)}
              docQuery={docQuery} setDocQuery={setDocQuery} onAdd={addDocument} onUpdate={updateDocument} onRemove={removeDocument}
            />
          )}
          {module === "quotes" && (
            <QuoteManager
              key={client.id}
              client={client} quote={quote} contract={contract} settings={settings}
              onGenerate={generateQuote} onCommit={commitQuote} onPrepareEmail={prepareQuoteEmail}
              onGenerateContract={prepareContract} onCommitContract={commitContract}
              onCountersigned={r => { setContractsByClient(prev => ({ ...prev, [clientId]: r.contract })); if (Array.isArray(r.docs)) setDocsByClient(prev => ({ ...prev, [clientId]: r.docs })); if (Array.isArray(r.fees)) setFeesByClient(prev => ({ ...prev, [clientId]: r.fees })); if (Array.isArray(r.tasks)) setTasksByClient(prev => ({ ...prev, [clientId]: r.tasks })); }}
              fees={feesByClient[clientId] || []} onCommitFees={list => setFeesByClient(prev => ({ ...prev, [clientId]: list }))}
              onPrepareContractEmail={prepareContractEmail} onMarkContractSent={markContractSent} onMarkContractSigned={markContractSigned}
              onOpenSettings={() => setSettingsOpen(true)}
            />
          )}
          {module === "portfolio" && <Portfolio portFilter={portFilter} setPortFilter={setPortFilter} />}
          {module === "library" && (
            <VendorLibrary
              library={vendorLibrary} clients={clientsState} activeClient={client}
              query={libQuery} setQuery={setLibQuery}
              cat={libCat} setCat={setLibCat}
              sortKey={libSort} setSortKey={setLibSort}
              selectedId={libSelectedId} setSelectedId={setLibSelectedId}
              onAddToClient={addVendorFromLibrary}
              onUpdateEntry={updateLibraryEntry}
              onGoToClientVendors={id => { onClientChange(id); setModule("vendors"); }}
            />
          )}
          {module === "report" && <WeeklyReport client={client} vendors={vendorsState} settings={settings} reportNote={reportNote} setReportNote={setReportNote}
            reportSent={(reportsByClient[clientId] || {})[isoWeekToday()] ? reportsByClient[clientId][isoWeekToday()].sentAt : false}
            setReportSent={at => setReportsByClient(prev => ({ ...prev, [clientId]: { ...(prev[clientId] || {}), [isoWeekToday()]: { sentAt: at, by: (getActiveStore().actor && getActiveStore().actor()) || "Planner" } } }))}
            weekly={weeklyUpdate({ client, contract: contractsByClient[clientId], settings, reports: reportsByClient[clientId], today: todayYmd() })} />}
          </>)}
        </div>
      </main>
      {settingsOpen && <SettingsPanel settings={settings} onSave={setSettings} onClose={() => setSettingsOpen(false)} />}
      {linksOpen && client && cloud && <CoupleLinksDialog client={client} onPreview={() => { setLinksOpen(false); setPortalMode(true); }} onClose={() => setLinksOpen(false)} />}
      {intakeShareOpen && cloud && <ShareIntakeDialog onFillHere={() => { setIntakeShareOpen(false); setIntakeMode(true); }} onClose={() => setIntakeShareOpen(false)} />}
    </div>
    </UpdatesContext.Provider>
    </FeesContext.Provider>
    </SettingsContext.Provider>
  );
}

/* ───────────────────────── Vendor Library ───────────────────────── */

function StarRating({ value, onChange, label = "Rating" }) {
  if (!onChange) {
    return (
      <span className="stars" role="img" aria-label={(value || 0) + " of 5 stars"}>
        {[1, 2, 3, 4, 5].map(n => <span key={n} className={n <= value ? "on" : ""} aria-hidden="true">★</span>)}
      </span>
    );
  }
  return (
    <span className="stars" role="group" aria-label={label}>
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n} type="button" className={n <= value ? "on" : ""} aria-pressed={n <= value}
          aria-label={n + " star" + (n > 1 ? "s" : "")}
          onClick={e => { e.stopPropagation(); onChange(n === value ? 0 : n); }}>★</button>
      ))}
    </span>
  );
}

function VendorLibrary({
  library, clients, activeClient, query, setQuery, cat, setCat, sortKey, setSortKey,
  selectedId, setSelectedId, onAddToClient, onUpdateEntry, onGoToClientVendors
}) {
  const [note, setNote] = useState("");
  // Side panel when there is room for it; a dialog (full screen on phones) when there isn't.
  const phone = useMedia("(max-width: 1100px)");
  const priceRange = e => {
    const qs = e.pricing.map(p => p.quote).filter(Boolean);
    if (!qs.length) return "—";
    const lo = Math.min(...qs), hi = Math.max(...qs);
    return lo === hi ? fmt(lo) : fmt(lo) + " – " + fmt(hi);
  };
  const categories = useMemo(() => ["All", ...Array.from(new Set(library.map(e => e.category))).sort()], [library]);
  const filtered = library.filter(e => {
    const q = query.trim().toLowerCase();
    const matchesQ = !q || e.name.toLowerCase().includes(q) || (e.contact || "").toLowerCase().includes(q) || e.category.toLowerCase().includes(q);
    return matchesQ && (cat === "All" || e.category === cat);
  });
  const sorted = [...filtered].sort((a, b) => {
    if (sortKey === "used") return b.usedWith.length - a.usedWith.length || a.name.localeCompare(b.name);
    if (sortKey === "rating") return (b.rating || 0) - (a.rating || 0) || a.name.localeCompare(b.name);
    return a.name.localeCompare(b.name);
  });
  const selected = library.find(e => e.id === selectedId) || null;
  const alreadyOnActive = selected && selected.usedWith.some(u => u.clientId === (activeClient && activeClient.id));
  const open = id => { setSelectedId(id); setNote(""); };

  const detail = selected && (
    <div className="stack" style={{ "--gap": "16px" }}>
      <div className="row" style={{ "--gap": "8px" }}>
        <span className="label">Your rating</span>
        <StarRating value={selected.rating || 0} onChange={r => onUpdateEntry(selected.id, { rating: r })} label={"Rate " + selected.name} />
      </div>
      <div className="grid-2" style={{ "--gap": "12px 18px" }}>
        {selected.contact && <Meta label="Contact">{selected.contact}</Meta>}
        {selected.phone && <Meta label="Phone">{selected.phone}</Meta>}
        {selected.email && <Meta label="Email">{selected.email}</Meta>}
        {selected.website && <Meta label="Website"><SafeLink href={selected.website} /></Meta>}
      </div>
      <div>
        <div className="label" style={{ marginBottom: 8 }}>Used with</div>
        <div className="row" style={{ "--gap": "6px" }}>
          {selected.usedWith.map(u => (
            <button key={u.clientId} className="chip chip-sm" onClick={() => onGoToClientVendors(u.clientId)} title="Open this client's vendors">{u.clientName}</button>
          ))}
        </div>
      </div>
      {selected.pricing.length > 0 && (
        <div>
          <div className="label" style={{ marginBottom: 4 }}>Pricing history</div>
          {selected.pricing.map((p, i) => (
            <div key={i} className="list-row list-split" style={{ fontSize: 15, padding: "7px 0" }}><span>{p.clientName} <span className="text-muted">({p.status})</span></span><span className="figure">{fmt(p.quote)}</span></div>
          ))}
        </div>
      )}
      <Field label="Notes"><textarea className="input" style={{ minHeight: 72 }} value={selected.notes || ""} onChange={e => onUpdateEntry(selected.id, { notes: e.target.value })} placeholder="Add a note about this vendor…" /></Field>
      <div className="stack" style={{ "--gap": "6px" }}>
        <button
          className="btn btn-primary btn-block"
          disabled={alreadyOnActive}
          onClick={() => {
            if (alreadyOnActive) return;
            const ok = onAddToClient(selected);
            setNote(ok ? "Added to " + (activeClient ? activeClient.full : "client") + " as a candidate." : "Already on this client.");
          }}
        >
          {alreadyOnActive ? "Already on " + (activeClient ? activeClient.names : "client") : "Add to " + (activeClient ? activeClient.names : "current client")}
        </button>
        {note && <div className="text-muted" role="status" style={{ fontSize: 15 }}>{note}</div>}
      </div>
    </div>
  );

  return (
    <div className="stack" style={{ "--gap": "18px" }}>
      <p className="module-intro">Every vendor ever added to any client, in one place. It updates whenever you add a vendor to a couple, so proven vendors are easy to reuse.</p>

      <div className="row" style={{ "--gap": "10px 18px", alignItems: "flex-end" }}>
        <div className="search">
          <Icon path={<><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.3-4.3"></path></>} size={16} />
          <input className="input" type="search" aria-label="Search vendors by name, contact or category" placeholder="Search name, contact, category…" value={query} onChange={e => setQuery(e.target.value)} />
        </div>
        <div role="group" aria-labelledby="vl-sort" className="row" style={{ "--gap": "6px" }}>
          <span id="vl-sort" className="label" style={{ marginRight: 4 }}>Sort</span>
          {[["name", "Name"], ["used", "Most used"], ["rating", "Top rated"]].map(([k, l]) => (
            <button key={k} className={"chip chip-sm" + (sortKey === k ? " active" : "")} aria-pressed={sortKey === k} onClick={() => setSortKey(k)}>{l}</button>
          ))}
        </div>
      </div>
      <div role="group" aria-labelledby="vl-cat" className="row" style={{ "--gap": "6px" }}>
        <span id="vl-cat" className="label" style={{ width: "100%" }}>Category</span>
        {categories.map(c => (
          <button key={c} className={"chip chip-sm" + (cat === c ? " active" : "")} aria-pressed={cat === c} onClick={() => setCat(c)}>{c}</button>
        ))}
      </div>

      <div className={selected && !phone ? "split" : ""} style={{ "--a": "1fr", "--b": "360px" }}>
        <div className="panel flush">
          <table className="table table-cards">
            <thead><tr><th>Vendor</th><th>Category</th><th>Used with</th><th>Pricing seen</th><th>Rating</th></tr></thead>
            <tbody>
              {sorted.map(e => (
                <tr key={e.id} onClick={() => open(e.id)} style={{ cursor: "pointer", background: e.id === selectedId ? "var(--vellum)" : undefined }}>
                  <td>
                    <button className="sortbtn" style={{ fontFamily: "var(--font-body)", textTransform: "none", letterSpacing: 0, fontSize: 18, fontWeight: 500 }} onClick={ev => { ev.stopPropagation(); open(e.id); }}>{e.name}</button>
                    <div className="text-muted" style={{ fontSize: 15 }}>{e.contact || "—"}</div>
                  </td>
                  <td data-label="Category">{e.category}</td>
                  <td data-label="Used with">{e.usedWith.length} client{e.usedWith.length === 1 ? "" : "s"}</td>
                  <td data-label="Pricing seen" className="figure">{priceRange(e)}</td>
                  <td data-label="Rating"><StarRating value={e.rating || 0} /></td>
                </tr>
              ))}
              {sorted.length === 0 && (
                <tr><td colSpan={5} className="empty" style={{ textAlign: "center" }}>No vendors match your filters yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {selected && !phone && (
          <aside className="panel" aria-label={selected.name} style={{ position: "sticky", top: 0 }}>
            <div className="panel-head" style={{ alignItems: "flex-start" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="label">{selected.category}</div>
                <h3 className="panel-title" style={{ marginTop: 4 }}>{selected.name}</h3>
              </div>
              <button className="btn btn-secondary" onClick={() => setSelectedId(null)}>Close</button>
            </div>
            {detail}
          </aside>
        )}
      </div>

      {selected && phone && (
        <Dialog title={selected.name} kicker={selected.category} onClose={() => setSelectedId(null)}>{detail}</Dialog>
      )}
    </div>
  );
}

/* ───────────────────────── Quotes & Contracts ───────────────────────── */

function QuoteManager({ client, quote, contract, settings, onGenerate, onCommit, onPrepareEmail, onGenerateContract, onCommitContract, onPrepareContractEmail, onMarkContractSent, onMarkContractSigned, onOpenSettings, onCountersigned, fees, onCommitFees }) {
  const [tab, setTab] = useState("quote");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [copyNote, setCopyNote] = useState("");
  const [finalInput, setFinalInput] = useState(quote && quote.finalNumber ? String(quote.finalNumber) : "");
  const [busyC, setBusyC] = useState(false);
  const [errC, setErrC] = useState(null);
  const [copyNoteC, setCopyNoteC] = useState("");

  const hasKey = aiReady(settings);
  const run = async () => {
    setBusy(true); setErr(null);
    try { await onGenerate(); } catch (e) { setErr(e); } finally { setBusy(false); }
  };
  const runContract = async () => {
    if (contract && contract.body !== contract.preparedBody && !window.confirm("Prepare the agreement again? Your edits to the text will be replaced.")) return;
    setBusyC(true); setErrC(null);
    try { await onGenerateContract(); } catch (e) { setErrC(e); } finally { setBusyC(false); }
  };
  const [cView, setCView] = useState("read");
  const copyText = async (txt, setNote) => {
    try { if (navigator.clipboard && navigator.clipboard.writeText) { await navigator.clipboard.writeText(txt); setNote("Copied to clipboard."); return; } } catch (e) { /* fall through */ }
    setNote("Select the text below and copy it (⌘/Ctrl-C).");
  };

  const lineSum = quote ? (quote.lineItems || []).reduce((s, li) => s + (Number(li.amount) || 0), 0) : 0;
  const setLine = (idx, key, val) => onCommit({ lineItems: quote.lineItems.map((li, i) => i === idx ? { ...li, [key]: key === "amount" ? (Number(val) || 0) : val } : li) });
  const addLine = () => onCommit({ lineItems: [...quote.lineItems, { label: "New line item", amount: 0, note: "", kind: "custom" }] });
  const customChecks = quote ? (quote.lineItems || []).filter(li => li.kind === "custom" && li.market) : [];
  const removeLine = idx => onCommit({ lineItems: quote.lineItems.filter((_, i) => i !== idx) });

  const approve = () => onCommit({ status: "Approved — ready to send", finalNumber: finalInput ? Math.round(Number(finalInput)) : null }, "Approved internally");
  const backToDraft = () => onCommit({ status: "Draft" }, "Reopened for edits");
  const markSent = () => onCommit({ status: "Sent", sentAt: new Date().toISOString() }, "Marked as sent");
  const clientResp = label => onCommit({ status: label }, "Client response: " + label);

  const approved = quote && quote.status !== "Draft";
  const sentOrLater = quote && ["Sent", "Client approved", "Client declined", "Negotiating"].includes(quote.status);
  const declined = quote && quote.status === "Client declined";
  // Oxblood once per view: the quote's next step gets it, unless the declined chip is already this screen's alert.
  const accent = cls => declined ? "btn-primary" : cls;

  const EmailBlock = ({ email, note, onCopy, hint }) => (
    <div className="stack" style={{ "--gap": "8px" }}>
      <div className="row" style={{ "--gap": "8px 12px" }}>
        <div className="label" style={{ flex: "1 1 260px", minWidth: 0, overflowWrap: "anywhere" }}>To: {email.to || "—"} · Subject: {email.subject}</div>
        <button className="btn-link" onClick={onCopy}>Copy email</button>
      </div>
      <textarea className="input" readOnly aria-label="Email text" style={{ minHeight: 200, fontSize: 15.5 }} value={email.body}></textarea>
      {note && <div className="text-muted" role="status" style={{ fontSize: 15 }}>{note}</div>}
      <div className="field-hint">{hint}</div>
    </div>
  );
  const EventLog = ({ events }) => (
    <details className="disclosure">
      <summary>Status timeline</summary>
      <div className="stack" style={{ "--gap": "4px", fontSize: 15 }}>
        {(events || []).map((ev, i) => (
          <div key={i}><span className="figure text-muted" style={{ fontSize: 13 }}>{new Date(ev.at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span> — {ev.label}{ev.by ? <span className="text-muted"> · {ev.by}</span> : null}</div>
        ))}
      </div>
    </details>
  );
  const NeedsKey = ({ children }) => (
    <Note>{children} <button className="btn-link" onClick={onOpenSettings} style={{ marginLeft: 6 }}>Open Settings</button></Note>
  );

  return (
    <div className="stack" style={{ "--gap": "22px" }}>
      <div className="tabs" role="tablist" aria-label="Quote or contract">
        {[["quote", "Quote"], ["contract", "Contract"], ["payments", "Payments"]].map(([tk, lbl]) => (
          <button key={tk} role="tab" className="tab" aria-selected={tab === tk} onClick={() => setTab(tk)}>{lbl}</button>
        ))}
      </div>

      {tab === "contract" && (() => {
        const unlocked = quote && quote.status === "Client approved";
        const cApproved = contract && contract.status !== "Draft";
        const cSentOrLater = contract && CONTRACT_SENT_OR_LATER.includes(contract.status);
        const paperOnly = contract && contract.status === "Sent" && !(contract.signatures || []).length;
        if (!unlocked) {
          return (
            <div className="panel measure" style={{ "--measure": "640px" }}>
              <div className="label">Contract</div>
              <p style={{ fontSize: 17, lineHeight: 1.6 }}>
                The agreement unlocks here once this couple&rsquo;s quote is marked <strong style={{ fontWeight: 500 }}>Client approved</strong> on the Quote tab.
                {quote ? " Their quote is currently: " + quote.status + "." : " No quote has been generated yet."}
              </p>
            </div>
          );
        }
        return (
          <div className="stack measure" style={{ "--gap": "18px", "--measure": "800px" }}>
            <Note>Your Coordination Services Agreement, word for word, filled in from Settings and {client.names}&rsquo;s details. Read it through before approving.</Note>

            {!contract && (
              <div className="panel" style={{ alignItems: "center", textAlign: "center", padding: "36px 28px" }}>
                <div className="label">Contract · {client.names}</div>
                <h3 className="panel-title" style={{ fontSize: 30 }}>Prepare the agreement</h3>
                <p className="text-muted" style={{ fontSize: 16, lineHeight: 1.6, maxWidth: 500 }}>
                  Fills in your contract with {client.names}&rsquo;s names, date, venue, package, fee and due dates, and adds the Highline section when it applies. You can still edit the text before sending.
                </p>
                <button className="btn btn-accent" onClick={runContract} disabled={busyC}>Prepare agreement</button>
                {errC && <ContractError err={errC} onOpenSettings={onOpenSettings} />}
              </div>
            )}

            {contract && (
              <div className="panel" style={{ gap: 16 }}>
                <div className="panel-head">
                  <div className="label" style={{ marginRight: "auto" }}>Contract · {client.names}</div>
                  <StatusTag status={contract.status} />
                </div>
                <ChangeRequestNote item={contract} />
                <SignatureNote contract={contract} />
                <SigningStatus contract={contract} client={client} />
                {contract.status === "Signed by couple" && <Countersign client={client} settings={settings} onDone={onCountersigned} />}
                <div className="label" style={{ textTransform: "none", letterSpacing: "0.08em" }}>
                  {contract.templateVersion ? "Her template, version " + contract.templateVersion : "Drafted before Phase 11 (AI-written). Prepare it again to use your contract."}
                  {contract.templateVersion && contract.body !== contract.preparedBody ? " · edited" : ""}
                </div>
                <div className="tabs" role="tablist" aria-label="Agreement view">
                  <button role="tab" className="tab" aria-selected={cView === "read"} onClick={() => setCView("read")}>Read</button>
                  {!cApproved && <button role="tab" className="tab" aria-selected={cView === "edit"} onClick={() => setCView("edit")}>Edit text</button>}
                </div>
                {(cView === "read" || cApproved)
                  ? <div className="agreement-paper" tabIndex={0} role="region" aria-label="Agreement preview"><AgreementDoc text={contract.body} /></div>
                  : <textarea className="input contract-text" aria-label="Contract text" value={contract.body}
                      onChange={e => onCommitContract({ body: e.target.value })}
                      onBlur={() => { if (contract.templateVersion && contract.body !== contract.preparedBody && !contract.editLogged) onCommitContract({ editLogged: true }, "Edited the text"); }}></textarea>}

                {!cApproved && (
                  <div className="row" style={{ "--gap": "10px" }}>
                    <button className="btn btn-secondary" onClick={runContract} disabled={busyC}>Prepare again</button>
                    <button className="btn btn-accent" onClick={() => {
                      const left = unfilled(contract.body);
                      if (left.length) { setErrC(Object.assign(new Error("Fill these blanks before approving: " + left.join(", ")), { blanks: true })); return; }
                      setErrC(null); onCommitContract({ status: "Approved — ready to send" }, "Approved — locked for sending");
                    }}>Approve agreement</button>
                    <span className="field-hint" style={{ flex: "1 1 220px" }}>Approving locks the text. Nothing can be sent before this.</span>
                  </div>
                )}
                {errC && <ContractError err={errC} onOpenSettings={onOpenSettings} />}

                {cApproved && (
                  <div className="stack" style={{ "--gap": "14px", borderTop: "1px solid var(--rule)", paddingTop: 16 }}>
                    <div className="row" style={{ "--gap": "10px" }}>
                      {!cSentOrLater && <button className="btn btn-secondary" onClick={() => onCommitContract({ status: "Draft" }, "Reopened for edits")}>Reopen for edits</button>}
                      {!["Signed by one partner", "Signed by couple", "Executed", "Signed"].includes(contract.status) && <button className={"btn " + (contract.email ? "btn-secondary" : "btn-accent")} onClick={onPrepareContractEmail}>{contract.email ? "Rebuild email" : "Prepare email"}</button>}
                      {contract.email && !cSentOrLater && <button className="btn btn-accent" onClick={onMarkContractSent}>Mark as sent</button>}
                      {paperOnly && <button className="btn btn-secondary" onClick={onMarkContractSigned}>Mark as signed on paper</button>}
                    </div>
                    {contract.email && (contract.status === "Approved — ready to send" || contract.status === "Sent") && (
                      <EmailBlock email={contract.email} note={copyNoteC}
                        onCopy={() => copyText("To: " + contract.email.to + "\nSubject: " + contract.email.subject + "\n\n" + contract.email.body, setCopyNoteC)}
                        hint={"Copy this into your email" + portalHint(client, "sign") + "."} />
                    )}
                    {cSentOrLater && (
                      <div style={{ fontSize: 16 }}>
                        ✓ In Documents{contract.sentAt ? " · sent " + new Date(contract.sentAt).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : ""}{contract.status === "Signed" ? " · marked signed" : contract.status === "Executed" ? " · executed" + (contract.effectiveDate ? ", effective " + longDay(contract.effectiveDate) : "") : ""}.
                      </div>
                    )}
                  </div>
                )}
                <EventLog events={contract.events} />
              </div>
            )}
          </div>
        );
      })()}

      {tab === "payments" && <PaymentsPanel client={client} contract={contract} fees={fees || []} settings={settings} onChange={onCommitFees} />}

      {tab === "quote" && (<>
        {!quote && (
          <div className="panel measure" style={{ "--measure": "640px", alignItems: "center", textAlign: "center", padding: "36px 28px" }}>
            <div className="label">Quote</div>
            <h3 className="panel-title" style={{ fontSize: 30 }}>Generate a quote for {client.names}</h3>
            <p className="text-muted" style={{ fontSize: 16, lineHeight: 1.6, maxWidth: 480 }}>
              Starts from your prices: the package {client.names} asked for, any of your add-ons their answers call for, and custom extras for anything beyond both, priced against the Central Coast rates in Settings. You can edit everything before it goes to the couple.
            </p>
            {!hasKey && <NeedsKey>You&rsquo;ll need an Anthropic API key first.</NeedsKey>}
            <button className="btn btn-accent" onClick={run} disabled={busy || !hasKey}>{busy ? "Generating…" : "Generate quote"}</button>
            {err && <QuoteError err={err} onOpenSettings={onOpenSettings} />}
          </div>
        )}

        {quote && (
          <div className="stack measure" style={{ "--gap": "18px", "--measure": "820px" }}>
            <div className="panel" style={{ gap: 18 }}>
              <div className="panel-head">
                <div className="label" style={{ marginRight: "auto" }}>Quote · {client.names}</div>
                <StatusTag status={quote.status} />
              </div>
              <ChangeRequestNote item={quote} />

              <div className="grid-3" style={{ "--gap": "16px" }}>
                <Field label="Package tier"><input className="input" value={quote.packageTier} disabled={approved} onChange={e => onCommit({ packageTier: e.target.value })} /></Field>
                <Field label="Range low ($)"><input type="number" className="input" value={quote.rangeLow} disabled={approved} onChange={e => onCommit({ rangeLow: Number(e.target.value) || 0 })} /></Field>
                <Field label="Range high ($)"><input type="number" className="input" value={quote.rangeHigh} disabled={approved} onChange={e => onCommit({ rangeHigh: Number(e.target.value) || 0 })} /></Field>
              </div>

              <div className="stack" style={{ "--gap": "8px" }}>
                <div className="label">Line items</div>
                {/* Must stay a table: it scrolls inside its own box on phones. */}
                <div className="scroll-x" tabIndex={0} role="region" aria-label="Quote line items">
                  <table className="table">
                    <thead><tr><th style={{ width: "44%" }}>Item</th><th style={{ width: "20%" }}>Amount</th><th>Note</th>{!approved && <th style={{ width: 52 }}><span className="sr-only">Remove</span></th>}</tr></thead>
                    <tbody>
                      {quote.lineItems.map((li, i) => (
                        <tr key={i}>
                          <td>
                            <input className="input" aria-label={"Line " + (i + 1) + " item"} value={li.label} disabled={approved} onChange={e => setLine(i, "label", e.target.value)} />
                            {li.kind && <div className="field-hint" style={{ marginTop: 4 }}>{li.kind === "package" ? "Your package price" : li.kind === "addon" ? "Your add-on rate" : "Custom extra"}</div>}
                          </td>
                          <td><input type="number" className="input" aria-label={"Line " + (i + 1) + " amount"} value={li.amount} disabled={approved} onChange={e => setLine(i, "amount", e.target.value)} /></td>
                          <td><input className="input" aria-label={"Line " + (i + 1) + " note"} placeholder="note" value={li.note} disabled={approved} onChange={e => setLine(i, "note", e.target.value)} /></td>
                          {!approved && <td><button className="btn btn-icon btn-ghost" onClick={() => removeLine(i)} aria-label={"Remove line " + (i + 1)}><Icon path={<><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></>} size={14} /></button></td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="row" style={{ "--gap": "8px 12px" }}>
                  {!approved && <button className="btn-link" onClick={addLine}>+ Add line item</button>}
                  <span className="push" style={{ fontSize: 15 }}><span className="label">Line items total</span> <span className="figure-strong" style={{ marginLeft: 6 }}>{fmt(lineSum)}</span></span>
                </div>
              </div>

              {customChecks.length > 0 && (
                <div className="stack" style={{ "--gap": "6px" }}>
                  <div className="label">Central Coast check (internal)</div>
                  {customChecks.map((li, i) => (
                    <div key={i} style={{ fontSize: 15.5, lineHeight: 1.5 }}><span style={{ fontWeight: 500 }}>{li.label}, {fmt(li.amount)}.</span> {li.market}</div>
                  ))}
                  <div className="field-hint">Custom extras are your call. Couples never see these notes.</div>
                </div>
              )}

              <Field label="Rationale (internal)"><textarea className="input" style={{ minHeight: 80 }} value={quote.rationale} disabled={approved} onChange={e => onCommit({ rationale: e.target.value })}></textarea></Field>

              {!approved && (
                <div className="row" style={{ "--gap": "12px", alignItems: "flex-end" }}>
                  <div style={{ flex: "1 1 260px", maxWidth: 340 }}>
                    <Field label="Final number" hint="Optional. Leave blank to keep the range."><input type="number" className="input" value={finalInput} onChange={e => setFinalInput(e.target.value)} /></Field>
                  </div>
                  <button className="btn btn-secondary" onClick={run} disabled={busy}>{busy ? "Regenerating…" : "Regenerate"}</button>
                  <button className="btn btn-accent" onClick={approve}>Approve quote</button>
                </div>
              )}
              {err && <QuoteError err={err} onOpenSettings={onOpenSettings} />}
            </div>

            {approved && (
              <div className="panel" style={{ gap: 14 }}>
                <div className="label">Send to couple</div>
                <div style={{ fontSize: 17 }}>Final: <span className="figure-strong" style={{ fontSize: 17 }}>{quote.finalNumber ? fmt(quote.finalNumber) : (fmt(quote.rangeLow) + " – " + fmt(quote.rangeHigh))}</span></div>
                <label style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 16, cursor: "pointer" }}>
                  <input type="checkbox" checked={quote.showBreakdownToClient} onChange={e => onCommit({ showBreakdownToClient: e.target.checked })} style={{ marginTop: 3 }} />
                  Include the full line-item breakdown in the couple&rsquo;s email (off = package + total only)
                </label>
                <div className="row" style={{ "--gap": "10px" }}>
                  {!sentOrLater && <button className="btn btn-secondary" onClick={backToDraft}>Reopen for edits</button>}
                  <button className={"btn " + (quote.email ? "btn-secondary" : accent("btn-accent"))} onClick={onPrepareEmail}>{quote.email ? "Rebuild email" : "Prepare email"}</button>
                  {quote.email && !sentOrLater && <button className={"btn " + accent("btn-accent")} onClick={markSent}>Mark as sent</button>}
                </div>
                {quote.email && (
                  <EmailBlock email={quote.email} note={copyNote}
                    onCopy={() => copyText("To: " + quote.email.to + "\nSubject: " + quote.email.subject + "\n\n" + quote.email.body, setCopyNote)}
                    hint={"Copy this into your email" + portalHint(client, "approve") + "."} />
                )}
                {sentOrLater && (
                  <div className="stack" style={{ "--gap": "10px" }}>
                    <div className="text-muted" style={{ fontSize: 15.5 }}>Sent {quote.sentAt ? new Date(quote.sentAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}. The couple can approve or ask for changes in their portal, or log their response here:</div>
                    <div className="row" role="group" aria-label="Client response" style={{ "--gap": "6px" }}>
                      {["Client approved", "Negotiating", "Client declined"].map(r => (
                        <button key={r} className={"chip chip-sm" + (quote.status === r ? " active" : "")} aria-pressed={quote.status === r} onClick={() => clientResp(r)}>{r}</button>
                      ))}
                    </div>
                    {quote.status === "Client approved" && <div style={{ fontSize: 16 }}>✓ The agreement is unlocked. Head to the Contract tab to prepare it.</div>}
                  </div>
                )}
              </div>
            )}

            <div className="panel" style={{ padding: "12px 22px" }}><EventLog events={quote.events} /></div>
          </div>
        )}
      </>)}
    </div>
  );
}

// Phase 11 · Step 6: the couple's payment schedule, with reminders and extra-hours invoices.
function PaymentsPanel({ client, contract, fees, settings, onChange }) {
  const today = todayYmd();
  const sum = feeSummary(fees, today, settings);
  const terms = normalizeSettings(settings).terms;
  const [hours, setHours] = useState("");
  const [mailFor, setMailFor] = useState(null);
  const [copied, setCopied] = useState("");
  const ot = overtimeFee(hours, today, settings);
  const event = ymd(client.date);
  const afterEvent = !!event && event <= today;
  const fmtMoney = n => "$" + Number(n).toLocaleString("en-US", { minimumFractionDigits: Number.isInteger(Number(n)) ? 0 : 2, maximumFractionDigits: 2 });
  const dueText = d => d ? longDay(d) : "Not set";
  const setFee = (id, patch) => onChange(fees.map(f => f.id === id ? { ...f, ...patch } : f));
  const mailFee = mailFor && fees.find(f => f.id === mailFor);
  const mail = mailFee ? reminderEmail({ client, fee: mailFee, settings, today }) : null;
  const copy = async () => {
    const t = "To: " + mail.to + "\nSubject: " + mail.subject + "\n\n" + mail.body;
    try { await navigator.clipboard.writeText(t); setCopied("Copied to clipboard."); } catch (e) { setCopied("Select the text and copy it (⌘/Ctrl-C)."); }
  };
  if (!fees.length) {
    return (
      <div className="panel measure" style={{ "--measure": "640px" }}>
        <div className="label">Payments · {client.names}</div>
        <p style={{ fontSize: 17, lineHeight: 1.6, margin: 0 }}>
          The payment schedule starts here once the agreement is in effect: when you countersign it, or mark it signed on paper.
          {contract && contract.status ? " The agreement is currently: " + contract.status + "." : " There's no agreement yet."}
        </p>
      </div>
    );
  }
  const alert = sum.overdue.length > 0; // oxblood once per view: the overdue tag is this screen's alert
  return (
    <div className="stack measure" style={{ "--gap": "18px", "--measure": "820px" }}>
      <div className="panel" style={{ gap: 16 }}>
        <div className="panel-head">
          <div className="label" style={{ marginRight: "auto" }}>Payments · {client.names}</div>
          <span className="mono" style={{ fontSize: 13 }}>Paid {fmtMoney(sum.paid)} of {fmtMoney(sum.total)}</span>
        </div>
        {sum.pause && <Note tone="warn">A payment is more than {terms.latePauseDays} days overdue. Your contract lets you pause services until it&rsquo;s paid (Section 4). That&rsquo;s your call.</Note>}
        {/* Must stay a table: it scrolls inside its own box on phones. */}
        <div className="scroll-x" tabIndex={0} role="region" aria-label="Payment schedule">
          <table className="table">
            <thead><tr><th style={{ width: "34%" }}>Payment</th><th>Amount</th><th>Due</th><th>Status</th><th style={{ position: "relative" }}><span className="sr-only">Actions</span></th></tr></thead>
            <tbody>
              {sum.rows.map(f => (
                <tr key={f.id}>
                  <td>{f.label}</td>
                  <td className="figure">{fmtMoney(f.amount)}</td>
                  <td>{dueText(f.due)}</td>
                  <td><StatusTag status={f.status} />{f.status === "Overdue" && <div className="field-hint" style={{ marginTop: 4 }}>{f.daysOverdue} {f.daysOverdue === 1 ? "day" : "days"} late</div>}{f.paidAt && <div className="field-hint" style={{ marginTop: 4 }}>{longDay(f.paidAt)}</div>}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {f.paidAt
                      ? <button className="btn-link" onClick={() => setFee(f.id, { paidAt: null })} aria-label={"Undo: " + f.label + " paid"}>Undo</button>
                      : <div className="row" style={{ "--gap": "6px 12px", flexWrap: "wrap" }}>
                          <button className="btn btn-secondary" onClick={() => setFee(f.id, { paidAt: today })} aria-label={"Mark paid: " + f.label}>Mark paid</button>
                          <button className="btn-link" onClick={() => { setMailFor(f.id); setCopied(""); }} aria-label={"Reminder: " + f.label}>Reminder</button>
                          {f.kind === "overtime" && <button className="btn-link" onClick={() => onChange(fees.filter(x => x.id !== f.id))} aria-label={"Remove: " + f.label}>Remove</button>}
                        </div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="row" style={{ "--gap": "8px 22px" }}>
          <span><span className="label">Outstanding</span> <span className="figure-strong" style={{ marginLeft: 6 }}>{fmtMoney(sum.outstanding)}</span></span>
          {sum.next && !sum.next.paidAt && <span className="text-muted" style={{ fontSize: 15 }}>Next: {sum.next.label}, {dueText(sum.next.due)}</span>}
        </div>
      </div>

      {mail && (
        <div className="panel stack" style={{ "--gap": "10px" }} aria-label="Payment reminder">
          <div className="row" style={{ "--gap": "8px 12px" }}>
            <div className="label" style={{ flex: "1 1 260px", minWidth: 0, overflowWrap: "anywhere" }}>To: {mail.to || "—"} · Subject: {mail.subject}</div>
            <button className="btn-link" onClick={copy}>Copy email</button>
            <a className="btn btn-secondary" href={"mailto:" + encodeURIComponent(mail.to) + "?subject=" + encodeURIComponent(mail.subject) + "&body=" + encodeURIComponent(mail.body)}>Open in my email app</a>
          </div>
          <textarea className="input" readOnly aria-label="Reminder text" style={{ minHeight: 190, fontSize: 15.5 }} value={mail.body}></textarea>
          {copied && <div className="text-muted" role="status" style={{ fontSize: 15 }}>{copied}</div>}
          <div className="field-hint">Payment details come from Settings → How couples pay you.</div>
        </div>
      )}

      <div className="panel stack" style={{ "--gap": "12px" }} aria-label="Extra on-site hours">
        <div className="label">Extra on-site hours</div>
        {afterEvent ? (<>
          <p className="text-muted" style={{ margin: 0, fontSize: 15.5 }}>Billed at {fmtMoney(terms.overtimeRate)} an hour, rounded up to the {terms.overtimeIncrementHrs === 0.5 ? "half hour" : terms.overtimeIncrementHrs + " hour"}, due {terms.invoiceDueDays} days after you invoice.</p>
          <div className="row" style={{ "--gap": "12px", alignItems: "flex-end" }}>
            <div style={{ flex: "0 1 200px" }}><Field label="Hours past the package"><input className="input" inputMode="decimal" value={hours} onChange={e => setHours(e.target.value)} /></Field></div>
            {ot && <span style={{ fontSize: 16, paddingBottom: 10 }}>{ot.hours} hours × {fmtMoney(terms.overtimeRate)} = <span className="figure-strong">{fmtMoney(ot.amount)}</span>, due {longDay(ot.due)}</span>}
            <button className={"btn " + (alert ? "btn-primary" : "btn-accent")} disabled={!ot} onClick={() => { onChange([...fees, ot]); setHours(""); }}>Add to payments</button>
          </div>
        </>) : <p className="text-muted" style={{ margin: 0, fontSize: 15.5 }}>After the wedding ({event ? longDay(event) : "date not set"}), log any hours past the package here.</p>}
      </div>
    </div>
  );
}

const CONTRACT_SENT_OR_LATER = ["Sent", "Signed by one partner", "Signed by couple", "Executed", "Signed"];
const longDay = ymd => { const [y, m, d] = String(ymd).split("-").map(Number); return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-US", { timeZone: "UTC", month: "long", day: "numeric", year: "numeric" }); };
const whenShort = iso => new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

// Phase 11 · Step 4: who has signed, from the portal, and the countersignature.
function SigningStatus({ contract, client }) {
  const sigs = contract.signatures || [];
  if (!["Sent", "Signed by one partner", "Signed by couple", "Executed"].includes(contract.status) || (!sigs.length && contract.status === "Sent" && !contract.templateVersion)) return null;
  if (contract.status === "Sent" && !sigs.length) return <Note>Sent. Each partner signs in their portal, then you countersign here.</Note>;
  const names = [0, 1].map(i => (client.partners && client.partners[i] && client.partners[i].name) || "Partner " + (i + 1));
  return (
    <div className="stack" style={{ "--gap": "6px" }} aria-label="Signatures">
      <div className="label">Signatures</div>
      {[0, 1].map(i => {
        const sg = sigs.find(x => x.partner === i);
        return <div key={i} style={{ fontSize: 16 }}>{sg ? "✓ " + sg.name + " signed · " + whenShort(sg.at) : "Waiting on " + names[i]}</div>;
      })}
      {contract.countersignature
        ? <div style={{ fontSize: 16 }}>✓ Countersigned by {contract.countersignature.name}, {contract.countersignature.title} · {whenShort(contract.countersignature.at)}</div>
        : sigs.length === 2 ? <div style={{ fontSize: 16 }}>Waiting on your countersignature</div> : null}
      {contract.effectiveDate && <div className="field-hint">Effective {longDay(contract.effectiveDate)}. The signed copy is filed in Documents.</div>}
    </div>
  );
}

function Countersign({ client, settings, onDone }) {
  const store = useStore();
  const cloud = store.mode === "cloud" ? store : null;
  const [name, setName] = useState((settings && settings.plannerName) || (cloud && cloud.displayName && !/@/.test(cloud.displayName) ? cloud.displayName : ""));
  const [title, setTitle] = useState("Owner");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  if (!cloud) return <Note>Both partners have signed. Countersigning happens in the hosted app.</Note>;
  if (!cloud.isOwner) return <Note>Both partners have signed. The studio owner countersigns.</Note>;
  const go = async () => {
    setBusy(true); setErr("");
    try { const r = await callFunction("portal", { action: "countersign", studioId: cloud.studioId, clientId: client.id, name: name.trim(), title: title.trim() || "Owner" }); onDone && onDone(r); }
    catch (e) { setErr(e.message || "Couldn't countersign. Try again."); } finally { setBusy(false); }
  };
  return (
    <div className="panel stack" style={{ "--gap": "14px", background: "var(--paper)" }} aria-label="Countersign">
      <div className="label">Both partners have signed</div>
      <h4 className="panel-title" style={{ fontSize: 24, margin: 0 }}>Countersign as {(settings && settings.role) || "Coordinator"}</h4>
      <p className="text-muted" style={{ margin: 0, fontSize: 15.5, lineHeight: 1.55 }}>Your signature is the last one, so today becomes the Effective Date. The signed PDF is filed in Documents and {client.names} get an email.</p>
      <div className="grid-2">
        <Field label="Your full name"><input className="input signature-input" value={name} onChange={e => setName(e.target.value)} autoComplete="name" /></Field>
        <Field label="Title"><input className="input" value={title} onChange={e => setTitle(e.target.value)} /></Field>
      </div>
      <label style={{ display: "flex", gap: 12, alignItems: "flex-start", fontSize: 16, lineHeight: 1.5, cursor: "pointer" }}>
        <input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)} style={{ marginTop: 3 }} />
        <span>I've read the agreement and agree that typing my name is my electronic signature for {(settings && settings.legalName) || "the studio"}.</span>
      </label>
      <div><button className="btn btn-accent" disabled={busy || !agree || name.trim().length < 3} onClick={go}>{busy ? "Countersigning…" : "Countersign agreement"}</button></div>
      {err && <Note tone="warn"><span role="alert">{err}</span></Note>}
    </div>
  );
}

function portalHint(client, verb) {
  const st = getActiveStore();
  if (st.mode !== "cloud") return "";
  return st.portalLinks && client && st.portalLinks[client.id]
    ? " — it includes the couple's portal link, where they can " + verb + " online"
    : " (tip: create the couple's portal link first — Couple portal button, top right — then rebuild the email so they can " + verb + " online)";
}

function ContractError({ err, onOpenSettings }) {
  if (!err.missing) return <div role="alert" className="note note-warn">{err.message}</div>;
  return (
    <div role="alert" className="note note-warn" style={{ textAlign: "left", width: "100%" }}>
      <div style={{ fontWeight: 500 }}>The agreement still needs:</div>
      <ul style={{ margin: "6px 0 6px 20px", padding: 0 }}>{err.missing.map(m => <li key={m}>{m}</li>)}</ul>
      <div className="field-hint">Add these on the Clients screen with Edit agreement details, or in <button className="btn-link" onClick={onOpenSettings}>Settings</button> for the package fee.</div>
    </div>
  );
}

function QuoteError({ err, onOpenSettings }) {
  return (
    <Note tone="warn">
      <span role="alert">{err.message}</span>
      {err.code === "NO_KEY" && <button className="btn-link" onClick={onOpenSettings} style={{ marginLeft: 10 }}>Open Settings</button>}
    </Note>
  );
}

/* ───────────── Settings · contract details (Phase 11 · Step 1) ─────────────
   Packages, add-ons, payment terms and the legal name, taken from Amanda's contract.
   Owner-only (readOnly for planners). The summaries are generated from the numbers. */

const TERM_FIELDS = [
  ["retainerPct", "Retainer (% of the fee)", "%"],
  ["retainerDueDays", "Retainer due, days after the Effective Date", "days"],
  ["balanceDaysBefore", "Balance due, days before the event", "days"],
  ["latePauseDays", "Services may pause after a payment is overdue by", "days"],
  ["cancelFullFeeWithinDays", "Full fee due if cancelled within", "days"],
  ["postponeWithinMonths", "One free postponement to a date within", "months"],
  ["overtimeRate", "Extra on-site hours, rate", "$ / hour"],
  ["overtimeIncrementHrs", "Extra hours billed in increments of", "hours"],
  ["maxOnSiteHours", "Never on site beyond this without approval", "hours"],
  ["invoiceDueDays", "Invoices (add-ons, extra hours) due within", "days"],
  ["vendorMealDaysBefore", "Vendor meal count to the couple, days before the event", "days"]
];

function ContractSettings({ form, setForm, readOnly }) {
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const setPkg = (id, k, v) => setForm(prev => ({ ...prev, packages: prev.packages.map(p => p.id === id ? { ...p, [k]: v } : p) }));
  const setAddon = (i, k, v) => setForm(prev => ({ ...prev, addons: prev.addons.map((a, j) => j === i ? { ...a, [k]: v } : a) }));
  const addAddon = () => setForm(prev => ({ ...prev, addons: [...prev.addons, { id: slug("add-on-" + Date.now().toString(36)), name: "", rate: "", unit: "flat" }] }));
  const removeAddon = i => setForm(prev => ({ ...prev, addons: prev.addons.filter((_, j) => j !== i) }));
  const setTerm = (k, v) => setForm(prev => ({ ...prev, terms: { ...prev.terms, [k]: v } }));
  const preview = normalizeSettings(form);

  return (
    <>
      <section className="stack" style={{ "--gap": "14px" }}>
        <div className="label">The business, as written in your contract</div>
        <div className="field-hint">These appear word for word in every agreement.</div>
        <div className="grid-2">
          <Field label="Legal name"><input className="input" value={form.legalName} disabled={readOnly} onChange={e => set("legalName", e.target.value)} /></Field>
          <Field label="Doing business as"><input className="input" value={form.dba} disabled={readOnly} onChange={e => set("dba", e.target.value)} /></Field>
          <Field label="Type of company"><input className="input" value={form.entityType} disabled={readOnly} onChange={e => set("entityType", e.target.value)} /></Field>
          <Field label="Your title in the agreement"><input className="input" value={form.role} disabled={readOnly} onChange={e => set("role", e.target.value)} /></Field>
          <Field label="Travel included within" hint="Anywhere else, travel is quoted in advance."><input className="input" value={form.region} disabled={readOnly} onChange={e => set("region", e.target.value)} /></Field>
        </div>
      </section>

      <hr className="rule" />

      <section className="stack" style={{ "--gap": "14px" }} aria-labelledby="settings-packages">
        <div className="label" id="settings-packages">Packages</div>
        <div className="field-hint">From your pricing sheet. Leave the fee blank to quote a package case by case; quotes then start at its starting price.</div>
        {form.packages.map(p => (
          <details key={p.id} className="panel pkg-editor" style={{ display: "block" }}>
            <summary style={{ cursor: "pointer", minHeight: 44, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <span className="panel-title" style={{ fontSize: 22 }}>{p.name || "Untitled package"}</span>
              <span className="mono text-muted" style={{ fontSize: 13 }}>{p.price === null || p.price === "" ? (p.priceFrom ? "From $" + Number(p.priceFrom).toLocaleString("en-US") + ", custom" : "Quoted case by case") : "$" + Number(p.price).toLocaleString("en-US")} · {p.onSiteHours} hours on site</span>
              <span className="mono pkg-toggle" aria-hidden="true" style={{ marginLeft: "auto", fontSize: 13 }}></span>
            </summary>
            <div className="stack" style={{ "--gap": "14px", marginTop: 14 }}>
              <div className="grid-2">
                <Field label="Package name"><input className="input" value={p.name} disabled={readOnly} onChange={e => setPkg(p.id, "name", e.target.value)} /></Field>
                <Field label="Fee ($)" hint="Blank = quoted case by case"><input className="input" inputMode="decimal" value={p.price ?? ""} disabled={readOnly} onChange={e => setPkg(p.id, "price", e.target.value)} /></Field>
                {(p.price === null || p.price === "") && <Field label="Starting at ($)" hint="Case-by-case quotes never go below this"><input className="input" inputMode="decimal" value={p.priceFrom ?? ""} disabled={readOnly} onChange={e => setPkg(p.id, "priceFrom", e.target.value)} /></Field>}
                <Field label="Hours on site, wedding day"><input className="input" inputMode="decimal" value={p.onSiteHours} disabled={readOnly} onChange={e => setPkg(p.id, "onSiteHours", e.target.value)} /></Field>
                <Field label="My involvement begins"><input className="input" value={p.involvementStarts} disabled={readOnly} onChange={e => setPkg(p.id, "involvementStarts", e.target.value)} /></Field>
              </div>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 16, cursor: readOnly ? "default" : "pointer", minHeight: 44 }}>
                <input type="checkbox" checked={!!p.weeklyUpdates} disabled={readOnly} onChange={e => setPkg(p.id, "weeklyUpdates", e.target.checked)} style={{ marginTop: 3 }} />
                Includes a written status update every week
              </label>
              <Field label={p.includes ? "What it adds (one per line)" : "What's included (one per line)"} hint={p.includes ? "Everything in Partial Planning & Coordination, plus these." : undefined}>
                <textarea className="input" style={{ minHeight: 150 }} value={(p.inclusions || []).join("\n")} disabled={readOnly} onChange={e => setPkg(p.id, "inclusions", e.target.value.split("\n"))}></textarea>
              </Field>
            </div>
          </details>
        ))}
      </section>

      <hr className="rule" />

      <section className="stack" style={{ "--gap": "14px" }} aria-labelledby="settings-addons">
        <div className="label" id="settings-addons">Add-ons</div>
        <div className="field-hint">These rates are locked into each agreement once it's signed.</div>
        {form.addons.map((a, i) => (
          <div key={a.id} className="list-row" style={{ alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
            <div style={{ flex: "2 1 220px", minWidth: 0 }}><Field label="Add-on"><input className="input" value={a.name} disabled={readOnly} onChange={e => setAddon(i, "name", e.target.value)} /></Field></div>
            <div style={{ flex: "1 1 100px", minWidth: 0 }}><Field label="Rate ($)"><input className="input" inputMode="decimal" value={a.rate} disabled={readOnly} onChange={e => setAddon(i, "rate", e.target.value)} /></Field></div>
            <div style={{ flex: "1 1 120px", minWidth: 0 }}><Field label="Charged">
              <select className="input" value={a.unit} disabled={readOnly} onChange={e => setAddon(i, "unit", e.target.value)}>
                {ADDON_UNITS.map(u => <option key={u} value={u}>{u === "flat" ? "Flat" : u === "hour" ? "Per hour" : "Per person"}</option>)}
              </select>
            </Field></div>
            {!readOnly && <button className="btn btn-ghost" aria-label={"Remove " + (a.name || "add-on")} onClick={() => removeAddon(i)}>Remove</button>}
          </div>
        ))}
        {!readOnly && <div><button className="btn btn-secondary" onClick={addAddon}>Add an add-on</button></div>}
      </section>

      <hr className="rule" />

      <section className="stack" style={{ "--gap": "14px" }} aria-labelledby="settings-terms">
        <div className="label" id="settings-terms">Payment and cancellation terms</div>
        <div className="grid-2">
          {TERM_FIELDS.map(([k, label, unit]) => (
            <Field key={k} label={label} hint={unit}>
              <input className="input" inputMode="decimal" value={form.terms[k]} disabled={readOnly} onChange={e => setTerm(k, e.target.value)} />
            </Field>
          ))}
        </div>
        <div className="panel" style={{ gap: 10 }} aria-live="polite">
          <div className="label">How it reads in quotes and emails</div>
          <p style={{ margin: 0 }}>{preview.paymentTerms}</p>
          <p style={{ margin: 0 }}>{preview.cancellationPolicy}</p>
        </div>
        <Note>These numbers have to match your contract. If you change one here, update the contract template as well.</Note>
        <Field label="How couples pay you" hint="Goes into payment reminder emails. For example: Zelle to your number, or checks payable to AD Freelance LLC.">
          <textarea className="input" style={{ minHeight: 64 }} value={form.paymentInstructions ?? ""} placeholder={DEFAULT_PAYMENT_INSTRUCTIONS} disabled={readOnly} onChange={e => set("paymentInstructions", e.target.value)}></textarea>
        </Field>
      </section>
    </>
  );
}

/* ───────────────────────── Settings panel ───────────────────────── */

function SettingsPanel({ settings, onSave, onClose }) {
  const store = useStore();
  const cloud = store.mode === "cloud" ? store : null;
  const readOnly = !!(cloud && !cloud.isOwner);
  const [form, setForm] = useState(settings);
  const [showKey, setShowKey] = useState(false);
  const [testState, setTestState] = useState("idle"); // idle | testing | ok | error
  const [testMsg, setTestMsg] = useState("");
  const [showHelp, setShowHelp] = useState(!settings.apiKey);
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const test = async () => {
    setTestState("testing"); setTestMsg("");
    try {
      const { text } = await callClaude(form, { prompt: "Reply with the single word: OK", maxTokens: 8 });
      setTestState("ok");
      setTestMsg("Connected — the API key works." + (text ? " (model replied: " + text.trim().slice(0, 20) + ")" : ""));
    } catch (e) {
      setTestState("error");
      setTestMsg(e.message || "Connection failed.");
    }
  };
  const save = () => { onSave(normalizeSettings(form)); onClose(); };
  const mr = form.marketRates || DEFAULT_MARKET_RATES;
  const numIn = v => v === "" ? null : Math.max(0, Math.round(Number(v) || 0));
  const setMr = patch => setForm(prev => ({ ...prev, marketRates: { ...(prev.marketRates || DEFAULT_MARKET_RATES), ...patch } }));
  const setRow = (i, key, v) => setMr({ rows: mr.rows.map((r, j) => j === i ? { ...r, [key]: (key === "low" || key === "high") ? numIn(v) : v } : r) });

  return (
    <Dialog title="Settings" width={680} onClose={onClose}
      footer={readOnly
        ? <span className="field-hint" style={{ marginRight: "auto" }}>Only the studio owner can change these settings.</span>
        : <>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save}>Save settings</button>
        </>}>
      {cloud && <CloudSettingsSection model={form.model} onModel={v => set("model", v)} readOnly={readOnly} />}
      {!cloud && <section className="stack" style={{ "--gap": "14px" }}>
        <div className="label">AI (Anthropic API)</div>
        <Field label="Anthropic API key" hint="Stored only in this browser. Never uploaded, and never included in your backup file.">
          <div className="row" style={{ "--gap": "8px", flexWrap: "nowrap" }}>
            <input className="input" aria-label="Anthropic API key" type={showKey ? "text" : "password"} placeholder="sk-ant-..." value={form.apiKey} onChange={e => set("apiKey", e.target.value)} style={{ flex: 1, minWidth: 0 }} />
            <button className="btn btn-secondary" onClick={() => setShowKey(s => !s)}>{showKey ? "Hide" : "Show"}</button>
          </div>
        </Field>
        <Field label="Model">
          <select className="input" value={form.model} onChange={e => set("model", e.target.value)}>
            {CLAUDE_MODELS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
        </Field>
        <div className="row" style={{ "--gap": "10px 18px" }}>
          <button className="btn btn-secondary" onClick={test} disabled={testState === "testing"}>
            {testState === "testing" ? "Testing…" : "Test connection"}
          </button>
          <button className="btn-link" onClick={() => setShowHelp(s => !s)}>{showHelp ? "Hide setup steps" : "How do I get a key?"}</button>
        </div>
        {testMsg && <div role="status" className={testState === "ok" ? "note note-ok" : "note note-warn"}>{testMsg}</div>}

        {showHelp && (
          <Note>
            <div style={{ fontWeight: 500 }}>Getting an Anthropic API key (about 5 minutes)</div>
            <ol>
              <li>Go to <strong style={{ fontWeight: 500 }}>console.anthropic.com</strong> and sign up or log in.</li>
              <li>Add a payment method under <strong style={{ fontWeight: 500 }}>Billing</strong> (usage is pay-as-you-go; each quote or contract costs a few cents).</li>
              <li>Set a monthly <strong style={{ fontWeight: 500 }}>spend limit</strong> in Billing so there are no surprises.</li>
              <li>Open <strong style={{ fontWeight: 500 }}>API keys</strong> → <strong style={{ fontWeight: 500 }}>Create key</strong>. Name it "GhostWriter Events dashboard".</li>
              <li>Copy the key (starts with <code>sk-ant-</code>) and paste it above, then click Test connection.</li>
            </ol>
            <div className="text-muted" style={{ marginTop: 6 }}>Because the key sits in this browser, use a dedicated key with a spend limit. Anyone with access to this computer could read it.</div>
          </Note>
        )}
      </section>}

      <hr className="rule" />

      <section className="stack" style={{ "--gap": "14px" }}>
        <div className="label">Studio details (used in quotes &amp; contracts)</div>
        <div className="grid-2">
          <Field label="Business name"><input className="input" value={form.businessName} disabled={readOnly} onChange={e => set("businessName", e.target.value)} /></Field>
          <Field label="Planner name"><input className="input" value={form.plannerName} disabled={readOnly} onChange={e => set("plannerName", e.target.value)} /></Field>
        </div>
        <div className="grid-2">
          <Field label="Studio contact email"><input className="input" type="email" value={form.plannerEmail} disabled={readOnly} onChange={e => set("plannerEmail", e.target.value)} /></Field>
          <Field label="Studio phone" hint="Shown in agreements."><input className="input" type="tel" value={form.contactPhone} disabled={readOnly} onChange={e => set("contactPhone", e.target.value)} /></Field>
        </div>
      </section>

      <hr className="rule" />
      <ContractSettings form={form} setForm={setForm} readOnly={readOnly} />

      <hr className="rule" />

      <section className="stack" style={{ "--gap": "12px" }} aria-label="Central Coast market rates">
        <div className="label">Central Coast market rates (for custom extras)</div>
        <p className="text-muted" style={{ fontSize: 15.5, margin: 0, lineHeight: 1.55 }}>
          When a couple wants something your package and add-ons don&rsquo;t cover, the quote prices it against this table. Couples never see it.
        </p>
        <div className="grid-2" style={{ "--gap": "14px" }}>
          <Field label="Region"><input className="input" value={mr.region || ""} disabled={readOnly} onChange={e => setMr({ region: e.target.value })} /></Field>
          <Field label="Collected"><input className="input" value={mr.asOf || ""} disabled={readOnly} onChange={e => setMr({ asOf: e.target.value })} /></Field>
        </div>
        <div className="stack" style={{ "--gap": "0px" }} role="list" aria-label="Market rate rows">
          {mr.rows.map((r, i) => (
            <div key={i} role="listitem" className="stack" style={{ "--gap": "10px", borderTop: "1px solid var(--rule)", padding: "14px 0" }}>
              <div className="row" style={{ "--gap": "10px", flexWrap: "nowrap", alignItems: "flex-end" }}>
                <div style={{ flex: 1, minWidth: 0 }}><Field label={"Rate " + (i + 1)}><input className="input" aria-label={"Rate " + (i + 1) + " item"} value={r.item || ""} disabled={readOnly} onChange={e => setRow(i, "item", e.target.value)} /></Field></div>
                {!readOnly && <button className="btn btn-ghost" onClick={() => setMr({ rows: mr.rows.filter((_, j) => j !== i) })} aria-label={"Remove rate " + (i + 1)}>Remove</button>}
              </div>
              <div className="grid-3" style={{ "--gap": "10px" }}>
                <Field label={<>Low ($)<span className="sr-only">, rate {i + 1}</span></>}><input type="number" className="input" aria-label={"Rate " + (i + 1) + " low"} value={r.low ?? ""} disabled={readOnly} onChange={e => setRow(i, "low", e.target.value)} /></Field>
                <Field label={<>High ($)<span className="sr-only">, rate {i + 1}</span></>}><input type="number" className="input" aria-label={"Rate " + (i + 1) + " high"} value={r.high ?? ""} disabled={readOnly} onChange={e => setRow(i, "high", e.target.value)} /></Field>
                <Field label={<>Per<span className="sr-only">, rate {i + 1}</span></>}><input className="input" aria-label={"Rate " + (i + 1) + " unit"} value={r.unit || ""} disabled={readOnly} onChange={e => setRow(i, "unit", e.target.value)} /></Field>
              </div>
              <Field label={<>Note<span className="sr-only">, rate {i + 1}</span></>}><input className="input" aria-label={"Rate " + (i + 1) + " note"} value={r.note || ""} disabled={readOnly} onChange={e => setRow(i, "note", e.target.value)} /></Field>
            </div>
          ))}
        </div>
        {!readOnly && <div><button className="btn-link" onClick={() => setMr({ rows: [...mr.rows, { item: "", low: null, high: null, unit: "flat", note: "" }] })}>+ Add a rate</button></div>}
        {(mr.sources || []).length > 0 && (
          <details className="disclosure">
            <summary>Where these numbers come from ({mr.sources.length})</summary>
            <div className="stack" style={{ "--gap": "4px", fontSize: 15 }}>
              {mr.sources.map((src, i) => <div key={i}><SafeLink href={src.url}>{src.name}</SafeLink>{src.town ? ", " + src.town : ""}{src.lists ? ": " + src.lists : ""}</div>)}
            </div>
          </details>
        )}
      </section>

      {cloud && cloud.isOwner && <><hr className="rule" /><EmailSetupSection /></>}
      {cloud && <><hr className="rule" /><TeamSection /></>}
    </Dialog>
  );
}

export { App, IntakeQuestionnaire, CouplePortal, Field, DEFAULT_SETTINGS, SETTINGS_KEY, fmt };
